/**
 * Loan Portfolio Step — V2 (Portfolio Management dashboard)
 *
 * Drop-in replacement for the original LoanPortfolioStep. Renders a 4-tab
 * "Portfolio Management" experience matching the prototype at
 *   frontend/public/prototype/loan-portfolio-prototype.html
 *
 * Tabs:
 *   1. Existing Loans & Eligibility for This New Loan
 *   2. Other Loan Eligibility After New Loan
 *   3. Future Repayment & Planner
 *   4. Wealth & Cash Flow Management
 *
 * Props are identical to the legacy component so the wizard import does not
 * change. The previous implementation is preserved at LoanPortfolioStep.legacy.js
 * for reference.
 *
 * Build is incremental — each step verifies in the wizard before adding more.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DATA-BINDING RULE (HARD REQUIREMENT FOR ALL 4 TABS)
 * ═══════════════════════════════════════════════════════════════════════════
 * Every number, table row, gauge, and metric in EVERY tab MUST derive from
 * one of these three props (via the existing helper functions):
 *
 *   1. existingLoans       → the customer's current loan inventory
 *   2. selectedOffer       → the new loan being underwritten
 *   3. eligibilityResult   → the customer's calculated borrowing capacity
 *
 * Use these helpers from utils/portfolioCalculations to derive everything:
 *   - calculateAnalysis(existingLoans, selectedOffer, eligibilityResult, ...)
 *       → drives Hero, Key Numbers, Today's Position, Cash Flow, Risk, Wealth
 *   - calculateAffordabilityScore(analysis)
 *   - generateRecommendations(analysis, score)
 *   - calculateEligibilityUtilization(analysis, eligibilityResult, selectedOffer)
 *   - calculateLoanRepaymentPriority(existingLoans, analysis)
 *   - calculateDebtFreedomTimeline(existingLoans, selectedOffer, analysis)
 *   - calculateWhatIfScenarios(selectedOffer, analysis)
 *   - calculatePrepaymentImpact(existingLoans, selectedOffer)
 *   - calculateCashFlowImpact(analysis)
 *   - calculateRiskAssessment(analysis, score)
 *
 * NO MOCKED NUMBERS. NO HARDCODED VALUES. If the data is not available,
 * show an empty state ("Not enough data — pick an offer first") instead
 * of fabricating numbers. The only exception is helper labels (e.g.
 * "Salaried · Single source") that describe metadata, not financial values.
 *
 * For NEW components that the existing helpers don't compute (e.g. Tab 2's
 * Other Loan Eligibility for Home/Car/Business/Education/Gold loans, Tab 4's
 * Wealth Trajectory), derive plausible numbers from existing helpers'
 * outputs (salary, FOIR headroom, score, etc.) using simple inline formulas.
 * Mark these with `// Derived from: <inputs>` so the lineage is auditable.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * UI rules (per .claude/skills/ui-ux/SKILL.md):
 *   - PrimeReact components only
 *   - PrimeFlex utility classes (no new SCSS)
 *   - PrimeIcons only
 *   - Inline style ONLY for dynamic / data-driven values
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressBar } from 'primereact/progressbar';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';

import existingLoanService from '../../services/existingLoanService';
import { fetchBestBTOffer, btDelta } from '../../services/portfolioBtService';

import {
    calculateAnalysis,
    calculateAffordabilityScore,
    generateRecommendations,
    formatCurrency,
    formatPercentage,
    calculateSalaryImpact,
    calculateEligibilityUtilization,
    prepareLoanComparisonMatrix,
    calculateCashFlowImpact,
    calculateRiskAssessment,
    calculateDebtFreedomTimeline,
    calculateWhatIfScenarios,
    calculateLoanRepaymentPriority,
    calculatePrepaymentImpact,
} from '../../utils/portfolioCalculations';

const LoanPortfolioStep = ({
    lead,
    existingLoans: existingLoansProp = [],
    selectedOffer = {},
    eligibilityResult = {},
    eligibilityInputs = {},
    formData = {},
    coApplicants = [],
    onPortfolioUpdate,
    toast: externalToast,
    isLoading = false,
    readOnly = false,
}) => {
    const internalToast = useRef(null);
    const toast = externalToast || internalToast;

    // ── Fresh-from-DB loans ────────────────────────────────────────────────
    // The wizard's in-memory `existingLoans` state can lag behind what's
    // actually saved (e.g. user clicked Calculate inside the Existing Loans
    // step, then navigated back-and-forth — the parent's snapshot was set on
    // the first load and never refreshed). Portfolio depends on persisted
    // calculated fields (emi, remaining_tenure, total_interest, etc.) being
    // present, so we re-fetch from the same endpoint the ExistingLoanDetails
    // component uses. Falls back to the prop if the fetch fails.
    const leadId = lead?.id || lead?.lead_id || null;
    const [freshLoans, setFreshLoans] = useState(null);
    useEffect(() => {
        if (!leadId) return;
        let cancelled = false;
        existingLoanService.getByLead(leadId)
            .then((res) => {
                if (cancelled) return;
                if (res?.success && Array.isArray(res.data?.loans)) {
                    setFreshLoans(res.data.loans);
                }
            })
            .catch(() => { /* silent — prop fallback covers the failure */ });
        return () => { cancelled = true; };
    }, [leadId]);
    // Use the fresh DB rows when available; fall back to the wizard prop on
    // first render (before the fetch resolves) or if the fetch failed.
    const existingLoans = freshLoans ?? existingLoansProp;

    // Active tab index (0 = Existing & New Loan, 1 = Other Eligibility, 2 = Repayment & Planner, 3 = Wealth)
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Below 768px the four tab labels don't fit on one row, so the pill strip
    // is swapped for a full-width dropdown. Tracked in JS rather than CSS
    // because the two render different markup, not just different styles.
    const [isMobileTabs, setIsMobileTabs] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = (e) => setIsMobileTabs(e.matches);
        setIsMobileTabs(mq.matches);
        // addEventListener isn't available on MediaQueryList in older WebKit
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // ── 1.1 All Loans Overview — per-row expand/collapse state. Keyed by
    //    row.key (loan id, or a synthetic key for the new-offer row). Default
    //    is all collapsed so the section fits 6-7 loans on a single screen.
    const [expandedLoanRows, setExpandedLoanRows] = useState({});

    // ── 1.1 BT (Balance Transfer) recommendation state ─────────────────────
    // Cache of BT offers per loan key. Value shape: { loading, best, all,
    // source, error }. Populated LAZILY — the first time a row is expanded
    // we fire fetchBestBTOffer(). Result is cached forever within this
    // Portfolio-tab mount so re-expanding doesn't refetch. Refresh on
    // reload only.
    const [btOffersByLoan, setBtOffersByLoan] = useState({});
    // All-lenders dialog: which loan is being shown + open state
    const [btDialogLoan, setBtDialogLoan] = useState(null);

    // ── Tab 2 state — active loan-type segment (Home / Car / Business / Education / Gold) ─
    const [activeLoanType, setActiveLoanType] = useState('home');

    // ── Tab 3 state — Smart Prepayment Planner inputs ─────────────────────
    // Lump-sum prepayment amount + allocation strategy (avalanche/snowball/proportional).
    // Default is ₹0 — user moves slider to model. Strategy default = avalanche
    // (highest-interest first), the textbook-correct choice for interest savings.
    const [prepaymentAmount, setPrepaymentAmount] = useState(0);
    const [prepaymentStrategy, setPrepaymentStrategy] = useState('avalanche');

    // ── EMI calculator state (used inside Tab 1 → 1.4 Existing Loan EMI Calculator) ─
    // Picker: which existing loan's values are loaded into the calculator
    const [calcSelectedLoanIdx, setCalcSelectedLoanIdx] = useState(0);
    const [calcPrincipal, setCalcPrincipal] = useState(0);
    const [calcRate, setCalcRate] = useState(0);
    const [calcTenure, setCalcTenure] = useState(0);
    // Prepayment scenario state
    //   mode = 'lumpsum' → user adds a one-time payment against principal
    //   mode = 'extra'   → user adds a fixed ₹ on top of every EMI
    const [prepayMode, setPrepayMode] = useState('lumpsum');
    const [prepayAmount, setPrepayAmount] = useState(0);
    // Reset the prepayment amount whenever the loaded loan changes, so a
    // ₹1L slider position from a bigger loan doesn't linger on a smaller one.
    useEffect(() => { setPrepayAmount(0); }, [calcSelectedLoanIdx]);

    // Load values from the selected existing loan on first render / when loan list changes
    useEffect(() => {
        const loan = (existingLoans || [])[calcSelectedLoanIdx];
        if (loan) {
            setCalcPrincipal(parseFloat(loan.outstanding_loan || loan.outstanding_amount || 0));
            setCalcRate(parseFloat(loan.interest_rate || 0));
            setCalcTenure(parseInt(loan.remaining_tenure || loan.tenure_remaining || loan.tenure_months || loan.tenure || 12, 10));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calcSelectedLoanIdx, existingLoans?.length]);

    // Memoised computations — same data layer the legacy component used so nothing breaks
    const analysis = useMemo(
        () => calculateAnalysis(existingLoans, selectedOffer, eligibilityResult, eligibilityInputs, formData),
        [existingLoans, selectedOffer, eligibilityResult, eligibilityInputs, formData]
    );
    const score = useMemo(() => calculateAffordabilityScore(analysis), [analysis]);
    const recommendations = useMemo(() => generateRecommendations(analysis, score), [analysis, score]);

    // Hero-strip derived values (defensive defaults for the early-skeleton phase)
    const customerName = lead?.full_name || lead?.first_name || 'Customer';
    const leadIdFormatted = lead?.lead_id_formatted || (lead?.id ? `Lead #${lead.id}` : 'Lead');
    const newLoanAmount = selectedOffer?.loan_amount || analysis?.newLoanAmount || 0;
    const newLoanTenure = selectedOffer?.tenure_months || analysis?.newLoanTenure || 0;
    const newLoanRate = selectedOffer?.roi || analysis?.newLoanRate || 0;
    const bankName = selectedOffer?.bank_name || selectedOffer?.lender_name || 'Lender';

    // Loading state — show skeletons until the legacy data layer settles
    if (isLoading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Skeleton height="180px" className="mb-3" />
                    <Skeleton height="80px" className="mb-3" />
                    <Skeleton height="60px" className="mb-3" />
                    <Skeleton height="400px" />
                </div>
            </div>
        );
    }

    // Hero stats configuration — keeps the JSX readable below
    const heroStats = [
        {
            label: 'Monthly Income',
            value: formatCurrency(analysis?.netSalary || 0),
            delta: 'Salaried',
        },
        {
            label: 'Total Existing Debt',
            value: formatCurrency(analysis?.currentOutstanding || 0),
            delta: `${existingLoans.length} active loan${existingLoans.length === 1 ? '' : 's'}`,
        },
        {
            label: 'New Loan Requested',
            value: formatCurrency(newLoanAmount),
            delta: `${formatPercentage(newLoanRate)} · ${newLoanTenure} months`,
        },
        {
            label: 'Affordability Score',
            value: `${score?.totalScore ?? '—'} / 100`,
            delta: score?.ratingLabel || '',
            isScore: true,
            scoreColor: score?.ratingColor,
        },
    ];

    // Key-numbers strip configuration
    const keyNumbers = [
        {
            label: 'Post-loan FOIR',
            value: formatPercentage(analysis?.foirAfter || 0),
            help: (analysis?.foirAfter || 0) > 50 ? 'Above 50% safe limit' : 'Within safe zone',
            color: (analysis?.foirAfter || 0) > 50 ? 'var(--orange-500)' : 'var(--green-500)',
        },
        {
            label: 'Disposable Income',
            value: `${formatCurrency(analysis?.disposableAfter || 0)}/mo`,
            help: 'After all EMIs',
            color: 'var(--green-500)',
        },
        {
            label: 'Affordability Score',
            value: `${score?.totalScore ?? '—'} / 100`,
            help: score?.ratingLabel || '',
            color: 'var(--primary-color)',
        },
        {
            label: 'Recommended Action',
            value: recommendations?.overallRecommendation?.verdictLabel || 'Review',
            help: recommendations?.overallRecommendation?.message?.slice(0, 60) || '',
            color: 'var(--purple-500)',
        },
    ];

    // ── Hero ──────────────────────────────────────────────────────────────
    const renderHero = () => (
        <div
            className="border-round-xl p-4 mb-3 text-white"
            style={{
                background:
                    'radial-gradient(1100px 400px at 20% -20%, rgba(99,102,241,.35), transparent 60%),' +
                    'radial-gradient(900px 400px at 100% 0%, rgba(56,189,248,.25), transparent 50%),' +
                    'linear-gradient(135deg, #0b1220 0%, #1e293b 100%)',
            }}
        >
            <div className="flex flex-wrap gap-3 justify-content-between align-items-start">
                <div>
                    <span
                        className="inline-flex align-items-center gap-2 px-2 py-1 border-round-3xl text-xs font-semibold uppercase"
                        style={{ background: 'rgba(255,255,255,.08)', letterSpacing: '.08em' }}
                    >
                        <i className="pi pi-briefcase" style={{ fontSize: '.65rem' }}></i>
                        Portfolio Management
                    </span>
                    <h2 className="m-0 mt-2 text-2xl font-bold">{customerName}</h2>
                    <div className="text-sm" style={{ opacity: 0.8 }}>
                        {leadIdFormatted} · {bankName} · {formatCurrency(newLoanAmount)} · {newLoanTenure} months
                    </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <Button
                        icon="pi pi-download"
                        label="Export PDF"
                        text
                        size="small"
                        style={{ color: '#fff', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.18)' }}
                    />
                    <Button
                        icon="pi pi-share-alt"
                        label="Share"
                        size="small"
                        style={{ color: '#0f172a', background: '#fff', border: '1px solid #fff' }}
                    />
                </div>
            </div>

            <div
                className="grid mt-3 pt-3"
                style={{ borderTop: '1px solid rgba(255,255,255,.1)' }}
            >
                {heroStats.map((stat) => (
                    <div className="col-12 md:col-6 lg:col-3" key={stat.label}>
                        <div className="text-xs uppercase font-semibold mb-1" style={{ opacity: 0.7, letterSpacing: '.07em' }}>
                            {stat.label}
                        </div>
                        <div className="text-2xl font-bold line-height-1">{stat.value}</div>
                        <div className="text-xs mt-1" style={{ opacity: 0.8 }}>
                            {stat.delta}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── Sticky Key-Numbers Strip ──────────────────────────────────────────
    const renderKeyNumbers = () => (
        <div
            className="surface-card border-1 surface-border border-round-md shadow-1 p-3 mb-3"
            style={{ position: 'sticky', top: '0.5rem', zIndex: 10 }}
        >
            <div className="grid">
                {keyNumbers.map((kn) => (
                    <div className="col-6 md:col-3" key={kn.label}>
                        <div
                            className="pl-2"
                            style={{ borderLeft: `3px solid ${kn.color}` }}
                        >
                            <div className="text-xs uppercase text-color-secondary font-semibold" style={{ letterSpacing: '.06em' }}>
                                {kn.label}
                            </div>
                            <div className="text-lg font-bold text-900 mt-1">{kn.value}</div>
                            <div className="text-xs text-400 mt-1">{kn.help}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── Section intro (top of each tab) ───────────────────────────────────
    const renderSectionIntro = (sectionNum, sectionLabel, title, sub, componentCount) => (
        <div className="surface-card border-1 surface-border border-round-md shadow-1 p-3 mb-3 flex flex-wrap gap-3 align-items-center">
            <div className="flex-1">
                <div className="text-xs uppercase text-400 font-semibold mb-1" style={{ letterSpacing: '.07em' }}>
                    Section {sectionNum} of 4 · {sectionLabel}
                </div>
                <h3 className="m-0 text-lg font-bold text-900">{title}</h3>
                <div className="text-sm text-color-secondary mt-1">{sub}</div>
            </div>
            <Tag value={`${componentCount} components`} severity="secondary" />
        </div>
    );

    // ── Recommendation callout (Tab 1 only) ───────────────────────────────
    const renderRecommendationCallout = () => {
        const verdict = recommendations?.overallRecommendation || {};
        return (
            <div
                className="border-round-md p-3 mb-3 flex gap-3 align-items-center"
                style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%)',
                    border: '1px solid #bbf7d0',
                }}
            >
                <div
                    className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                    style={{ width: '3rem', height: '3rem', background: '#10b981', color: '#fff' }}
                >
                    <i className="pi pi-check text-xl"></i>
                </div>
                <div className="flex-1">
                    <div
                        className="text-xs uppercase font-bold"
                        style={{ color: '#047857', letterSpacing: '.08em' }}
                    >
                        Advisor Recommendation
                    </div>
                    <div className="text-lg font-bold text-900 mt-1">
                        {verdict.verdictLabel || 'Review the metrics below'}
                    </div>
                    <div className="text-sm text-700 mt-1 line-height-3">
                        {verdict.message ||
                            'Once data is fully loaded, a tailored recommendation will appear here based on the customer\'s affordability, FOIR, and existing debt.'}
                    </div>
                </div>
            </div>
        );
    };

    // ── Reusable: sub-group divider inside a tab ──────────────────────────
    const renderSubgroup = (title) => (
        <div className="flex align-items-center gap-3 mt-4 mb-2">
            <span className="text-xs uppercase font-bold text-color-secondary flex align-items-center gap-2" style={{ letterSpacing: '.1em' }}>
                <i className="pi pi-circle-fill text-primary" style={{ fontSize: '.6rem' }}></i>
                {title}
            </span>
            <span className="flex-1" style={{ height: '1px', background: 'var(--surface-border)' }}></span>
        </div>
    );

    // ── Colored subgroup section wrapper ─────────────────────────────────
    // Emimeter-style: each subgroup gets a tinted background + colored left
    // accent, so the eye can instantly separate one section from the next.
    // Tones: 'info' (blue), 'warn' (amber), 'success' (green).
    const renderSubgroupSection = (title, tone, children) => {
        const palette = {
            info:    { bg: '#f5f9ff', accent: 'var(--blue-500)',   dotColor: 'var(--blue-500)' },
            warn:    { bg: '#fffbeb', accent: 'var(--orange-500)', dotColor: 'var(--orange-500)' },
            success: { bg: '#f0fdf4', accent: 'var(--green-500)',  dotColor: 'var(--green-500)' },
        }[tone] || { bg: 'var(--surface-50)', accent: 'var(--primary-color)', dotColor: 'var(--primary-color)' };

        return (
            <div
                className="border-round-md p-3 mb-3"
                style={{
                    background: palette.bg,
                    borderLeft: `4px solid ${palette.accent}`,
                }}
            >
                <div className="flex align-items-center gap-3 mb-3">
                    <span
                        className="text-xs uppercase font-bold flex align-items-center gap-2"
                        style={{ letterSpacing: '.1em', color: palette.accent }}
                    >
                        <i className="pi pi-circle-fill" style={{ fontSize: '.6rem' }}></i>
                        {title}
                    </span>
                    <span className="flex-1" style={{ height: '1px', background: 'var(--surface-border)' }}></span>
                </div>
                {children}
            </div>
        );
    };

    // ── Reusable: component card with icon-chip header ────────────────────
    const renderComponentCard = (icon, iconBg, iconColor, title, desc, body) => (
        <section
            className="surface-card border-1 surface-border border-round-md shadow-1 mb-3 overflow-hidden"
            aria-label={title}
        >
            <div className="p-3 border-bottom-1 surface-border flex align-items-center gap-3">
                <span
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{ width: '2rem', height: '2rem', background: iconBg, color: iconColor }}
                    aria-hidden="true"
                >
                    <i className={`pi ${icon}`}></i>
                </span>
                <div>
                    <h4 className="m-0 text-sm font-bold text-900">{title}</h4>
                    <div className="text-xs text-color-secondary mt-1">{desc}</div>
                </div>
            </div>
            <div className="p-3">{body}</div>
        </section>
    );

    // ── Reusable: small metric block ──────────────────────────────────────
    const renderMetric = ({ label, value, footnote, tone }) => {
        const toneClass = tone === 'positive' ? 'surface-100' : tone === 'negative' ? 'surface-100' : 'surface-50';
        const valueColor = tone === 'positive' ? 'var(--green-700)' : tone === 'negative' ? 'var(--red-700)' : 'var(--text-color)';
        const bg = tone === 'positive' ? '#ecfdf5' : tone === 'negative' ? '#fef2f2' : 'var(--surface-50)';
        return (
            <div className={`p-3 border-round-md ${toneClass}`} style={{ background: bg }}>
                <div className="text-xs uppercase text-color-secondary font-semibold mb-2" style={{ letterSpacing: '.06em' }}>
                    {label}
                </div>
                <div className="text-xl font-bold line-height-1" style={{ color: valueColor, letterSpacing: '-.015em' }}>
                    {value}
                </div>
                {footnote && (
                    <div className="text-xs text-400 mt-2">{footnote}</div>
                )}
            </div>
        );
    };

    // ── BT fetch trigger — call this when a row is expanded. Idempotent:
    //    guards against duplicate calls when the user rapidly toggles.
    //    Reads the borrower "context" (salary, cibil, employer, salary bank)
    //    from formData + eligibilityInputs — the same fields the DSA entered
    //    on the Offers & Eligibility step, so we send an equivalent payload
    //    to the offer engine as if this were a fresh loan of `outstanding`
    //    size.
    const triggerBTFetch = (loan) => {
        const key = loan?.id || loan?.key;
        if (!key || !leadId) return;
        // Skip if already fetched (or currently loading)
        if (btOffersByLoan[key]) return;
        // Skip synthetic "new offer" row — that IS the proposal, no BT needed
        if (loan?.isNewLoan) return;

        setBtOffersByLoan((prev) => ({
            ...prev,
            [key]: { loading: true, best: null, all: [], source: null, error: null },
        }));

        // Salary can live under any of these keys depending on how the
        // borrower record was hydrated (OCR / Equifax / manual entry).
        // Portfolio's KPI header already resolves these successfully, so
        // we do the same walk here.
        const salary = formData?.net_salary
            || formData?.salary
            || formData?.monthly_income
            || formData?.net_salary_per_month
            || eligibilityInputs?.salary
            || eligibilityInputs?.net_salary
            || null;
        const cibil = formData?.credit_score
            || formData?.cibil_score
            || eligibilityInputs?.credit_score
            || eligibilityInputs?.cibil_score
            || null;
        // employer_name is supposed to be a free-text string. In some wizard
        // flows the multi-select company picker stores an ARRAY of IDs there
        // instead — guard so we never send an array to the engine's
        // "employer_name" string field. If the field is an array, drop it;
        // the array is the correct value for selected_company_ids anyway.
        const rawEmployerName = formData?.employer_name;
        const employerName = (typeof rawEmployerName === 'string' && rawEmployerName.trim())
            ? rawEmployerName.trim()
            : null;
        // selected_company_ids should be an array of UUIDs. If formData
        // stashed the same array under employer_name, prefer whichever is
        // present.
        const selectedCompanyIds = formData?.selected_company_ids
            || (Array.isArray(rawEmployerName) ? rawEmployerName : null);

        const borrowerContext = {
            salary,
            cibil_score: cibil,
            existing_emi: formData?.existing_emi || 0,
            employer_name: employerName,
            salary_bank_account: formData?.salary_bank_account || null,
            selected_company_ids: selectedCompanyIds,
        };
        // Debug log — helps diagnose "No BT available" issues. Safe to keep;
        // it prints ONCE per loan-first-expand and doesn't leak PII beyond
        // what's already in the wizard console.
        // eslint-disable-next-line no-console
        console.info('[BT] Fetch triggered for loan', {
            loanKey: key,
            // Log BOTH possible shapes so it's clear whether the transformed
            // row (loanType/outstanding/remainingTenure) or the raw DB shape
            // (loan_type/outstanding_loan/remaining_tenure) was passed in.
            loanType: loan?.loan_type || loan?.loanType,
            outstanding: loan?.outstanding_loan || loan?.outstanding,
            tenure: loan?.remaining_tenure || loan?.remainingTenure,
            borrowerContext,
        });

        fetchBestBTOffer(leadId, loan, borrowerContext)
            .then((res) => {
                setBtOffersByLoan((prev) => ({ ...prev, [key]: { ...res, loading: false } }));
            })
            .catch((err) => {
                setBtOffersByLoan((prev) => ({
                    ...prev,
                    [key]: { loading: false, best: null, all: [], source: 'error', error: err?.message || 'Fetch failed' },
                }));
            });
    };

    // ── 1.1  All Loans Overview — KPI tiles + dense loan rows ─────────────
    //
    // Layout (rebuilt 2026-06-30):
    //   1. KPI tile row at the top (Total Loans / Outstanding / Monthly EMI /
    //      Weighted Avg ROI). When a new offer is selected the EMI and
    //      Outstanding tiles show a small green delta so the DSA can see the
    //      before/after at a glance.
    //   2. Dense list — ONE LINE per loan by default. Click the row to expand
    //      a 4-field detail strip (Outstanding · EMI · Tenure Left · Total
    //      Interest). Lets a borrower with 6-7 loans fit on one screen
    //      instead of scrolling past 900+ px of tall cards.
    //
    // The new offer (if any) is appended as a 7th row with green accent so
    // the DSA sees existing + proposed in the same place.
    const renderAllLoansOverview = () => {
        // Humanize a loan_type slug — backend `getSummary()` already returns a
        // `loan_type_label` for DB rows, but Equifax-derived rows on the
        // wizard sometimes carry just the raw slug ("business_loan_unsecured")
        // or a snake-cased value. Convert anything snake/kebab into Title Case
        // so the card title reads "Business Loan Unsecured" not the slug.
        const formatLoanType = (loan) => {
            if (loan?.loan_type_label) return loan.loan_type_label;
            const raw = loan?.loan_type || loan?.type || 'Loan';
            return String(raw)
                .replace(/[_-]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .split(' ')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(' ');
        };

        // Loan-type visual identity: icon + color chip per product family.
        // Non-verbal scan cue (Bharat Principle 3) — DSA identifies loan
        // categories at a glance without reading the label. Uses PrimeIcons
        // + theme color variables only (no hex).
        //
        // Match order matters: we test for the most specific keywords first
        // (credit-card before consumer, business before personal, etc.) so a
        // "Business Credit Card" doesn't accidentally match plain "Card".
        const getLoanIcon = (loanTypeLabel) => {
            const s = String(loanTypeLabel || '').toLowerCase();
            if (s.includes('credit') || s.includes('card'))                 return { icon: 'pi-credit-card', color: 'pink' };
            if (s.includes('home') || s.includes('house') || s.includes('housing') || s.includes('property') || s.includes('lap')) return { icon: 'pi-home', color: 'cyan' };
            if (s.includes('auto') || s.includes('car') || s.includes('vehicle') || s.includes('two wheeler') || s.includes('two_wheeler')) return { icon: 'pi-car', color: 'orange' };
            if (s.includes('business') || s.includes('msme') || s.includes('working capital')) return { icon: 'pi-briefcase', color: 'purple' };
            if (s.includes('education') || s.includes('scholar')) return { icon: 'pi-book', color: 'teal' };
            if (s.includes('gold'))                              return { icon: 'pi-star', color: 'yellow' };
            if (s.includes('consumer') || s.includes('durable')) return { icon: 'pi-shopping-bag', color: 'indigo' };
            if (s.includes('personal'))                          return { icon: 'pi-user', color: 'blue' };
            return { icon: 'pi-money-bill', color: 'gray' };
        };

        // 1) Existing loans → rows
        const existingRows = (existingLoans || []).map((loan) => {
            const loanType = formatLoanType(loan);
            const lenderName = loan.lender_name || loan.lender || '—';
            const remainingTenure = loan.remaining_tenure || loan.tenure_remaining || null;
            const isRevolving = loanType.toLowerCase().includes('credit') || loanType.toLowerCase().includes('card');
            const outstanding = Number(loan.outstanding_loan || loan.outstanding_amount || 0);
            const emi = Number(loan.emi || loan.emi_amount || 0);
            const roi = Number(loan.interest_rate || 0);
            // Remaining interest = "interest you still need to pay until this
            // loan ends". The Calculate engine on the Existing Loans step
            // persists this as `outstanding_interest`; prefer that when present
            // (matches the "Interest ₹1,716" the DSA saw on the previous
            // step under "Remaining (5 mo)"). Fall back to the EMI*tenure -
            // outstanding approximation only when the precise value isn't
            // available (e.g. a freshly added loan that hasn't been Calculated
            // yet). Previously this column showed the approximation
            // unconditionally, producing tiny mis-labeled values like ₹43.
            let remainingInterest = null;
            if (loan.outstanding_interest != null && loan.outstanding_interest !== '') {
                remainingInterest = Number(loan.outstanding_interest);
            } else if (remainingTenure && emi) {
                remainingInterest = Math.max(0, emi * Number(remainingTenure) - outstanding);
            }
            // Helper: parse a number-or-null without coercing nullish to 0.
            // We need null to flow through so the expand can show "—" for
            // fields the Calculate engine hasn't filled (e.g. paid_tenure
            // unknown → principal_paid_till_date stays null).
            const num = (v) => (v == null || v === '' ? null : Number(v));
            return {
                key: loan.id || `${loanType}-${lenderName}-${outstanding}`,
                loanType,
                lenderName,
                outstanding,
                emi,
                roi,
                remainingTenure,
                tenureLeft: isRevolving ? 'Revolving' : (remainingTenure ? `${remainingTenure} mo` : '—'),
                totalInterest: remainingInterest,
                isRevolving,
                isNewLoan: false,
                // ── Detail fields for the expanded view ──────────────────
                // Carry the full Calculate-engine breakdown so the expand
                // can show Paid So Far / Remaining / Whole Loan without
                // the row component having to know the loan object's shape.
                detail: {
                    paidTenure: num(loan.paid_tenure),
                    tenureMonths: num(loan.tenure_months),
                    loanStartDate: loan.loan_start_date || null,
                    loanEndDate: loan.loan_end_date || null,
                    originalLoanAmount: num(loan.original_loan_amount),
                    principalPaid: num(loan.principal_paid_till_date),
                    interestPaid: num(loan.interest_paid_till_date),
                    emiPaid: num(loan.emi_paid_till_date),
                    remainingPrincipal: num(loan.remaining_principal),
                    remainingInterest: num(loan.outstanding_interest),
                    remainingEmiTotal: num(loan.remaining_emi_total),
                    totalInterestLifetime: num(loan.total_interest),
                    totalEmiPayment: num(loan.total_emi_payment),
                },
            };
        });

        // 2) Selected offer → one extra row if present
        const hasOffer = (analysis?.newLoanAmount || 0) > 0 || (analysis?.newEMI || 0) > 0;
        const newLoanRow = hasOffer ? {
            key: 'new-loan-row',
            loanType: 'Personal Loan',
            lenderName: `${bankName || 'New lender'} · Proposed`,
            outstanding: Number(analysis?.newLoanAmount || 0),
            emi: Number(analysis?.newEMI || 0),
            roi: Number(analysis?.newLoanRate || 0),
            remainingTenure: Number(analysis?.newLoanTenure || 0) || null,
            tenureLeft: (analysis?.newLoanTenure || 0) > 0 ? `${analysis.newLoanTenure} mo` : '—',
            totalInterest: (analysis?.newLoanTotalInterest != null)
                ? Number(analysis.newLoanTotalInterest)
                : ((analysis?.newEMI && analysis?.newLoanTenure)
                    ? Math.max(0, Number(analysis.newEMI) * Number(analysis.newLoanTenure) - Number(analysis.newLoanAmount || 0))
                    : 0),
            isRevolving: false,
            isNewLoan: true,
            // A proposed loan has nothing paid yet — Paid column shows zeros,
            // Remaining = the whole loan, Whole Loan = the proposed terms.
            detail: {
                paidTenure: 0,
                tenureMonths: Number(analysis?.newLoanTenure || 0) || null,
                loanStartDate: null,
                loanEndDate: null,
                originalLoanAmount: Number(analysis?.newLoanAmount || 0),
                principalPaid: 0,
                interestPaid: 0,
                emiPaid: 0,
                remainingPrincipal: Number(analysis?.newLoanAmount || 0),
                remainingInterest: analysis?.newLoanTotalInterest != null
                    ? Number(analysis.newLoanTotalInterest)
                    : null,
                remainingEmiTotal: (analysis?.newEMI && analysis?.newLoanTenure)
                    ? Number(analysis.newEMI) * Number(analysis.newLoanTenure)
                    : null,
                totalInterestLifetime: analysis?.newLoanTotalInterest != null
                    ? Number(analysis.newLoanTotalInterest)
                    : null,
                totalEmiPayment: (analysis?.newEMI && analysis?.newLoanTenure)
                    ? Number(analysis.newEMI) * Number(analysis.newLoanTenure)
                    : null,
            },
        } : null;

        const rows = [...existingRows, ...(newLoanRow ? [newLoanRow] : [])];

        // 3) KPI roll-up — separates existing-only totals from existing+new so
        //    the tile can show a delta when an offer is on the table.
        const existingCount = existingRows.length;
        const existingOutstanding = existingRows.reduce((s, r) => s + r.outstanding, 0);
        const existingEmi = existingRows.reduce((s, r) => s + r.emi, 0);
        // Weighted-avg ROI: weight by outstanding so a ₹15L loan at 10% beats
        // a ₹5k credit card at 47% — gives the DSA the true cost-of-debt rate
        // rather than the misleading simple average.
        const roiWeightSum = existingRows.reduce((s, r) => s + r.outstanding * r.roi, 0);
        const weightedRoi = existingOutstanding > 0 ? roiWeightSum / existingOutstanding : 0;
        const newEmi = newLoanRow?.emi || 0;
        const newOutstanding = newLoanRow?.outstanding || 0;

        // ── KPI tile ───────────────────────────────────────────────────────
        // KPI tile — compact P15 variant. Reduced padding (p-3 not the
        // default 1.5rem card padding), smaller icon chip (1.75rem), value
        // still text-lg for scan-at-glance. Optional accentColor prop lets
        // the CIBIL tile carry a semantic tint (green/orange/red band) —
        // for all other tiles it stays the neutral bg-primary-50.
        const tile = ({ label, value, delta, icon, accentColor }) => (
            <div
                className="surface-card border-1 surface-border border-round-md h-full p-3"
                style={accentColor ? {
                    borderLeftWidth: '3px',
                    borderLeftStyle: 'solid',
                    borderLeftColor: `var(--${accentColor}-500)`,
                } : undefined}
            >
                <div className="flex justify-content-between align-items-start gap-2">
                    <div className="flex flex-column gap-1 min-w-0 flex-1">
                        <span className="text-600 text-xs font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis"
                            style={{ letterSpacing: '.01em' }}>
                            {label}
                        </span>
                        <span className="text-900 text-lg font-semibold line-height-1"
                            style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {value}
                        </span>
                    </div>
                    <div
                        className={`flex align-items-center justify-content-center border-round flex-shrink-0`}
                        style={{
                            width: '1.75rem',
                            height: '1.75rem',
                            background: accentColor ? `var(--${accentColor}-50)` : 'var(--primary-50)',
                        }}
                    >
                        <i className={`pi ${icon}`}
                            style={{
                                fontSize: '0.85rem',
                                color: accentColor ? `var(--${accentColor}-600)` : 'var(--primary-color)',
                            }}
                            aria-hidden="true" />
                    </div>
                </div>
                {delta && (
                    <span className="block mt-2 text-xs font-medium"
                        style={{ color: accentColor ? `var(--${accentColor}-700)` : 'var(--green-700)' }}>
                        {delta}
                    </span>
                )}
            </div>
        );

        // ── Dense loan row ────────────────────────────────────────────────
        // Default state: one line per loan with the headline numbers inline.
        // On `< md` it converts to a 2-line layout (lender on top, numbers
        // in a compact 4-cell bar below) so it still fits a phone viewport.
        // Click anywhere on the row to expand the detail strip (kept the
        // existing 4-field grid for parity with the previous design).
        const renderLoanRow = (row) => {
            const isNew = row.isNewLoan;
            let accentColor = 'var(--blue-500)';
            let bgColor = 'var(--surface-card)';
            if (isNew) {
                accentColor = 'var(--green-500)';
                bgColor = 'var(--green-50)';
            } else if (row.isRevolving) {
                accentColor = 'var(--orange-500)';
            }

            const isOpen = !!expandedLoanRows[row.key];

            return (
                <div
                    key={row.key}
                    className="border-round-lg surface-border border-1 cursor-pointer transition-all transition-duration-150 shadow-1 hover:shadow-3"
                    style={{
                        borderLeftWidth: '4px',
                        borderLeftStyle: 'solid',
                        borderLeftColor: accentColor,
                        background: bgColor,
                    }}
                    onClick={() => {
                        // Toggle expand + lazy-fetch BT offers the first time
                        // the row opens. triggerBTFetch is idempotent — safe
                        // to call on every open, only the first call hits the
                        // network.
                        setExpandedLoanRows((prev) => {
                            const opening = !prev[row.key];
                            if (opening) triggerBTFetch(row);
                            return { ...prev, [row.key]: opening };
                        });
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedLoanRows((prev) => {
                                const opening = !prev[row.key];
                                if (opening) triggerBTFetch(row);
                                return { ...prev, [row.key]: opening };
                            });
                        }
                    }}
                    aria-expanded={isOpen}
                >
                    {/* ─── Headline strip ─── Desktop: one line. Mobile: 2 lines. */}
                    <div className="p-2 md:p-3">
                        {/* Top line: chevron + icon chip + type+lender (left) + ROI (right). */}
                        <div className="flex align-items-center gap-2">
                            <i className={`pi ${isOpen ? 'pi-chevron-down' : 'pi-chevron-right'} text-color-secondary flex-shrink-0`}
                                style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                            {/* Loan-type icon chip: circular, tinted per product
                                family. 1.75rem square = same visual weight as
                                the ROI value on the right, forms a scannable
                                left-anchor for the row. */}
                            {(() => {
                                const { icon, color } = getLoanIcon(row.loanType);
                                return (
                                    <div
                                        className={`flex align-items-center justify-content-center border-round bg-${color}-50 flex-shrink-0`}
                                        style={{ width: '1.75rem', height: '1.75rem' }}
                                        aria-hidden="true"
                                    >
                                        <i className={`pi ${icon} text-${color}-600`} style={{ fontSize: '0.85rem' }} />
                                    </div>
                                );
                            })()}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {row.loanType}
                                    {isNew && (
                                        <Tag value="NEW" severity="success" className="ml-2"
                                            style={{ fontSize: '0.55rem', padding: '0.05rem 0.3rem', verticalAlign: 'middle' }} />
                                    )}
                                </div>
                                <div className="text-xs text-color-secondary white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {row.lenderName}
                                </div>
                            </div>

                            {/* ─── BT recommendation chip ────────────────
                                Always visible on every non-new loan row so
                                the DSA sees the feature exists. Five states:
                                  idle     → "Check BT" (click to fetch)
                                  loading  → spinner
                                  better   → "BT @ X% · Save ₹Y" (green)
                                  same     → "Best rate" (grey)
                                  none     → "No BT available" (grey)
                                Chip is clickable in all states except loading:
                                idle triggers the fetch; loaded opens dialog.  */}
                            {!isNew && (() => {
                                const bt = btOffersByLoan[row.key];
                                const state = !bt ? 'idle'
                                    : bt.loading ? 'loading'
                                    : (!bt.best || bt.source === 'ineligible' || bt.source === 'unavailable') ? 'none'
                                    : 'ready';

                                const handleChipClick = (e) => {
                                    e.stopPropagation();
                                    if (state === 'idle') {
                                        triggerBTFetch(row);
                                    } else if (state === 'ready') {
                                        setBtDialogLoan(row);
                                    }
                                };

                                let icon = 'pi-bolt';
                                let iconColor = 'text-primary';
                                let bg = 'surface-100';
                                let label = 'Check BT';
                                let labelColor = 'text-primary';

                                if (state === 'loading') {
                                    icon = 'pi-spinner pi-spin';
                                    iconColor = 'text-color-secondary';
                                    label = 'Checking…';
                                    labelColor = 'text-color-secondary';
                                } else if (state === 'none') {
                                    icon = 'pi-times-circle';
                                    iconColor = 'text-color-secondary';
                                    label = 'No BT available';
                                    labelColor = 'text-color-secondary';
                                } else if (state === 'ready') {
                                    const { best } = bt;
                                    const delta = btDelta(row.roi, best.roi);
                                    const isBetter = delta.isBetter && best.savings > 0;
                                    if (isBetter) {
                                        icon = 'pi-bolt';
                                        iconColor = 'text-green-700';
                                        bg = 'bg-green-50';
                                        labelColor = best.isMarketRate ? 'text-blue-700' : 'text-green-700';
                                        label = `BT @ ${best.roi.toFixed(2)}%${best.savings >= 1000 ? ` · Save ${formatCurrency(best.savings)}` : ''}`;
                                    } else {
                                        // Borrower already has the best rate — celebrate it
                                        // (calm teal, not "action" green — different signal).
                                        icon = 'pi-verified';
                                        iconColor = 'text-teal-700';
                                        bg = '';  // background provided by .bt-chip-best gradient
                                        label = 'Best rate';
                                        labelColor = 'text-teal-700';
                                    }
                                }

                                // Attention class per state:
                                //   isBetterReady    → bt-chip-glow  (urgent green pulse — click to save)
                                //   ready & same     → bt-chip-best  (calm teal pulse — celebrates "best rate")
                                //   idle             → bt-chip-idle  (subtle blue ring)
                                //   loading / none   → no extra class
                                const isBetterReady = state === 'ready'
                                    && bt.best
                                    && btDelta(row.roi, bt.best.roi).isBetter
                                    && bt.best.savings > 0;
                                const isBestRateReady = state === 'ready' && !isBetterReady && bt.best;
                                const attentionClass = isBetterReady
                                    ? 'bt-chip-glow'
                                    : isBestRateReady
                                        ? 'bt-chip-best'
                                        : state === 'idle'
                                            ? 'bt-chip-idle'
                                            : '';

                                return (
                                    <button
                                        type="button"
                                        onClick={handleChipClick}
                                        disabled={state === 'loading' || state === 'none'}
                                        className={`border-round-md px-2 py-1 flex align-items-center gap-1 flex-shrink-0 ${state === 'loading' || state === 'none' ? 'opacity-70 border-none' : 'cursor-pointer'} transition-all transition-duration-150 ${bg} ${attentionClass}`}
                                        style={{ fontSize: '0.65rem', maxWidth: '14rem' }}
                                        title={
                                            state === 'idle' ? 'Click to find balance-transfer offers for this loan'
                                            : state === 'loading' ? 'Fetching lender quotes…'
                                            : state === 'none' ? 'No refinance options found for this loan'
                                            : state === 'ready' && bt.best
                                                ? `Best offer: ${bt.best.lender_name} @ ${bt.best.roi.toFixed(2)}%. Click for all lenders.`
                                                : ''
                                        }
                                    >
                                        <i className={`pi ${icon} ${iconColor}`} style={{ fontSize: '0.65rem' }} />
                                        <span className={`white-space-nowrap overflow-hidden text-overflow-ellipsis ${labelColor}`}
                                            style={{ fontWeight: isBetterReady ? 700 : 500 }}>
                                            {label}
                                        </span>
                                        {state === 'ready' && bt.best && (
                                            <i className="pi pi-chevron-right text-color-secondary" style={{ fontSize: '0.55rem' }} />
                                        )}
                                    </button>
                                );
                            })()}
                        </div>

                        {/* Bottom line: all 5 stats inline with · separators.
                            PrimeFlex has no `gap-x-*` utility (only `gap-*`
                            which applies to both axes), and we want a lot
                            of horizontal breathing room with tight vertical
                            wrap — so we use flex-wrap + inline column-gap /
                            row-gap style. Extra mt-3 (was mt-2) so the line
                            sits clearly below the loan title/lender header
                            rather than crowding it. */}
                        <div className="hidden md:flex align-items-center flex-wrap mt-3 text-xs"
                            style={{
                                fontVariantNumeric: 'tabular-nums',
                                paddingLeft: '1.2rem',
                                columnGap: '0.85rem',
                                rowGap: '0.3rem',
                            }}>
                            <span><span className="text-color-secondary">Outstanding</span> <b className="text-900 ml-1">{formatCurrency(row.outstanding)}</b></span>
                            <span className="text-300">·</span>
                            <span>
                                <span className="text-color-secondary">EMI</span>
                                <b className={`ml-1 ${isNew ? 'text-green-700' : 'text-900'}`}>{row.emi ? formatCurrency(row.emi) : '—'}</b>
                                {/* Per-loan FOIR: what % of monthly income
                                    this one loan's EMI eats up. Color-tiered:
                                    < 15% healthy (green) / 15-30% watch
                                    (orange) / > 30% danger (red). The word
                                    "FOIR" gets an uppercase bold treatment
                                    so it reads as a labeled metric, not a
                                    raw percentage in parentheses. */}
                                {(() => {
                                    const net = Number(analysis?.netSalary) || 0;
                                    const emi = Number(row.emi) || 0;
                                    if (net <= 0 || emi <= 0) return null;
                                    const pct = (emi / net) * 100;
                                    const color = pct < 15 ? 'var(--green-700)'
                                                : pct < 30 ? 'var(--orange-700)'
                                                : 'var(--red-700)';
                                    return (
                                        <span className="ml-2" style={{ fontSize: '0.65rem' }}
                                            title={`This loan uses ${pct.toFixed(1)}% of monthly income (Fixed Obligation to Income Ratio)`}>
                                            <span className="text-color-secondary">used </span>
                                            <b style={{ color, fontVariantNumeric: 'tabular-nums' }}>{pct.toFixed(1)}%</b>
                                            <b className="ml-1"
                                                style={{
                                                    color,
                                                    letterSpacing: '.05em',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 700,
                                                }}>FOIR</b>
                                        </span>
                                    );
                                })()}
                            </span>
                            <span className="text-300">·</span>
                            <span><span className="text-color-secondary">Tenure Left</span> <b className="text-900 ml-1">{row.tenureLeft}</b></span>
                            <span className="text-300">·</span>
                            <span><span className="text-color-secondary">Interest Left</span> <b className="text-900 ml-1">{row.totalInterest != null && row.totalInterest >= 0 ? formatCurrency(row.totalInterest) : '—'}</b></span>
                            <span className="inline-flex align-items-center border-round"
                                style={{
                                    background: 'var(--primary-50)',
                                    color: 'var(--primary-700)',
                                    padding: '0.15rem 0.5rem',
                                    border: '1px solid var(--primary-100)',
                                }}>
                                <span style={{ fontSize: '0.6rem', opacity: 0.75, letterSpacing: '.04em', textTransform: 'uppercase', fontWeight: 600 }}>ROI</span>
                                <b className="ml-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPercentage(row.roi)}</b>
                                <span className="ml-1" style={{ fontSize: '0.6rem', opacity: 0.75 }}>p.a.</span>
                            </span>
                        </div>

                        {/* Mobile-only condensed 4-cell bar + ROI row. ROI
                            gets its own full-width strip below the 2×2 grid
                            so it's not squeezed into a corner cell. */}
                        <div className="md:hidden grid mt-2 text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            <div className="col-6"><div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>OUTSTANDING</div><b className="text-900">{formatCurrency(row.outstanding)}</b></div>
                            <div className="col-6">
                                <div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>EMI</div>
                                <b className={isNew ? 'text-green-700' : 'text-900'}>{row.emi ? formatCurrency(row.emi) : '—'}</b>
                                {/* Per-loan FOIR under EMI on mobile too */}
                                {(() => {
                                    const net = Number(analysis?.netSalary) || 0;
                                    const emi = Number(row.emi) || 0;
                                    if (net <= 0 || emi <= 0) return null;
                                    const pct = (emi / net) * 100;
                                    const color = pct < 15 ? 'var(--green-700)'
                                                : pct < 30 ? 'var(--orange-700)'
                                                : 'var(--red-700)';
                                    return (
                                        <div style={{ fontSize: '0.6rem' }}>
                                            <span className="text-color-secondary">used </span>
                                            <b style={{ color, fontVariantNumeric: 'tabular-nums' }}>{pct.toFixed(1)}%</b>
                                            <b className="ml-1"
                                                style={{ color, letterSpacing: '.05em', textTransform: 'uppercase', fontWeight: 700 }}>
                                                FOIR
                                            </b>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="col-6"><div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>TENURE LEFT</div><b className="text-900">{row.tenureLeft}</b></div>
                            <div className="col-6"><div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>INTEREST LEFT</div><b className="text-900">{row.totalInterest != null && row.totalInterest >= 0 ? formatCurrency(row.totalInterest) : '—'}</b></div>
                            <div className="col-12 mt-1 flex justify-content-end">
                                <span className="text-color-secondary">
                                    <b className="text-900">{formatPercentage(row.roi)}</b> p.a.
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Expanded detail strip — 3 vertical columns, each is a
                        period of the loan lifecycle:
                          PAID SO FAR (X mo) │ REMAINING (Y mo) │ WHOLE LOAN (Z mo)
                        The 4 headline numbers already cover the "Remaining"
                        slice + total outstanding, so the expand adds the two
                        slices the user can't see in the headline — what's
                        already been paid (principal/interest/EMI breakdown)
                        and the lifetime totals (original amount, total
                        interest, total payment). Loan start/end dates appear
                        below the grid since they're not per-column.
                        Mobile: columns stack vertically (col-12 → col-4 at md). */}
                    {isOpen && (
                        <div className="px-3 pb-3 pt-2 border-top-1 surface-border" style={{ background: 'var(--surface-50)' }}>
                            {(() => {
                                const d = row.detail || {};
                                const fmt = (v) => (v == null || (typeof v === 'number' && v < 0)) ? '—' : formatCurrency(v);
                                const fmtMo = (v) => (v == null ? '—' : `${v} mo`);
                                const fmtDate = (v) => {
                                    if (!v) return '—';
                                    const dt = new Date(v);
                                    if (Number.isNaN(dt.getTime())) return v;
                                    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                                };
                                const cell = (lbl, val, accent = false) => (
                                    <div className="flex justify-content-between align-items-baseline py-1">
                                        <span className="text-color-secondary" style={{ fontSize: '0.65rem' }}>{lbl}</span>
                                        <b className={accent ? 'text-green-700' : 'text-900'} style={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                                            {val}
                                        </b>
                                    </div>
                                );
                                const colHeader = (lbl, sub, color) => (
                                    <div className="mb-2">
                                        <div className="font-bold text-900" style={{ fontSize: '0.7rem', letterSpacing: '.04em', textTransform: 'uppercase', color }}>
                                            {lbl}
                                        </div>
                                        <div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>{sub}</div>
                                    </div>
                                );
                                // ── Loan-lifecycle progress bar ─────────────
                                // Visual "how far along is this loan" — the
                                // number one thing a borrower/DSA wants to
                                // see when they open a loan card. Uses the
                                // paid_tenure / tenure_months ratio (falls
                                // back to principal_paid / original_amount
                                // if tenure data is incomplete). Custom
                                // segmented bar (paid | remaining) drawn
                                // with two flex divs so we can label both
                                // halves inline. PrimeReact ProgressBar
                                // itself doesn't support a two-tone/labelled
                                // bar in one component.
                                const totalMonths = Number(d.tenureMonths) || 0;
                                const paidMonths = Number(d.paidTenure) || 0;
                                let progressPct = 0;
                                if (totalMonths > 0) {
                                    progressPct = Math.min(100, Math.max(0, (paidMonths / totalMonths) * 100));
                                } else if (d.originalLoanAmount && d.principalPaid != null) {
                                    // Fallback when tenure is missing but
                                    // principal-paid is known.
                                    progressPct = Math.min(100, Math.max(0,
                                        (Number(d.principalPaid) / Number(d.originalLoanAmount)) * 100
                                    ));
                                }
                                const remainingPct = 100 - progressPct;
                                // Show the bar only when we have real data
                                // to compute a meaningful percentage.
                                const hasProgressData = (totalMonths > 0 && paidMonths >= 0)
                                    || (d.originalLoanAmount && d.principalPaid != null);

                                return (
                                    <>
                                        {hasProgressData && (
                                            <div className="mb-4">
                                                <div className="flex justify-content-between align-items-baseline mb-2">
                                                    <span className="text-color-secondary font-semibold"
                                                        style={{ fontSize: '0.65rem', letterSpacing: '.04em', textTransform: 'uppercase' }}>
                                                        Loan Progress
                                                    </span>
                                                    <span className="text-900 font-bold" style={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                                                        {progressPct.toFixed(0)}% paid
                                                    </span>
                                                </div>
                                                {/* Modernized progress bar:
                                                      - taller (0.9rem for visual weight)
                                                      - fully rounded caps (border-radius: full)
                                                      - green→teal gradient on the paid side (depth)
                                                      - inset shadow on the track (glass feel)
                                                      - drop shadow on the filled bar
                                                      - shimmer highlight overlay on the filled bar */}
                                                <div
                                                    className="relative overflow-hidden"
                                                    style={{
                                                        height: '0.9rem',
                                                        borderRadius: '999px',
                                                        background: 'linear-gradient(180deg, var(--orange-100) 0%, var(--orange-50) 100%)',
                                                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)',
                                                    }}
                                                    role="progressbar"
                                                    aria-valuenow={progressPct.toFixed(0)}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                    aria-label={`Loan is ${progressPct.toFixed(0)}% paid`}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${progressPct}%`,
                                                            height: '100%',
                                                            background: 'linear-gradient(90deg, var(--green-400) 0%, var(--green-600) 100%)',
                                                            borderRadius: '999px',
                                                            boxShadow: '0 1px 4px rgba(34, 197, 94, 0.35)',
                                                            transition: 'width 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        {/* Glass highlight overlay — thin translucent stripe
                                                            along the top edge, gives the bar a subtle 3D quality. */}
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '2px',
                                                                left: '4px',
                                                                right: '4px',
                                                                height: '25%',
                                                                background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 100%)',
                                                                borderRadius: '999px',
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                {/* Bottom caption */}
                                                <div className="flex justify-content-between mt-2"
                                                    style={{ fontSize: '0.65rem', color: 'var(--text-color-secondary)' }}>
                                                    <span>
                                                        <i className="pi pi-check-circle text-green-600 mr-1" style={{ fontSize: '0.6rem' }} />
                                                        {totalMonths > 0
                                                            ? `${paidMonths} of ${totalMonths} months paid`
                                                            : 'Progress by principal repaid'}
                                                    </span>
                                                    <span>
                                                        <i className="pi pi-clock text-orange-600 mr-1" style={{ fontSize: '0.6rem' }} />
                                                        {remainingPct.toFixed(0)}% remaining
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {/* ─── 3-panel comparison view ────────
                                            Each of the 3 loan-lifecycle
                                            stages gets its OWN colored panel
                                            card. Inside each panel, 3 metric
                                            rows show Principal / Interest /
                                            EMI with a mini bar visualizing
                                            THIS panel's value as a fraction
                                            of the corresponding "Whole Loan"
                                            total. So the DSA sees "you've
                                            paid 34% of principal, 61% of
                                            interest" without reading numbers. */}
                                        {(() => {
                                            // Whole-loan denominators for the mini bars
                                            const wholePrincipal = Number(d.originalLoanAmount) || 0;
                                            const wholeInterest = Number(d.totalInterestLifetime) || 0;
                                            const wholePayment = Number(d.totalEmiPayment) || 0;

                                            // pct helper (safe division, clamped 0-100)
                                            const pct = (num, den) => {
                                                if (!num || !den) return 0;
                                                return Math.min(100, Math.max(0, (Number(num) / Number(den)) * 100));
                                            };

                                            // Metric row = label + value + modern gradient bar + %.
                                            // Bar receives a gradient (two-stop) matching the panel
                                            // theme, rounded caps, and a subtle drop shadow so it
                                            // feels like a UI element rather than a flat rectangle.
                                            // barGradient is a CSS gradient string for the fill.
                                            const metricRow = (label, val, pctVal, barGradient, glowRgba) => (
                                                <div className="mb-3">
                                                    <div className="flex justify-content-between align-items-baseline">
                                                        <span className="text-color-secondary" style={{ fontSize: '0.7rem' }}>{label}</span>
                                                        <span className="text-900 font-bold" style={{ fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}>
                                                            {fmt(val)}
                                                        </span>
                                                    </div>
                                                    <div className="flex align-items-center gap-2 mt-1">
                                                        <div
                                                            className="flex-1 overflow-hidden"
                                                            style={{
                                                                height: '0.5rem',
                                                                borderRadius: '999px',
                                                                background: 'rgba(255,255,255,0.55)',
                                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: `${pctVal}%`,
                                                                    height: '100%',
                                                                    background: barGradient,
                                                                    borderRadius: '999px',
                                                                    boxShadow: glowRgba ? `0 1px 3px ${glowRgba}` : 'none',
                                                                    transition: 'width 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-900 font-semibold flex-shrink-0"
                                                            style={{ fontSize: '0.65rem', fontVariantNumeric: 'tabular-nums', minWidth: '2.5rem', textAlign: 'right' }}>
                                                            {pctVal.toFixed(0)}%
                                                        </span>
                                                    </div>
                                                </div>
                                            );

                                            // Colored panel — border-left accent + light-tint background.
                                            // Uses PrimeReact theme color vars so it matches the app palette.
                                            const panel = (title, subtitle, iconClass, colorName, children) => (
                                                <div
                                                    className={`col-12 md:col-4`}
                                                >
                                                    <div
                                                        className={`border-round-md p-3 h-full bg-${colorName}-50 border-1 border-${colorName}-200`}
                                                    >
                                                        <div className="flex align-items-center gap-2 mb-3">
                                                            <div
                                                                className={`flex align-items-center justify-content-center border-round bg-${colorName}-100 flex-shrink-0`}
                                                                style={{ width: '1.5rem', height: '1.5rem' }}
                                                            >
                                                                <i className={`pi ${iconClass} text-${colorName}-700`} style={{ fontSize: '0.7rem' }} />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className={`font-bold text-${colorName}-700 uppercase`}
                                                                    style={{ fontSize: '0.65rem', letterSpacing: '.04em' }}>
                                                                    {title}
                                                                </div>
                                                                <div className="text-color-secondary" style={{ fontSize: '0.6rem' }}>
                                                                    {subtitle}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {children}
                                                    </div>
                                                </div>
                                            );

                                            // Panel-specific gradients (two-stop, light → dark)
                                            // + coordinated shadow tint so each bar picks up the
                                            // parent panel's color language.
                                            const gGrad = 'linear-gradient(90deg, var(--green-400) 0%, var(--green-600) 100%)';
                                            const oGrad = 'linear-gradient(90deg, var(--orange-400) 0%, var(--orange-600) 100%)';
                                            const bGrad = 'linear-gradient(90deg, var(--blue-400) 0%, var(--blue-600) 100%)';
                                            const gGlow = 'rgba(34, 197, 94, 0.35)';
                                            const oGlow = 'rgba(249, 115, 22, 0.35)';
                                            const bGlow = 'rgba(59, 130, 246, 0.35)';

                                            return (
                                                <div className="grid">
                                                    {panel(
                                                        'Paid So Far',
                                                        fmtMo(d.paidTenure),
                                                        'pi-check-circle',
                                                        'green',
                                                        <>
                                                            {metricRow('Principal', d.principalPaid, pct(d.principalPaid, wholePrincipal), gGrad, gGlow)}
                                                            {metricRow('Interest',  d.interestPaid,  pct(d.interestPaid,  wholeInterest),  gGrad, gGlow)}
                                                            {metricRow('EMI Paid',  d.emiPaid,       pct(d.emiPaid,       wholePayment),   gGrad, gGlow)}
                                                        </>
                                                    )}
                                                    {panel(
                                                        'Remaining',
                                                        fmtMo(row.remainingTenure),
                                                        'pi-clock',
                                                        'orange',
                                                        <>
                                                            {metricRow('Principal', d.remainingPrincipal, pct(d.remainingPrincipal, wholePrincipal), oGrad, oGlow)}
                                                            {metricRow('Interest',  d.remainingInterest,  pct(d.remainingInterest,  wholeInterest),  oGrad, oGlow)}
                                                            {metricRow('EMI Left',  d.remainingEmiTotal,  pct(d.remainingEmiTotal,  wholePayment),   oGrad, oGlow)}
                                                        </>
                                                    )}
                                                    {panel(
                                                        'Whole Loan',
                                                        fmtMo(d.tenureMonths),
                                                        'pi-chart-bar',
                                                        'blue',
                                                        <>
                                                            {metricRow('Original Amt', d.originalLoanAmount,    100, bGrad, bGlow)}
                                                            {metricRow('Total Int.',   d.totalInterestLifetime, 100, bGrad, bGlow)}
                                                            {metricRow('Total Pmt.',   d.totalEmiPayment,       100, bGrad, bGlow)}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        {/* Start/end dates row — separate from the
                                            3-col grid because they belong to the
                                            loan as a whole, not to one slice. */}
                                        {(d.loanStartDate || d.loanEndDate) && (
                                            <div className="flex flex-wrap gap-3 mt-2 pt-2 border-top-1 surface-border" style={{ fontSize: '0.65rem' }}>
                                                <span className="text-color-secondary">
                                                    Start: <b className="text-900">{fmtDate(d.loanStartDate)}</b>
                                                </span>
                                                <span className="text-color-secondary">
                                                    End: <b className="text-900">{fmtDate(d.loanEndDate)}</b>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            );
        };

        // ── Empty state ──────────────────────────────────────────────────
        const body = rows.length === 0 ? (
            <div className="text-center text-color-secondary py-4">
                <i className="pi pi-inbox text-2xl mb-2 block text-400" aria-hidden="true"></i>
                <div className="text-sm">No existing loans recorded and no offer selected.</div>
            </div>
        ) : (
            <div className="flex flex-column gap-3">
                {/* KPI tiles — 5-up on lg (equal ~20% each via `lg:col`
                    flex-1), 3-per-row on md, 2-per-row on mobile.
                    CIBIL card comes FIRST so the DSA sees credit health
                    before the debt numbers.                              */}
                <div className="grid">
                    {(() => {
                        // CIBIL sourced from formData (already entered on
                        // Offers & Eligibility step).
                        const cibil = Number(
                            formData?.credit_score
                            || formData?.cibil_score
                            || eligibilityInputs?.credit_score
                            || 0
                        );
                        const isEntered = cibil >= 300 && cibil <= 900;

                        // Band definitions (CIBIL official ranges).
                        // Rainbow order (left→right on the arc, low→high score):
                        //   300-579  Poor       red
                        //   580-669  Fair       orange
                        //   670-739  Good       yellow
                        //   740-799  Very Good  light-green
                        //   800-900  Excellent  green
                        // Each band is one of 5 arc segments — equal 36° each
                        // (5 × 36 = 180°). The needle points to the exact score.
                        let cibilLabel = 'Not entered';
                        let labelColor = 'var(--text-color-secondary)';
                        if (isEntered) {
                            if      (cibil >= 800) { cibilLabel = 'Excellent'; labelColor = 'var(--green-700)'; }
                            else if (cibil >= 740) { cibilLabel = 'Very Good'; labelColor = 'var(--green-600)'; }
                            else if (cibil >= 670) { cibilLabel = 'Good';      labelColor = 'var(--yellow-700)'; }
                            else if (cibil >= 580) { cibilLabel = 'Fair';      labelColor = 'var(--orange-700)'; }
                            else                   { cibilLabel = 'Poor';      labelColor = 'var(--red-700)'; }
                        }

                        // Score → position on the arc.
                        // Clamp so out-of-range inputs still render sensibly.
                        const scoreForAngle = Math.max(300, Math.min(900, cibil || 300));
                        // Progress fraction 0 (score=300) → 1 (score=900)
                        const scoreFrac = (scoreForAngle - 300) / 600;

                        // Gauge geometry: semicircle centered at (50, 50),
                        // radius 40, stroked (not filled) with strokeWidth 12.
                        // The stroke IS the arc — no ring-segment path math
                        // needed. Each of the 5 bands is a separate arc path
                        // covering the FULL semicircle but visually clipped
                        // via stroke-dasharray so only its 20% portion draws.
                        const R = 40;
                        const CX = 50;
                        const CY = 50;
                        // Full semicircle arc path — from (CX-R, CY) sweeping
                        // over the top to (CX+R, CY). "A" params: rx ry x-axis
                        // large-arc-flag sweep-flag end-x end-y. large-arc=0
                        // (< 180°... but we have exactly 180°, both valid),
                        // sweep=1 to go over the top.
                        const semicirclePath = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;
                        // Circumference of the semicircle
                        const arcLen = Math.PI * R;
                        // Each band = 20% of arcLen
                        const seg = arcLen / 5;

                        // Colors per band, low→high score.
                        const bandColors = ['var(--red-500)', 'var(--orange-500)', 'var(--yellow-500)', 'var(--green-400)', 'var(--green-600)'];
                        // Empty-state uses same colors but faded via opacity
                        const bandOpacity = isEntered ? 1 : 0.25;

                        // Needle endpoint on the arc.
                        //
                        // Angle mapping matches the semicircle path M (CX-R, CY)
                        // A R R 0 0 1 (CX+R, CY):
                        //   t=0 (score=300) → left  → angle π  → cos=-1, sin=0
                        //   t=0.5 (score=600) → top → angle π/2 → cos=0,  sin=1
                        //   t=1 (score=900) → right → angle 0  → cos=1,  sin=0
                        // Formula: angle = π × (1 − t). Then x = CX + r·cos,
                        // y = CY − r·sin (SVG y grows downward, arc bulges up).
                        // needleR slightly inside stroke so needle tip visually
                        // meets the band.
                        const needleR = R - 3;
                        const needleAngle = Math.PI * (1 - scoreFrac);
                        const nx = CX + needleR * Math.cos(needleAngle);
                        const ny = CY - needleR * Math.sin(needleAngle);

                        return (
                            <div className="col-6 md:col-4 lg:col">
                                <div
                                    className="surface-card border-1 surface-border border-round-md h-full p-3"
                                    style={isEntered ? {
                                        borderLeftWidth: '3px',
                                        borderLeftStyle: 'solid',
                                        borderLeftColor: labelColor,
                                    } : undefined}
                                >
                                    {/* Horizontal split: label + score + band
                                        on the LEFT (matches the "label + value
                                        + delta" pattern of the other tiles);
                                        small rainbow gauge on the RIGHT
                                        (mirrors the icon chip in other tiles).
                                        Keeps tile height consistent across
                                        the KPI row. */}
                                    <div className="flex justify-content-between align-items-start gap-2">
                                        <div className="flex flex-column gap-1 min-w-0 flex-1">
                                            <span className="text-600 text-xs font-medium"
                                                style={{ letterSpacing: '.01em' }}>
                                                CIBIL Score
                                            </span>
                                            <span className="text-900 text-lg font-semibold line-height-1"
                                                style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {isEntered ? cibil : '—'}
                                            </span>
                                            <span className="block text-xs font-medium mt-1"
                                                style={{ color: labelColor }}>
                                                {cibilLabel}
                                            </span>
                                        </div>
                                        {/* Compact rainbow gauge (v2 — 2026-07-01).
                                            5 stroked arcs on top of each other,
                                            each with stroke-dasharray showing
                                            only its 20% band. Cleaner + more
                                            robust than the old ring-segment
                                            path math which was drawing extra
                                            arcs and looking like a full ring.
                                            viewBox 100×60: semicircle spans
                                            x∈[10,90], stroke 12 → total width
                                            100, height 50 (arc) + margin. */}
                                        <svg
                                            viewBox="0 0 100 60"
                                            style={{ width: '4rem', height: 'auto', flexShrink: 0 }}
                                            aria-label={`CIBIL score gauge: ${isEntered ? cibil : 'not entered'}`}
                                            role="img"
                                        >
                                            {bandColors.map((color, i) => (
                                                <path
                                                    key={i}
                                                    d={semicirclePath}
                                                    fill="none"
                                                    stroke={color}
                                                    strokeWidth="10"
                                                    strokeLinecap="butt"
                                                    strokeDasharray={`${seg} ${arcLen}`}
                                                    strokeDashoffset={-seg * i}
                                                    opacity={bandOpacity}
                                                />
                                            ))}
                                            {isEntered && (
                                                <>
                                                    <line
                                                        x1={CX} y1={CY}
                                                        x2={nx} y2={ny}
                                                        stroke="var(--text-color)"
                                                        strokeWidth="2.5"
                                                        strokeLinecap="round"
                                                    />
                                                    <circle cx={CX} cy={CY} r="4" fill="var(--text-color)" />
                                                    <circle cx={CX} cy={CY} r="1.75" fill="#fff" />
                                                </>
                                            )}
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    <div className="col-6 md:col-4 lg:col">
                        {tile({
                            label: 'Total Loans',
                            value: existingCount + (hasOffer ? 1 : 0),
                            delta: hasOffer ? `+1 with new offer` : null,
                            icon: 'pi-briefcase',
                        })}
                    </div>
                    <div className="col-6 md:col-4 lg:col">
                        {tile({
                            label: 'Total Outstanding',
                            value: formatCurrency(existingOutstanding),
                            delta: hasOffer && newOutstanding > 0 ? `+${formatCurrency(newOutstanding)} new loan` : null,
                            icon: 'pi-wallet',
                        })}
                    </div>
                    <div className="col-6 md:col-4 lg:col">
                        {tile({
                            label: 'Monthly EMI',
                            value: formatCurrency(existingEmi),
                            delta: hasOffer && newEmi > 0 ? `+${formatCurrency(newEmi)}/mo new EMI` : null,
                            icon: 'pi-calendar',
                        })}
                    </div>
                    <div className="col-6 md:col-4 lg:col">
                        {tile({
                            label: 'Weighted Avg ROI',
                            value: existingOutstanding > 0 ? formatPercentage(weightedRoi) : '—',
                            delta: null,
                            icon: 'pi-percentage',
                        })}
                    </div>
                </div>

                {/* Dense loan list. gap-3 (was gap-2) so the elevated
                    shadow of one card doesn't touch the next — visual
                    breathing room signals "these are separate loans". */}
                <div className="flex flex-column gap-3">
                    {rows.map(renderLoanRow)}
                </div>

                {/* ─── Total row ─────────────────────────────────────
                    Sums the same 4 columns as each loan row (Outstanding
                    · EMI · Tenure Left · Interest Left) plus a potential
                    "if you BT everything" savings chip. Visually distinct
                    from loan rows via:
                      - primary-tinted gradient background (not white)
                      - "TOTAL" label chip on the left instead of an icon
                      - bolder / larger values
                      - no chevron / not clickable / no shadow
                    Only shown when there are existing loans (skips the
                    empty and single-new-offer cases).                    */}
                {existingRows.length > 0 && (() => {
                    // Longest remaining tenure across all loans — see
                    // "Tenure aggregation" decision: MAX is the most
                    // useful figure (how long the borrower is locked in).
                    const longestTenure = existingRows.reduce(
                        (max, r) => Math.max(max, Number(r.remainingTenure) || 0),
                        0
                    );
                    // Sum of "Interest Left" — precise number from the
                    // Calculate engine (loan.outstanding_interest) is stored
                    // as row.totalInterest by the mapper above.
                    const totalInterestLeft = existingRows.reduce(
                        (s, r) => s + (Number(r.totalInterest) || 0),
                        0
                    );
                    // Potential BT savings — sum whatever offers we've
                    // fetched so far. Loans without a BT click yet
                    // contribute 0. Loans where the best offer is worse
                    // than current also contribute 0 (savings clamped >= 0).
                    let btSavingsPotential = 0;
                    let btLoansFetched = 0;
                    let btLoansWithSaving = 0;
                    existingRows.forEach((r) => {
                        const bt = btOffersByLoan[r.key];
                        if (!bt || bt.loading) return;
                        btLoansFetched += 1;
                        if (bt.best && bt.best.savings > 0) {
                            btSavingsPotential += bt.best.savings;
                            btLoansWithSaving += 1;
                        }
                    });
                    const allFetched = btLoansFetched === existingRows.length;

                    return (
                        <div
                            className="mt-3 border-round-lg border-1 p-3"
                            style={{
                                background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--surface-0) 100%)',
                                borderColor: 'var(--primary-200)',
                                borderLeftWidth: '4px',
                                borderLeftStyle: 'solid',
                                borderLeftColor: 'var(--primary-500)',
                            }}
                            aria-label="Portfolio totals"
                        >
                            {/* Top line: "TOTAL" tag + count + BT savings chip on right */}
                            <div className="flex align-items-center flex-wrap gap-2 mb-2">
                                <span
                                    className="inline-flex align-items-center border-round px-2 py-1 font-bold uppercase"
                                    style={{
                                        background: 'var(--primary-500)',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        letterSpacing: '.06em',
                                    }}
                                >
                                    <i className="pi pi-calculator mr-1" style={{ fontSize: '0.65rem' }} />
                                    Total
                                </span>
                                <span className="text-xs text-color-secondary">
                                    across {existingRows.length} {existingRows.length === 1 ? 'loan' : 'loans'}
                                </span>

                                {/* BT savings chip — pushed to the right */}
                                <div className="flex-1" />
                                {btLoansFetched === 0 ? (
                                    <span className="text-xs text-color-secondary flex align-items-center gap-1"
                                        style={{ fontStyle: 'italic' }}>
                                        <i className="pi pi-info-circle" style={{ fontSize: '0.65rem' }} />
                                        Click "Check BT" on loans to see total refinance savings
                                    </span>
                                ) : btLoansWithSaving === 0 ? (
                                    <span className="inline-flex align-items-center border-round px-2 py-1 gap-1"
                                        style={{
                                            background: 'var(--teal-50)',
                                            color: 'var(--teal-700)',
                                            border: '1px solid var(--teal-200)',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                        }}>
                                        <i className="pi pi-verified" style={{ fontSize: '0.7rem' }} />
                                        You have the best rates already
                                    </span>
                                ) : (
                                    <span className="inline-flex align-items-center border-round px-2 py-1 gap-1"
                                        style={{
                                            background: 'linear-gradient(135deg, var(--green-50) 0%, var(--green-100) 100%)',
                                            color: 'var(--green-700)',
                                            border: '1px solid var(--green-300)',
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            boxShadow: '0 1px 4px rgba(34, 197, 94, 0.2)',
                                        }}>
                                        <i className="pi pi-bolt" style={{ fontSize: '0.7rem' }} />
                                        Potential BT saving: {formatCurrency(btSavingsPotential)}
                                        {!allFetched && (
                                            <span className="ml-1 font-normal" style={{ opacity: 0.75, fontSize: '0.65rem' }}>
                                                (across {btLoansWithSaving} of {existingRows.length})
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>

                            {/* Bottom line: 4 aggregate stats, formatted like each
                                loan row but bolder (text-sm vs text-xs) and with
                                a stronger primary-color highlight on values. */}
                            <div className="flex align-items-center flex-wrap text-sm"
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    columnGap: '1.25rem',
                                    rowGap: '0.5rem',
                                }}>
                                <span>
                                    <span className="text-color-secondary text-xs uppercase font-semibold" style={{ letterSpacing: '.04em' }}>
                                        Outstanding
                                    </span>
                                    <b className="ml-2 text-primary-700" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(existingOutstanding)}
                                    </b>
                                </span>
                                <span className="text-300">·</span>
                                <span>
                                    <span className="text-color-secondary text-xs uppercase font-semibold" style={{ letterSpacing: '.04em' }}>
                                        EMI
                                    </span>
                                    <b className="ml-2 text-primary-700" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(existingEmi)}
                                    </b>
                                    <span className="text-color-secondary ml-1" style={{ fontSize: '0.7rem' }}>/mo</span>
                                </span>
                                {/* Combined FOIR — all loans' EMI / net salary.
                                    Uses the SAME 40%/50% thresholds the aggregate
                                    FOIR gauge uses (below 40 = green, 40-50 =
                                    orange, above 50 = red) so the value here
                                    matches the "FOIR" tile in Debt Burden below.
                                    Hidden if netSalary or EMI unavailable.       */}
                                {(() => {
                                    const net = Number(analysis?.netSalary) || 0;
                                    if (net <= 0 || existingEmi <= 0) return null;
                                    const totalFoir = (existingEmi / net) * 100;
                                    const color = totalFoir < 40 ? 'var(--green-700)'
                                                : totalFoir < 50 ? 'var(--orange-700)'
                                                : 'var(--red-700)';
                                    return (
                                        <>
                                            <span className="text-300">·</span>
                                            <span title={`Total EMI is ${totalFoir.toFixed(1)}% of monthly income (Fixed Obligation to Income Ratio)`}>
                                                <span className="text-color-secondary text-xs uppercase font-semibold" style={{ letterSpacing: '.04em' }}>
                                                    used
                                                </span>
                                                <b className="ml-2" style={{ fontSize: '1rem', color, fontVariantNumeric: 'tabular-nums' }}>
                                                    {totalFoir.toFixed(1)}%
                                                </b>
                                                <b className="ml-1"
                                                    style={{
                                                        color,
                                                        fontSize: '0.75rem',
                                                        letterSpacing: '.05em',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 700,
                                                    }}>
                                                    FOIR
                                                </b>
                                            </span>
                                        </>
                                    );
                                })()}
                                <span className="text-300">·</span>
                                <span>
                                    <span className="text-color-secondary text-xs uppercase font-semibold" style={{ letterSpacing: '.04em' }}>
                                        Longest Tenure
                                    </span>
                                    <b className="ml-2 text-primary-700" style={{ fontSize: '1rem' }}>
                                        {longestTenure > 0 ? `${longestTenure} mo` : '—'}
                                    </b>
                                </span>
                                <span className="text-300">·</span>
                                <span>
                                    <span className="text-color-secondary text-xs uppercase font-semibold" style={{ letterSpacing: '.04em' }}>
                                        Interest Left
                                    </span>
                                    <b className="ml-2 text-primary-700" style={{ fontSize: '1rem' }}>
                                        {totalInterestLeft > 0 ? formatCurrency(totalInterestLeft) : '—'}
                                    </b>
                                </span>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );

        // Inline the section card directly (instead of going through a
        // helper) so this whole section is self-contained — easier to read,
        // and avoids any stale-HMR risk if a shared helper changes shape.
        // Header has the icon + title + a right-edge "Optimise these" chip
        // (hidden when there are no loans).
        const title = 'All Loans Overview';
        const desc = hasOffer
            ? 'Existing loans + the new selected offer'
            : 'Complete inventory of active loans and credit lines';

        return (
            <section
                className="surface-card border-1 surface-border border-round-md shadow-1 mb-3 overflow-hidden"
                aria-label={title}
            >
                <div className="p-3 border-bottom-1 surface-border flex align-items-center gap-3">
                    <span
                        className="flex align-items-center justify-content-center border-round flex-shrink-0"
                        style={{ width: '2rem', height: '2rem', background: '#eef2ff', color: '#4338ca' }}
                        aria-hidden="true"
                    >
                        <i className="pi pi-list"></i>
                    </span>
                    <div className="flex-1 min-w-0">
                        <h4 className="m-0 text-sm font-bold text-900">{title}</h4>
                        <div className="text-xs text-color-secondary mt-1">{desc}</div>
                    </div>
                    {rows.length > 0 && (
                        <span
                            className="text-xs font-semibold flex align-items-center gap-1 cursor-pointer hover:text-primary flex-shrink-0"
                            style={{ color: 'var(--green-700)' }}
                        >
                            <i className="pi pi-bolt" style={{ fontSize: '0.65rem' }} />
                            Optimise these
                        </span>
                    )}
                </div>
                <div className="p-3">{body}</div>
            </section>
        );
    };

    // ── 1.2  Debt Burden Analysis ─────────────────────────────────────────
    //
    // When the user selects an offer, the primary "burden" view is the
    // post-disbursal picture (combined EMI / foirAfter / disposableAfter).
    // The pre-offer numbers are shown as a footnote so the delta is obvious.
    const renderDebtBurdenAnalysis = () => {
        const hasOffer = (analysis?.newLoanAmount || 0) > 0 || (analysis?.newEMI || 0) > 0;

        // Decide which set of numbers is the headline based on whether an
        // offer is present. Pre-offer view → "today"; post-offer view → "after".
        const headlineEmi = hasOffer ? (analysis?.combinedEMI || 0) : (analysis?.currentTotalEMI || 0);
        const headlineFoir = hasOffer ? (analysis?.foirAfter || 0) : (analysis?.foirBefore || 0);
        const headlineDisposable = hasOffer ? (analysis?.disposableAfter || 0) : (analysis?.disposableBefore || 0);
        const headlineEmiFootnote = hasOffer
            ? `${formatPercentage(headlineFoir)} of net income · was ${formatCurrency(analysis?.currentTotalEMI || 0)}`
            : `${formatPercentage(headlineFoir)} of net income`;
        const headlineDisposableFootnote = hasOffer
            ? `was ${formatCurrency(analysis?.disposableBefore || 0)} before new loan`
            : 'Available after existing EMIs';

        // Weighted-average rate including new loan when present
        const computeBlendedRate = () => {
            const items = [];
            (existingLoans || []).forEach((l) => {
                items.push({
                    rate: parseFloat(l.interest_rate) || 0,
                    weight: parseFloat(l.outstanding_loan || l.outstanding_amount) || 0,
                });
            });
            if (hasOffer) {
                items.push({
                    rate: analysis?.newLoanRate || 0,
                    weight: analysis?.newLoanAmount || 0,
                });
            }
            const totalWeight = items.reduce((s, i) => s + i.weight, 0);
            if (totalWeight === 0) return 0;
            return items.reduce((s, i) => s + i.rate * i.weight, 0) / totalWeight;
        };
        const avgRate = hasOffer ? computeBlendedRate() : (analysis?.weightedAverageRate || analysis?.avgInterestRate || 0);

        const dti = analysis?.debtToIncomeRatio != null ? analysis.debtToIncomeRatio.toFixed(1) : '—';
        const burdenColor = headlineFoir >= 50 ? 'var(--red-500)' : headlineFoir >= 40 ? 'var(--orange-500)' : 'var(--green-500)';

        // NOTE: The section header now lives outside the card as a `renderSubgroup`
        // above the call site (renderTab1), matching the "Today's Position" style.
        // We render only the card body here — no icon/title/desc inside.
        return (
            <section
                className="surface-card border-1 surface-border border-round-md shadow-1 mb-3 overflow-hidden"
                aria-label="Debt Burden Analysis"
            >
                <div className="p-3">
                {/* 6 metric tiles — 2×3 on md, 3×2 on lg. Order tells a
                    story: income → what's committed → what's left → what
                    lenders check → macro ratio → cost of debt. */}
                <div className="grid">
                    {/* 1. Net Income — the base */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: 'Net Income',
                            value: formatCurrency(analysis?.netSalary || 0),
                            footnote: 'Take-home per month',
                        })}
                    </div>
                    {/* 2. Monthly EMI — what's committed */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: hasOffer ? 'Monthly EMI (after)' : 'Monthly EMI',
                            value: formatCurrency(headlineEmi),
                            footnote: headlineEmiFootnote,
                            tone: hasOffer ? 'negative' : undefined,
                        })}
                    </div>
                    {/* 3. Disposable — what's left */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: hasOffer ? 'Disposable (after)' : 'Disposable Income',
                            value: formatCurrency(headlineDisposable),
                            footnote: headlineDisposableFootnote,
                            tone: 'positive',
                        })}
                    </div>
                    {/* 4. FOIR — the ratio lenders check */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: hasOffer ? 'FOIR (after)' : 'FOIR',
                            value: formatPercentage(headlineFoir),
                            footnote: headlineFoir >= 50 ? 'Above safe 50% limit' : headlineFoir >= 40 ? 'Approaching limit' : 'Within safe zone',
                            tone: headlineFoir >= 50 ? 'negative' : headlineFoir >= 40 ? undefined : 'positive',
                        })}
                    </div>
                    {/* 5. Debt-to-Income — macro ratio */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: 'Debt-to-Income',
                            value: `${dti}x`,
                            footnote: 'Annual income vs total debt',
                        })}
                    </div>
                    {/* 6. Avg Interest — cost of debt */}
                    <div className="col-6 md:col-4 lg:col-2">
                        {renderMetric({
                            label: 'Avg Interest',
                            value: formatPercentage(avgRate),
                            footnote: hasOffer ? 'Weighted incl. new loan' : 'Weighted across loans',
                        })}
                    </div>
                </div>

                <div className="surface-50 p-3 border-round-md mt-3">
                    <div className="flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <span className="text-sm font-semibold text-900">
                            {hasOffer ? 'EMI as % of income (after new loan)' : 'EMI as % of monthly income'}
                        </span>
                        <span className="font-bold" style={{ color: burdenColor }}>{formatPercentage(headlineFoir)}</span>
                    </div>
                    <ProgressBar value={Math.min(headlineFoir, 100)} showValue={false} style={{ height: '6px' }} color={burdenColor} />
                    <div className="text-xs text-400 mt-2">
                        Healthy zone is below 40%. {headlineFoir >= 40 ? 'Customer is approaching or exceeding the upper edge of safety.' : 'Customer is well within safe limits.'}
                        {hasOffer && (
                            <span> Before this loan: <strong>{formatPercentage(analysis?.foirBefore || 0)}</strong>.</span>
                        )}
                    </div>
                </div>
                </div>
            </section>
        );
    };

    // ── 1.3  FOIR Utilization ─────────────────────────────────────────────
    //
    // The gauge needle reflects the headline FOIR. With no offer selected,
    // headline = foirBefore. With an offer, headline = foirAfter and the
    // "before" value is shown alongside so the delta is explicit.
    const renderFoirUtilization = () => {
        const hasOffer = (analysis?.newLoanAmount || 0) > 0 || (analysis?.newEMI || 0) > 0;
        const foirBefore = analysis?.foirBefore || 0;
        const foirAfter = analysis?.foirAfter || 0;
        const foir = hasOffer ? foirAfter : foirBefore;
        const safeLimit = 50;
        const headroomPct = Math.max(safeLimit - foir, 0);
        const headroomAmount = ((analysis?.netSalary || 0) * headroomPct) / 100;
        const inSafeZone = foir < safeLimit;

        // Gauge — SVG circular progress
        const radius = 60;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference * (1 - Math.min(foir, 100) / 100);
        const gaugeColor = foir >= 50 ? 'var(--red-500)' : foir >= 40 ? 'var(--orange-500)' : 'var(--green-500)';

        return renderComponentCard(
            'pi-gauge',
            '#ecfdf5',
            '#047857',
            'FOIR Utilization',
            hasOffer
                ? 'Post-loan Fixed Obligation to Income Ratio — what it becomes after disbursal'
                : 'Fixed Obligation to Income Ratio — current commitment level',
            <div className="flex flex-wrap align-items-center" style={{ gap: '1.75rem' }}>
                <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
                    <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="70" cy="70" r={radius} stroke="var(--surface-200)" strokeWidth="11" fill="none" />
                        <circle
                            cx="70"
                            cy="70"
                            r={radius}
                            stroke={gaugeColor}
                            strokeWidth="11"
                            fill="none"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset .3s ease' }}
                        />
                    </svg>
                    <div
                        className="flex flex-column align-items-center justify-content-center"
                        style={{ position: 'absolute', inset: 0 }}
                    >
                        <div className="text-xl font-bold line-height-1 text-900" style={{ letterSpacing: '-.015em' }}>
                            {formatPercentage(foir)}
                        </div>
                        <div className="text-xs uppercase font-semibold text-color-secondary mt-1" style={{ letterSpacing: '.06em' }}>
                            {hasOffer ? 'FOIR After' : 'Current FOIR'}
                        </div>
                    </div>
                </div>

                <div className="flex-1" style={{ minWidth: '260px' }}>
                    {hasOffer && (
                        <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                            <span className="text-sm text-color-secondary">FOIR before new loan</span>
                            <span className="font-bold text-900">
                                {formatPercentage(foirBefore)}
                                <span className="text-xs text-color-secondary ml-2">
                                    (+{(foirAfter - foirBefore).toFixed(1)} pts)
                                </span>
                            </span>
                        </div>
                    )}
                    <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                        <span className="text-sm text-color-secondary">Safe limit (industry standard)</span>
                        <span className="font-bold text-900">{formatPercentage(safeLimit)}</span>
                    </div>
                    <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                        <span className="text-sm text-color-secondary">Headroom available</span>
                        <span className="font-bold" style={{ color: inSafeZone ? 'var(--green-700)' : 'var(--red-700)' }}>
                            {formatPercentage(headroomPct)} ({formatCurrency(headroomAmount)}/mo)
                        </span>
                    </div>
                    <div className="flex justify-content-between align-items-center py-2">
                        <span className="text-sm text-color-secondary">Status</span>
                        {inSafeZone ? (
                            <Tag value="Within safe zone" severity="success" icon="pi pi-check" />
                        ) : (
                            <Tag value="Above safe limit" severity="warning" icon="pi pi-exclamation-triangle" />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 1.4  Existing Loan EMI Calculator
    //
    // Derived from: existingLoans (the picker loads each loan's principal,
    // rate, remaining tenure). EMI / total interest / total payment are
    // computed live from the slider values.
    // ─────────────────────────────────────────────────────────────────────
    const renderExistingLoanCalculator = () => {
        const loans = existingLoans || [];
        const monthlyIncome = analysis?.netSalary || 0;

        // ── Baseline (no prepayment) ─────────────────────────────────────
        // EMI formula: P * r * (1+r)^n / ((1+r)^n - 1), r = annual rate / 12 / 100
        const monthlyRate = (calcRate || 0) / 12 / 100;
        const n = calcTenure || 0;
        const p = calcPrincipal || 0;
        let emi = 0;
        if (p > 0 && n > 0) {
            emi = monthlyRate === 0
                ? p / n
                : (p * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
        }
        const totalPayment = emi * n;
        const totalInterest = totalPayment - p;
        const emiPctOfIncome = monthlyIncome > 0 ? (emi / monthlyIncome) * 100 : 0;

        // ── Prepayment scenario ──────────────────────────────────────────
        // Given a lump-sum L (paid today) OR a fixed extra E on every EMI,
        // compute the new (shorter) tenure keeping the current EMI or
        // the boosted EMI respectively.
        //
        //   Lump-sum: principal shrinks to (P - L). New tenure n' derived
        //     from same EMI, same rate. Solve for n' via log formula:
        //       n' = -ln(1 - (P' * r / EMI)) / ln(1 + r)
        //   Extra monthly: EMI grows to (EMI + E), principal stays P. Solve
        //     for n' the same way with the boosted EMI.
        //
        // Edge cases: if EMI × months ≤ interest, formula diverges → cap at n.
        const solveTenure = (principal, emiPayment, monthlyR) => {
            if (principal <= 0 || emiPayment <= 0) return 0;
            if (monthlyR === 0) return principal / emiPayment;
            const ratio = (principal * monthlyR) / emiPayment;
            if (ratio >= 1) return n; // EMI too small to ever repay — fall back to baseline
            return -Math.log(1 - ratio) / Math.log(1 + monthlyR);
        };

        const prepayLump  = prepayMode === 'lumpsum' ? Math.min(prepayAmount, p * 0.9) : 0;
        const prepayExtra = prepayMode === 'extra'   ? prepayAmount : 0;
        const newPrincipal = Math.max(p - prepayLump, 0);
        const newEmi       = emi + prepayExtra;
        const newTenureN   = solveTenure(newPrincipal, newEmi, monthlyRate);
        const newTotalPayment  = (newEmi * newTenureN) + prepayLump;
        const newTotalInterest = Math.max(newTotalPayment - p, 0);
        const monthsSaved   = Math.max(n - newTenureN, 0);
        const interestSaved = Math.max(totalInterest - newTotalInterest, 0);

        // Payoff-date formatting
        const closeMonth = (monthsAhead) => {
            const d = new Date();
            d.setMonth(d.getMonth() + Math.round(monthsAhead));
            return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
        };
        const baselineCloseDate = closeMonth(n);
        const newCloseDate      = closeMonth(newTenureN);
        const hasPrepayment     = prepayAmount > 0;

        // Loan picker options — split loanType + lender so the custom
        // pill picker below can render them on two lines.
        const pickerOptions = loans.map((loan, idx) => {
            const loanType = loan.loan_type || loan.type || 'Loan';
            const lender   = loan.lender_name || loan.lender || '';
            return { value: idx, loanType, lender };
        });

        if (loans.length === 0) {
            return renderComponentCard(
                'pi-calculator',
                '#f5f9ff',
                '#1d4ed8',
                'Existing Loan EMI Calculator',
                'Recompute EMI for any existing loan — useful for prepayment / restructuring scenarios',
                <div className="text-center text-color-secondary py-4">
                    <i className="pi pi-inbox pi-2x mb-2 block text-400" aria-hidden="true"></i>
                    <div>No existing loans to recalculate. Add loans in the Existing Loans step.</div>
                </div>
            );
        }

        // ── Small payoff timeline curves ──────────────────────────────
        // Sample outstanding balance every K months so we always draw
        // 20–30 points regardless of tenure length.
        //   Baseline (grey dashed): principal P, EMI, tenure n
        //   With-prepay (green):    principal (P − lumpsum), EMI+extra
        const buildCurve = (startPrincipal, monthlyR, monthlyEMI, maxMonths) => {
            const pts = [];
            if (startPrincipal <= 0 || maxMonths <= 0) return pts;
            const steps = Math.min(30, Math.max(15, Math.ceil(maxMonths)));
            const dm    = maxMonths / steps;
            for (let i = 0; i <= steps; i++) {
                const m = i * dm;
                let bal;
                if (monthlyR === 0) {
                    bal = startPrincipal - monthlyEMI * m;
                } else {
                    const factor = Math.pow(1 + monthlyR, m);
                    bal = startPrincipal * factor - monthlyEMI * ((factor - 1) / monthlyR);
                }
                pts.push({ m, bal: Math.max(bal, 0) });
                if (bal <= 0) break;
            }
            return pts;
        };
        const baselineCurve = buildCurve(p, monthlyRate, emi, n);
        const prepayCurve   = buildCurve(newPrincipal, monthlyRate, newEmi, Math.ceil(newTenureN));

        // Chart viewport — small size (fits inside the left column)
        const CH_W = 320, CH_H = 140, PAD_L = 8, PAD_R = 8, PAD_T = 14, PAD_B = 22;
        const chartMaxMonths = Math.max(n, 1);
        const chartMaxBal    = Math.max(p, 1);
        const xFor = (m)   => PAD_L + (m / chartMaxMonths) * (CH_W - PAD_L - PAD_R);
        const yFor = (bal) => PAD_T + (1 - bal / chartMaxBal) * (CH_H - PAD_T - PAD_B);
        const toPoly = (pts) => pts.map((pt) => `${xFor(pt.m).toFixed(1)},${yFor(pt.bal).toFixed(1)}`).join(' ');
        const baselinePolyline = toPoly(baselineCurve);
        const prepayPolyline   = toPoly(prepayCurve);

        return renderComponentCard(
            'pi-calculator',
            '#f5f9ff',
            '#1d4ed8',
            'Existing Loan EMI Calculator',
            'Recompute EMI for any existing loan — useful for prepayment / restructuring scenarios',
            <>
                {/* Loan picker — TabMenu-style pills, one row on desktop,
                    horizontal-scroll on mobile so lenders never truncate. */}
                <div className="mb-3 flex flex-wrap gap-2">
                    {pickerOptions.map((opt) => {
                        const isActive = calcSelectedLoanIdx === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setCalcSelectedLoanIdx(opt.value)}
                                aria-pressed={isActive}
                                className="border-round-md p-2 px-3 cursor-pointer"
                                style={{
                                    border: isActive
                                        ? '1px solid var(--primary-color)'
                                        : '1px solid var(--surface-border)',
                                    background: isActive
                                        ? 'var(--primary-color)'
                                        : 'var(--surface-card)',
                                    color: isActive ? '#fff' : 'var(--text-color)',
                                    fontSize: '.85rem',
                                    fontWeight: 600,
                                    transition: 'background .15s, border-color .15s, color .15s',
                                    minWidth: 0,
                                }}
                            >
                                <div className="flex flex-column align-items-start" style={{ lineHeight: 1.2 }}>
                                    <span>{opt.loanType}</span>
                                    <span
                                        className="text-xs mt-1"
                                        style={{ opacity: isActive ? 0.9 : 0.7, fontWeight: 500 }}
                                    >
                                        {opt.lender}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* ── Prepayment simulator (left) + comparison (right) ── */}
                <div className="grid">
                    {/* Left: mode toggle + slider + savings chip */}
                    <div className="col-12 md:col-5">
                        {/* Mode toggle */}
                        <div className="flex gap-2 mb-2">
                            {[
                                { key: 'lumpsum', label: 'One-time payment', icon: 'pi-bolt' },
                                { key: 'extra',   label: 'Extra monthly',    icon: 'pi-forward' },
                            ].map((opt) => {
                                const active = prepayMode === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => { setPrepayMode(opt.key); setPrepayAmount(0); }}
                                        aria-pressed={active}
                                        className="flex-1 border-round-md px-2 py-2 cursor-pointer flex align-items-center justify-content-center gap-2"
                                        style={{
                                            border: active ? '1px solid var(--primary-color)' : '1px solid var(--surface-border)',
                                            background: active ? 'var(--primary-color)' : 'var(--surface-card)',
                                            color: active ? '#fff' : 'var(--text-color)',
                                            fontSize: '.8rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        <i className={`pi ${opt.icon}`} aria-hidden="true"></i>
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Prepayment slider */}
                        <div className="surface-50 border-1 surface-border border-round-md p-3 h-full">
                            <div className="flex justify-content-between align-items-baseline mb-2">
                                <label className="text-sm font-medium text-700">
                                    {prepayMode === 'lumpsum' ? 'Lump-sum payment today' : 'Extra ₹ on every EMI'}
                                </label>
                                <span className="text-sm font-bold text-primary">
                                    {formatCurrency(prepayAmount)}
                                </span>
                            </div>
                            <Slider
                                value={prepayAmount}
                                onChange={(e) => setPrepayAmount(e.value)}
                                min={0}
                                max={prepayMode === 'lumpsum'
                                    ? Math.max(Math.floor(p * 0.9), 10000)
                                    : Math.max(Math.floor(emi * 3), 5000)
                                }
                                step={prepayMode === 'lumpsum' ? 5000 : 500}
                            />

                            {hasPrepayment ? (
                                <div className="mt-3 p-2 border-round-md flex align-items-center gap-2" style={{ background: 'var(--green-50, #f0fdf4)' }}>
                                    <i className="pi pi-check-circle text-green-600" aria-hidden="true"></i>
                                    <span className="text-xs text-green-700 font-medium">
                                        Save <b>{formatCurrency(interestSaved)}</b> · close <b>{Math.round(monthsSaved)} months earlier</b> ({newCloseDate})
                                    </span>
                                </div>
                            ) : (
                                <div className="mt-3 text-xs text-500">
                                    <i className="pi pi-info-circle mr-1" aria-hidden="true"></i>
                                    Drag the slider to see how much interest you save and how many months earlier you close.
                                </div>
                            )}

                            {/* Mini payoff chart — updates live with slider */}
                            <div className="mt-3 pt-3 border-top-1 surface-border">
                                <div className="flex align-items-center justify-content-between mb-1">
                                    <span className="text-xs text-600 font-semibold">Payoff timeline</span>
                                    <div className="flex align-items-center gap-2 text-xs">
                                        <span className="flex align-items-center gap-1 text-500">
                                            <span aria-hidden="true" style={{ width: 12, height: 0, borderTop: '2px dashed var(--surface-500, #94a3b8)', display: 'inline-block' }}></span>
                                            Now
                                        </span>
                                        <span className="flex align-items-center gap-1 text-green-700 font-semibold">
                                            <span aria-hidden="true" style={{ width: 12, height: 2, background: 'var(--green-500)', display: 'inline-block' }}></span>
                                            With prepay
                                        </span>
                                    </div>
                                </div>
                                <svg
                                    viewBox={`0 0 ${CH_W} ${CH_H}`}
                                    preserveAspectRatio="xMidYMid meet"
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                    role="img"
                                    aria-label="Payoff timeline chart"
                                >
                                    {/* Faint gridlines */}
                                    {[0, 0.5, 1].map((k) => {
                                        const y = PAD_T + k * (CH_H - PAD_T - PAD_B);
                                        return (
                                            <line
                                                key={k}
                                                x1={PAD_L} y1={y} x2={CH_W - PAD_R} y2={y}
                                                stroke="var(--surface-border)"
                                                strokeWidth="1"
                                                strokeDasharray={k === 1 ? undefined : '2 3'}
                                            />
                                        );
                                    })}

                                    {/* Baseline curve (grey dashed) */}
                                    {baselinePolyline && (
                                        <polyline
                                            points={baselinePolyline}
                                            fill="none"
                                            stroke="var(--surface-500, #94a3b8)"
                                            strokeWidth="1.5"
                                            strokeDasharray="4 3"
                                        />
                                    )}
                                    {/* Baseline payoff marker */}
                                    <circle cx={xFor(n)} cy={yFor(0)} r="3" fill="var(--surface-500, #94a3b8)" />

                                    {/* With-prepay curve (green solid) */}
                                    {prepayPolyline && (
                                        <polyline
                                            points={prepayPolyline}
                                            fill="none"
                                            stroke="var(--green-500)"
                                            strokeWidth="2"
                                            strokeLinejoin="round"
                                            strokeLinecap="round"
                                        />
                                    )}
                                    {/* With-prepay payoff marker + label */}
                                    {hasPrepayment && (
                                        <>
                                            <circle cx={xFor(newTenureN)} cy={yFor(0)} r="4" fill="var(--green-500)" />
                                            <text
                                                x={xFor(newTenureN)} y={yFor(0) - 6}
                                                fontSize="9" fill="var(--green-700)" textAnchor="middle" fontWeight="700"
                                            >
                                                {newCloseDate}
                                            </text>
                                        </>
                                    )}

                                    {/* X-axis: Now → Payoff */}
                                    <text
                                        x={PAD_L + 2} y={CH_H - 6}
                                        fontSize="9" fill="var(--text-color-secondary)" textAnchor="start"
                                    >
                                        Now
                                    </text>
                                    <text
                                        x={xFor(n)} y={CH_H - 6}
                                        fontSize="9" fill="var(--text-color-secondary)" textAnchor="end"
                                    >
                                        {baselineCloseDate}
                                    </text>
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Right: NOW vs WITH-PREPAY comparison table */}
                    <div className="col-12 md:col-7">
                        <div
                            className="border-1 surface-border border-round-md p-3 surface-card h-full"
                            style={{ borderLeftWidth: '4px', borderLeftColor: 'var(--primary-color)' }}
                        >
                            <div className="grid text-xs uppercase font-semibold text-500 mb-2" style={{ letterSpacing: '.05em' }}>
                                <div className="col-6"></div>
                                <div className="col-3 text-right">Now</div>
                                <div className="col-3 text-right text-primary">With prepay</div>
                            </div>

                            {[
                                { label: 'Monthly EMI',      base: formatCurrency(emi),                new: formatCurrency(newEmi) },
                                { label: 'Total Interest',   base: formatCurrency(totalInterest),      new: formatCurrency(newTotalInterest), tone: 'good' },
                                { label: 'Total Payable',    base: formatCurrency(totalPayment),       new: formatCurrency(newTotalPayment),  tone: 'good' },
                                { label: 'Tenure',           base: `${n} mo`,                          new: `${Math.round(newTenureN)} mo`,   tone: 'good' },
                                { label: 'Payoff Date',      base: baselineCloseDate,                  new: newCloseDate,                     tone: 'good' },
                            ].map((row, idx, arr) => (
                                <div
                                    key={row.label}
                                    className={`grid align-items-center py-2 ${idx === arr.length - 1 ? '' : 'border-bottom-1 surface-border'}`}
                                >
                                    <div className="col-6 text-sm text-600">{row.label}</div>
                                    <div className="col-3 text-right text-sm text-500" style={{ textDecoration: hasPrepayment ? 'line-through' : 'none' }}>
                                        {row.base}
                                    </div>
                                    <div className={`col-3 text-right text-sm font-bold ${hasPrepayment && row.tone === 'good' ? 'text-green-700' : 'text-900'}`}>
                                        {row.new}
                                    </div>
                                </div>
                            ))}

                            <div className="mt-3 pt-2 border-top-1 surface-border flex justify-content-between align-items-center">
                                <span className="text-xs text-600">EMI as % of income</span>
                                <span className={`text-sm font-bold ${emiPctOfIncome > 30 ? 'text-orange-600' : 'text-green-700'}`}>
                                    {formatPercentage(emiPctOfIncome)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 1.5  Existing Loan Eligibility for New Loan Offer
    //
    // Derived from: analysis + eligibilityResult + selectedOffer via
    // calculateEligibilityUtilization(). The breakdown table shows how each
    // existing loan reduces the customer's max new-loan capacity.
    // ─────────────────────────────────────────────────────────────────────
    const renderExistingLoanEligibility = () => {
        const util = calculateEligibilityUtilization(analysis, eligibilityResult, selectedOffer) || {};
        const maxEligible = util.maxEligible || selectedOffer?.max_loan_eligibility || 0;
        const requested = util.requestedAmount || analysis?.newLoanAmount || 0;
        const remaining = util.remainingCapacity || Math.max(maxEligible - requested, 0);
        const utilizationPct = maxEligible > 0 ? Math.min((requested / maxEligible) * 100, 100) : 0;

        // Per-loan eligibility reduction estimate.
        // Derived from: each existing loan's EMI × ~70 (rough FOIR multiplier)
        // — gives the agent a directional "this loan eats X of new eligibility" view.
        const monthlyIncome = analysis?.netSalary || 0;
        const eligibilityMultiplier = monthlyIncome > 0 ? maxEligible / Math.max(monthlyIncome, 1) : 60;
        const breakdown = (existingLoans || []).map((loan) => {
            const emi = parseFloat(loan.emi || loan.emi_amount || 0);
            const reducesBy = emi * eligibilityMultiplier;
            const loanType = loan.loan_type || loan.type || 'Loan';
            const lender = loan.lender_name || loan.lender || '—';
            return {
                key: loan.id || `${loanType}-${lender}`,
                loan: `${loanType} · ${lender}`,
                emi,
                reducesBy,
                reason: `₹${Math.round(emi).toLocaleString('en-IN')} EMI consumes FOIR headroom worth about ${formatCurrency(reducesBy)} of new borrowing`,
            };
        });

        // Gauge — % of max eligibility already being used by the requested amount
        const radius = 60;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference * (1 - utilizationPct / 100);

        return renderComponentCard(
            'pi-check-circle',
            '#ecfeff',
            '#0e7490',
            'Existing Loan Eligibility for New Loan Offer',
            "Maximum new loan amount given the customer's current obligations",
            <>
                <div className="flex flex-wrap align-items-center" style={{ gap: '1.75rem' }}>
                    <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
                        <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                            <circle cx="70" cy="70" r={radius} stroke="var(--surface-200)" strokeWidth="11" fill="none" />
                            <circle
                                cx="70"
                                cy="70"
                                r={radius}
                                stroke="var(--cyan-500, #06b6d4)"
                                strokeWidth="11"
                                fill="none"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                strokeLinecap="round"
                                style={{ transition: 'stroke-dashoffset .3s ease' }}
                            />
                        </svg>
                        <div className="flex flex-column align-items-center justify-content-center" style={{ position: 'absolute', inset: 0 }}>
                            <div className="text-xl font-bold line-height-1 text-900" style={{ letterSpacing: '-.015em' }}>
                                {Math.round(utilizationPct)}%
                            </div>
                            <div className="text-xs uppercase font-semibold text-color-secondary mt-1" style={{ letterSpacing: '.06em' }}>
                                utilized
                            </div>
                        </div>
                    </div>

                    <div className="flex-1" style={{ minWidth: '260px' }}>
                        <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                            <span className="text-sm text-color-secondary">Maximum eligibility (this product)</span>
                            <span className="font-bold text-900">{formatCurrency(maxEligible)}</span>
                        </div>
                        <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                            <span className="text-sm text-color-secondary">Requested amount</span>
                            <span className="font-bold" style={{ color: 'var(--primary-color)' }}>{formatCurrency(requested)}</span>
                        </div>
                        <div className="flex justify-content-between align-items-center py-2">
                            <span className="text-sm text-color-secondary">Remaining headroom</span>
                            <span className="font-bold" style={{ color: 'var(--green-700)' }}>{formatCurrency(remaining)}</span>
                        </div>
                    </div>
                </div>

                {breakdown.length > 0 && (
                    <div className="mt-3">
                        <DataTable value={breakdown} dataKey="key" size="small" stripedRows responsiveLayout="scroll">
                            <Column field="loan" header="Existing Loan" />
                            <Column
                                header="Reduces Eligibility By"
                                body={(r) => <span style={{ color: 'var(--red-700)', fontWeight: 600 }}>−{formatCurrency(r.reducesBy)}</span>}
                                alignHeader="right"
                                bodyClassName="text-right"
                            />
                            <Column field="reason" header="Why" />
                        </DataTable>
                    </div>
                )}
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 1.6  Loan Repayment Priority (to increase eligibility for THIS new loan)
    //
    // Derived from: calculateLoanRepaymentPriority(existingLoans, analysis).
    // Ranks loans by which one frees up the most new-loan capacity per rupee
    // repaid (revolving credit first, then highest-rate, then lowest-rate).
    // ─────────────────────────────────────────────────────────────────────
    const renderRepaymentPriorityForThisLoan = () => {
        // calculateLoanRepaymentPriority returns { loans: [...], newLoan, ... }
        // Each loan has: id, loanType, lender, outstanding, emi, interestRate,
        // priorityRank, priorityLabel, priorityColor, recommendation, etc.
        const priorityResult = calculateLoanRepaymentPriority(existingLoans, analysis) || {};
        const priorityLoans = Array.isArray(priorityResult.loans) ? priorityResult.loans : [];

        const monthlyIncome = analysis?.netSalary || 0;
        const eligibilityMultiplier = monthlyIncome > 0
            ? (selectedOffer?.max_loan_eligibility || 0) / Math.max(monthlyIncome, 1)
            : 60;

        if (priorityLoans.length === 0) {
            return renderComponentCard(
                'pi-sort-amount-down',
                '#fef2f2',
                '#b91c1c',
                'Loan Repayment Priority',
                'Order in which to clear existing debts to increase eligibility for this new loan',
                <div className="text-center text-color-secondary py-4">
                    <i className="pi pi-inbox text-3xl mb-2 block text-400"></i>
                    <div>No existing loans to prioritise.</div>
                </div>
            );
        }

        return renderComponentCard(
            'pi-sort-amount-down',
            '#fef2f2',
            '#b91c1c',
            'Loan Repayment Priority',
            <>Order to clear existing debts to <strong>unlock more eligibility</strong></>,
            <div>
                {/* B — Honest-math caption so the DSA knows the number is a rough estimate */}
                <div
                    className="flex align-items-center gap-2 mb-2 p-2 border-round-md"
                    style={{ background: 'var(--surface-50)' }}
                >
                    <i className="pi pi-info-circle text-500" aria-hidden="true" />
                    <span className="text-xs text-500 line-height-3">
                        Rough estimate: freed EMI × loan-per-salary multiplier.
                        Actual gain depends on lender re-underwriting.
                    </span>
                </div>

                {priorityLoans.map((item, idx) => {
                    const emi = parseFloat(item.emi || 0);
                    const newEligibilityGain = emi * eligibilityMultiplier;
                    const reason = item.recommendation
                        || `Frees ~${formatCurrency(newEligibilityGain)} of new-loan capacity`;

                    return (
                        <div
                            key={item.id != null ? `pri-${item.id}` : `pri-${idx}`}
                            className="flex align-items-center gap-3 py-2"
                            style={{
                                borderBottom: idx < priorityLoans.length - 1 ? '1px solid var(--surface-border)' : 'none',
                            }}
                        >
                            {/* Small rank badge */}
                            <div
                                className="flex align-items-center justify-content-center border-circle text-white flex-shrink-0"
                                aria-label={`Priority rank ${item.priorityRank || idx + 1}`}
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                    background: item.priorityColor || '#64748b',
                                    fontSize: '.75rem',
                                    fontWeight: 700,
                                }}
                            >
                                {item.priorityRank || idx + 1}
                            </div>
                            {/* Loan info — name + reason on ONE line each, smaller sizes */}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {item.loanType || 'Loan'} · {item.lender || '—'}
                                </div>
                                <div className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {reason}
                                </div>
                            </div>
                            {/* C — Honest label: "est. extra capacity" instead of "new eligibility" */}
                            <div className="text-right flex-shrink-0">
                                <div className="text-sm font-bold text-green-700">
                                    + {formatCurrency(newEligibilityGain)}
                                </div>
                                <div className="text-xs text-500">est. extra capacity</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ═════════════════════════════════════════════════════════════════════
    //  TAB 2 — Other Loan Eligibility After New Loan
    //
    // All numbers derive from `analysis` (which itself derives from
    // existingLoans + selectedOffer + eligibilityResult). Per-loan-type
    // eligibility uses the industry-standard FOIR-headroom formula:
    //
    //   emi_capacity_for_type = (income × foir_limit) − combinedEMI
    //   eligible_amount       = emi_capacity × tenure_multiplier_for_type
    //
    // tenure_multiplier_for_type is an inverted EMI factor — i.e. how many
    // rupees of principal each rupee of monthly EMI buys, given that loan
    // type's typical ROI and tenure. These are real industry constants for
    // each loan category.
    // ═════════════════════════════════════════════════════════════════════

    // Per-loan-type configuration. Numbers reflect industry norms (FOIR
    // limits, indicative ROI, tenures, CIBIL gates) used by Indian lenders.
    const LOAN_TYPE_CONFIG = {
        home: {
            label: 'Home Loan',
            icon: 'pi pi-home',
            foirLimit: 50,                  // Home loans allow higher FOIR than PL
            cibilRequired: 750,
            roiRange: '8.5% – 9.25%',
            roiForEmi: 8.5,
            tenureMonths: 240,              // 20 years typical
            displayTenure: '20 years',
            taxBenefit: '₹3.5 L/yr',
            taxNote: 'Sec 24 + 80C combined',
            note: 'Wait 12–18 months after the new PL disburses before applying. Mid-tenure on the PL improves FOIR.',
            noteTone: 'info',
        },
        car: {
            label: 'Car Loan',
            icon: 'pi pi-car',
            foirLimit: 60,
            cibilRequired: 720,
            roiRange: '9.0% – 10.5%',
            roiForEmi: 9.5,
            tenureMonths: 84,               // 7 years max for car loans
            displayTenure: '7 years',
            taxBenefit: 'None',
            taxNote: 'Applies if self-employed',
            note: 'Taking both the new PL and a Car Loan in the same quarter pushes FOIR too high. Delay 12+ months or use the PL for the vehicle itself.',
            noteTone: 'warning',
        },
        business: {
            label: 'Business Loan',
            icon: 'pi pi-briefcase',
            foirLimit: 50,
            cibilRequired: 750,
            roiRange: '16% – 22%',
            roiForEmi: 18,
            tenureMonths: 36,               // 3 years
            displayTenure: '3 years',
            taxBenefit: 'Yes',
            taxNote: 'Business expense deduction',
            note: 'For starting a business, an MUDRA loan (up to ₹10L at 8.5–12%) is cheaper with government backing. Requires sole proprietorship registration.',
            noteTone: 'info',
        },
        education: {
            label: 'Education Loan',
            icon: 'pi pi-book',
            foirLimit: 55,
            cibilRequired: 700,
            roiRange: '9.5% – 11%',
            roiForEmi: 10,
            tenureMonths: 120,              // 10 years post-moratorium
            displayTenure: '10 years',
            taxBenefit: '100%',
            taxNote: 'Sec 80E — full interest deductible',
            note: 'Course duration + 6 months moratorium before EMIs begin. Subsidized rates for premier institutes.',
            noteTone: 'info',
        },
        gold: {
            label: 'Gold Loan',
            icon: 'pi pi-star',
            foirLimit: 100,                  // No FOIR check — collateral-backed
            cibilRequired: 0,
            roiRange: '7.5% – 11%',
            roiForEmi: 9,
            tenureMonths: 12,                // Short term
            displayTenure: '1 year typical',
            taxBenefit: 'None',
            taxNote: 'Pure collateral product',
            note: 'No CIBIL check, same-day disbursal. Best for emergency liquidity — cheaper than PL, no impact on credit score.',
            noteTone: 'success',
            isCollateralBacked: true,
        },
    };

    // EMI factor — given monthly_rate and tenure, gives principal per unit EMI.
    // Inverse of the standard EMI formula. Used to convert FOIR headroom →
    // maximum eligible loan amount.
    const calcPrincipalFromEmi = (emi, annualRate, tenureMonths) => {
        if (emi <= 0 || tenureMonths <= 0) return 0;
        const r = annualRate / 12 / 100;
        if (r === 0) return emi * tenureMonths;
        const pow = Math.pow(1 + r, tenureMonths);
        return (emi * (pow - 1)) / (r * pow);
    };

    // Compute eligibility for one loan type, after the new PL is disbursed.
    const computeLoanTypeEligibility = (cfg) => {
        const income = analysis?.netSalary || 0;
        const combinedEmi = analysis?.combinedEMI || 0;
        // EMI capacity left for this loan type, given its FOIR cap
        const emiCapacity = Math.max(0, (income * cfg.foirLimit / 100) - combinedEmi);
        // Convert that EMI capacity → loan principal using the type's ROI + tenure
        const eligibleAmount = cfg.isCollateralBacked
            ? null  // Gold loan is LTV-based, not income-based
            : Math.round(calcPrincipalFromEmi(emiCapacity, cfg.roiForEmi, cfg.tenureMonths));
        // The "expected EMI" shown to the user assumes they take the eligible amount
        const expectedEmi = emiCapacity;
        return { emiCapacity, eligibleAmount, expectedEmi };
    };

    // ─────────────────────────────────────────────────────────────────────
    // 2.1  Other Loan Eligibility After This New Loan (with 5-type picker)
    // ─────────────────────────────────────────────────────────────────────
    const renderOtherLoanEligibility = () => {
        const cfg = LOAN_TYPE_CONFIG[activeLoanType];
        const elig = computeLoanTypeEligibility(cfg);
        // Customer's CIBIL — pull from a few possible field names, default to "—" if unknown
        const customerCibil = lead?.cibil_score
            || eligibilityResult?.cibil_score
            || formData?.cibil_score
            || analysis?.cibilScore
            || null;
        const cibilQualifies = customerCibil != null && customerCibil >= cfg.cibilRequired;

        const segmentOptions = Object.entries(LOAN_TYPE_CONFIG).map(([key, c]) => ({
            label: c.label,
            value: key,
            icon: c.icon,
        }));

        const segmentTemplate = (opt) => (
            <span className="flex align-items-center gap-2">
                <i className={opt.icon}></i>
                <span>{opt.label}</span>
            </span>
        );

        return renderComponentCard(
            'pi-th-large',
            '#eef2ff',
            '#4338ca',
            'Other Loan Eligibility After This New Loan',
            'Eligibility for each major loan category — assuming the new loan is disbursed',
            <>
                <div className="mb-3">
                    <SelectButton
                        value={activeLoanType}
                        options={segmentOptions}
                        onChange={(e) => e.value != null && setActiveLoanType(e.value)}
                        itemTemplate={segmentTemplate}
                        className="w-full"
                    />
                </div>

                {/* Eligibility hero — split into two columns */}
                <div
                    className="grid align-items-center p-3 border-round-md"
                    style={{
                        background: 'linear-gradient(135deg, var(--surface-card), var(--surface-50))',
                        border: '1px solid var(--surface-border)',
                    }}
                >
                    <div className="col-12 md:col-6">
                        <div className="text-xs uppercase font-semibold text-color-secondary mb-2" style={{ letterSpacing: '.07em' }}>
                            Eligible amount
                        </div>
                        <div className="text-3xl font-bold" style={{ color: 'var(--primary-color)', letterSpacing: '-.02em' }}>
                            {cfg.isCollateralBacked ? '75% of gold value' : formatCurrency(elig.eligibleAmount || 0)}
                        </div>
                        <div className="text-xs text-color-secondary mt-2 line-height-3">
                            {cfg.isCollateralBacked
                                ? 'LTV-based — depends on collateral submitted. No income / FOIR check.'
                                : `Up to ${cfg.displayTenure} tenure at ${cfg.roiRange}.`}
                        </div>
                    </div>
                    <div className="col-12 md:col-6 md:border-left-1 md:surface-border md:pl-3">
                        <div className="text-xs uppercase font-semibold text-color-secondary mb-2" style={{ letterSpacing: '.07em' }}>
                            Score requirement
                        </div>
                        <div className="text-3xl font-bold text-900" style={{ letterSpacing: '-.02em' }}>
                            {cfg.cibilRequired === 0 ? 'No CIBIL check' : `${cfg.cibilRequired} +`}
                        </div>
                        <div className="text-xs text-color-secondary mt-2 line-height-3">
                            {cfg.cibilRequired === 0 ? (
                                <Tag value="Open to all — collateral-backed" severity="success" icon="pi pi-check" />
                            ) : customerCibil != null ? (
                                <>
                                    Customer's CIBIL is <strong>{customerCibil}</strong>.{' '}
                                    <Tag
                                        value={cibilQualifies ? 'Qualifies' : 'Below threshold'}
                                        severity={cibilQualifies ? 'success' : 'danger'}
                                        icon={cibilQualifies ? 'pi pi-check' : 'pi pi-times'}
                                    />
                                </>
                            ) : (
                                <Tag value="CIBIL score not on file" severity="info" icon="pi pi-info-circle" />
                            )}
                        </div>
                    </div>
                </div>

                {/* Per-type details */}
                <div className="grid mt-3">
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Indicative ROI',
                            value: cfg.roiRange,
                            footnote: cfg.isCollateralBacked ? 'Based on lender / tenure' : 'Floating, repo-linked',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: cfg.isCollateralBacked ? 'Tenure' : `Expected EMI (${cfg.displayTenure})`,
                            value: cfg.isCollateralBacked
                                ? '3 – 36 mo'
                                : formatCurrency(elig.expectedEmi || 0),
                            footnote: cfg.isCollateralBacked
                                ? 'Short-term liquidity'
                                : `For ${formatCurrency(elig.eligibleAmount || 0)} principal`,
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'FOIR Headroom needed',
                            value: cfg.foirLimit === 100 ? 'None' : `${cfg.foirLimit}%`,
                            footnote: cfg.isCollateralBacked ? 'Collateral, not income' : 'Industry standard',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Tax Benefit',
                            value: cfg.taxBenefit,
                            footnote: cfg.taxNote,
                            tone: cfg.taxBenefit !== 'None' ? 'positive' : undefined,
                        })}
                    </div>
                </div>

                {/* Type-specific insight */}
                {cfg.note && (
                    <div className="mt-3">
                        <Message
                            severity={cfg.noteTone === 'warning' ? 'warn' : cfg.noteTone}
                            content={
                                <div className="flex align-items-start gap-2">
                                    <i
                                        className={`pi ${cfg.noteTone === 'warning' ? 'pi-exclamation-triangle' : 'pi-info-circle'} mt-1`}
                                    ></i>
                                    <div>
                                        <div className="font-semibold mb-1">
                                            {cfg.noteTone === 'warning' ? 'Caution' : cfg.noteTone === 'success' ? 'Bridge financing option' : 'Timing recommendation'}
                                        </div>
                                        <div className="text-sm line-height-3">{cfg.note}</div>
                                    </div>
                                </div>
                            }
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 2.2  Loan Repayment Priority to Unlock Other Loans
    //
    // Reuses calculateLoanRepaymentPriority(existingLoans, analysis).loans
    // The "what gets unlocked" column estimates across multiple loan types
    // by summing eligibility gains from the 5 configured types.
    // ─────────────────────────────────────────────────────────────────────
    const renderRepaymentPriorityForOtherLoans = () => {
        const priorityResult = calculateLoanRepaymentPriority(existingLoans, analysis) || {};
        const priorityLoans = Array.isArray(priorityResult.loans) ? priorityResult.loans : [];

        if (priorityLoans.length === 0) {
            return renderComponentCard(
                'pi-sort-amount-down',
                '#fef2f2',
                '#b91c1c',
                'Loan Repayment Priority to Unlock Other Loans',
                'Order in which to clear current debts to qualify for additional loan types above',
                <div className="text-center text-color-secondary py-4">
                    <i className="pi pi-inbox text-3xl mb-2 block text-400"></i>
                    <div>No existing loans to prioritise.</div>
                </div>
            );
        }

        // For each priority loan, estimate cross-type unlock potential.
        // Derived from: clearing this loan frees its EMI; that freed EMI
        // converts to additional eligibility across Home + Education (the two
        // most income-sensitive types).
        const homeCfg = LOAN_TYPE_CONFIG.home;
        const eduCfg = LOAN_TYPE_CONFIG.education;

        return renderComponentCard(
            'pi-sort-amount-down',
            '#fef2f2',
            '#b91c1c',
            'Loan Repayment Priority to Unlock Other Loans',
            'Order in which to clear current debts to qualify for the loan types above',
            <div>
                {priorityLoans.map((item, idx) => {
                    const emi = parseFloat(item.emi || 0);
                    const homeUnlock = calcPrincipalFromEmi(emi, homeCfg.roiForEmi, homeCfg.tenureMonths);
                    const eduUnlock = calcPrincipalFromEmi(emi, eduCfg.roiForEmi, eduCfg.tenureMonths);
                    const totalUnlock = homeUnlock + eduUnlock;

                    return (
                        <div
                            key={item.id != null ? `unlock-${item.id}` : `unlock-${idx}`}
                            className="grid align-items-center py-3"
                            style={{
                                borderBottom: idx < priorityLoans.length - 1 ? '1px solid var(--surface-border)' : 'none',
                            }}
                        >
                            <div className="col-fixed" style={{ width: '3rem' }}>
                                <div
                                    className="flex align-items-center justify-content-center border-round font-bold text-white"
                                    aria-label={`Priority rank ${item.priorityRank || idx + 1}`}
                                    style={{ width: '2rem', height: '2rem', background: item.priorityColor || '#64748b' }}
                                >
                                    {item.priorityRank || idx + 1}
                                </div>
                            </div>
                            <div className="col-12 md:col">
                                <div className="font-bold text-900">
                                    Clear {item.loanType || 'Loan'} · {item.lender || '—'}
                                </div>
                                <div className="text-xs text-color-secondary mt-1 line-height-3">
                                    Frees ₹{Math.round(emi).toLocaleString('en-IN')}/mo of FOIR headroom. Combined eligibility for Home + Education loans opens up significantly.
                                </div>
                            </div>
                            <div className="col-12 md:col-fixed text-left md:text-right" style={{ minWidth: '12rem' }}>
                                <div className="font-bold text-900">+ {formatCurrency(totalUnlock)}</div>
                                <div className="text-xs text-color-secondary">across Home + Education</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 2.3  Future Borrowing Headroom
    //
    // When milestones happen → existing loans close → FOIR drops → new
    // borrowing capacity opens up. Derived from each existing loan's
    // remaining tenure and EMI.
    // ─────────────────────────────────────────────────────────────────────
    const renderFutureBorrowingHeadroom = () => {
        const income = analysis?.netSalary || 0;
        const currentFoir = analysis?.foirAfter || 0;

        // Build milestone rows from existing loans (when each closes)
        // + assumed midway milestone for the new loan + new loan close
        const today = new Date();
        const milestones = [];

        (existingLoans || []).forEach((loan, idx) => {
            const remaining = parseInt(loan.remaining_tenure || loan.tenure_remaining || loan.tenure_months || 0, 10);
            if (remaining <= 0) return;
            const emiFreed = parseFloat(loan.emi || loan.emi_amount || 0);
            const closeDate = new Date(today.getFullYear(), today.getMonth() + remaining, 1);
            const dateLabel = closeDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
            const loanType = loan.loan_type || loan.type || 'Loan';
            const lender = loan.lender_name || loan.lender || '';
            milestones.push({
                key: `el-${loan.id || idx}`,
                label: `${loanType} (${lender}) paid off`,
                date: dateLabel,
                sortOrder: closeDate.getTime(),
                emiFreed,
            });
        });

        // New loan milestones — midway + close
        const newTenure = analysis?.newLoanTenure || selectedOffer?.tenure_months || 0;
        const newEmi = analysis?.newEMI || 0;
        if (newTenure > 0) {
            const midway = new Date(today.getFullYear(), today.getMonth() + Math.floor(newTenure / 2), 1);
            const close = new Date(today.getFullYear(), today.getMonth() + newTenure, 1);
            milestones.push({
                key: 'new-mid',
                label: 'New loan midway through',
                date: midway.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
                sortOrder: midway.getTime(),
                emiFreed: 0,         // No EMI freed at midway; just lender confidence
                isMidway: true,
            });
            milestones.push({
                key: 'new-close',
                label: 'New loan closed',
                date: close.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
                sortOrder: close.getTime(),
                emiFreed: newEmi,
            });
        }

        // Sort chronologically
        milestones.sort((a, b) => a.sortOrder - b.sortOrder);

        if (milestones.length === 0) {
            return renderComponentCard(
                'pi-flag',
                '#eef2ff',
                '#4338ca',
                'Future Borrowing Headroom',
                'When more borrowing capacity opens up as loans close',
                <div className="text-center text-color-secondary py-4">
                    <i className="pi pi-inbox text-3xl mb-2 block text-400"></i>
                    <div>No tenure data available to project future capacity.</div>
                </div>
            );
        }

        // Compute cumulative FOIR drop and added eligibility as milestones cascade
        let runningFoir = currentFoir;
        const projected = milestones.map((m) => {
            // Each milestone drops FOIR by (emiFreed / income) * 100
            const foirDrop = income > 0 && !m.isMidway ? (m.emiFreed / income) * 100 : 0;
            runningFoir = Math.max(0, runningFoir - foirDrop);
            // Capacity unlocked — convert freed EMI to home-loan principal (illustrative)
            const homeCfg = LOAN_TYPE_CONFIG.home;
            const capacityUnlocked = !m.isMidway
                ? calcPrincipalFromEmi(m.emiFreed, homeCfg.roiForEmi, homeCfg.tenureMonths)
                : 0;
            return { ...m, newFoir: runningFoir, capacityUnlocked };
        });

        return renderComponentCard(
            'pi-flag',
            '#eef2ff',
            '#4338ca',
            'Future Borrowing Headroom',
            'When more borrowing capacity opens up as loans close',
            <DataTable value={projected} dataKey="key" size="small" stripedRows responsiveLayout="scroll">
                <Column field="label" header="Milestone" body={(r) => <strong>{r.label}</strong>} />
                <Column field="date" header="Date" />
                <Column
                    header="EMI Freed"
                    body={(r) => (
                        <span style={{ color: r.emiFreed > 0 ? 'var(--green-700)' : 'var(--text-color-secondary)' }}>
                            {r.emiFreed > 0 ? `+ ${formatCurrency(r.emiFreed)}/mo` : '—'}
                        </span>
                    )}
                    alignHeader="right"
                    bodyClassName="text-right"
                />
                <Column
                    header="New FOIR"
                    body={(r) => formatPercentage(r.newFoir)}
                    alignHeader="right"
                    bodyClassName="text-right"
                />
                <Column
                    header="Capacity Unlocked"
                    body={(r) => (r.capacityUnlocked > 0 ? `+ ${formatCurrency(r.capacityUnlocked)}` : '—')}
                    alignHeader="right"
                    bodyClassName="text-right"
                />
            </DataTable>
        );
    };

    // ── Tab 2 body ────────────────────────────────────────────────────────
    const renderTab2 = () => (
        <div>
            {renderSectionIntro(
                2,
                'Other Loan Eligibility',
                'Other Loan Eligibility After New Loan',
                'What additional loans the customer can take after this loan is sanctioned, plus how to unlock more',
                3
            )}
            {renderOtherLoanEligibility()}
            {renderRepaymentPriorityForOtherLoans()}
            {renderFutureBorrowingHeadroom()}
        </div>
    );

    // ═════════════════════════════════════════════════════════════════════
    //  TAB 3 — Future Repayment & Planner
    //
    // All numbers derive from analysis + existingLoans + selectedOffer via
    // the purpose-built helpers in portfolioCalculations.js:
    //   - calculateCashFlowImpact     → 3.1
    //   - calculateRiskAssessment     → 3.2
    //   - generateRecommendations     → 3.3  (already memoised as `recommendations`)
    //   - calculateDebtFreedomTimeline → 3.4
    //   - calculateWhatIfScenarios    → 3.5
    //   - calculatePrepaymentImpact   → 3.6  (parameterised by user input)
    // ═════════════════════════════════════════════════════════════════════

    // ─────────────────────────────────────────────────────────────────────
    // 3.1  Monthly Cash Flow Impact
    // ─────────────────────────────────────────────────────────────────────
    const renderMonthlyCashFlowImpact = () => {
        const cf = calculateCashFlowImpact(analysis) || {};
        const before = cf.beforeLoan || {};
        const after = cf.afterLoan || {};
        const changes = cf.changes || {};

        // Before vs After cards
        const beforeAfterStrip = (
            <div className="grid align-items-stretch">
                <div className="col-12 md:col-5">
                    <div
                        className="p-3 border-round-md h-full"
                        style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                    >
                        <div className="text-xs uppercase font-semibold text-color-secondary mb-2" style={{ letterSpacing: '.07em' }}>
                            Before new loan
                        </div>
                        <div className="text-2xl font-bold text-900" style={{ letterSpacing: '-.02em' }}>
                            {formatCurrency(before.available || 0)}
                        </div>
                        <div className="text-xs text-color-secondary mt-2 line-height-3">
                            {formatCurrency(before.income || 0)} income − {formatCurrency(before.emiOutflow || 0)} EMIs
                            <br />
                            FOIR {formatPercentage(analysis?.foirBefore || 0)}
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-2 flex align-items-center justify-content-center">
                    <i className="pi pi-arrow-right text-2xl text-color-secondary"></i>
                </div>
                <div className="col-12 md:col-5">
                    <div
                        className="p-3 border-round-md h-full"
                        style={{
                            background: 'linear-gradient(135deg, var(--primary-50, #eff6ff), var(--surface-card))',
                            border: '1px solid var(--primary-200, #bfdbfe)',
                        }}
                    >
                        <div className="text-xs uppercase font-semibold mb-2" style={{ letterSpacing: '.07em', color: 'var(--primary-color)' }}>
                            After new loan
                        </div>
                        <div className="text-2xl font-bold" style={{ color: 'var(--primary-color)', letterSpacing: '-.02em' }}>
                            {formatCurrency(after.available || 0)}
                        </div>
                        <div className="text-xs text-color-secondary mt-2 line-height-3">
                            {formatCurrency(after.income || 0)} income − {formatCurrency(after.emiOutflow || 0)} EMIs
                            <br />
                            FOIR {formatPercentage(analysis?.foirAfter || 0)}
                        </div>
                    </div>
                </div>
            </div>
        );

        const foirDelta = (analysis?.foirAfter || 0) - (analysis?.foirBefore || 0);
        const safeLimitBreached = (analysis?.foirAfter || 0) > 50 && (analysis?.foirBefore || 0) <= 50;

        return renderComponentCard(
            'pi-arrow-right-arrow-left',
            '#f5f9ff',
            '#1d4ed8',
            'Monthly Cash Flow Impact',
            'Side-by-side comparison of monthly cash movement',
            <>
                {beforeAfterStrip}
                <div className="grid mt-3">
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'EMI Increase',
                            value: `+ ${formatCurrency(changes.emiIncrease || 0)}`,
                            footnote: `+${(changes.emiIncreasePercent || 0).toFixed(1)}% over current`,
                            tone: 'negative',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Disposable Reduction',
                            value: `− ${formatCurrency(changes.availableReduction || 0)}`,
                            footnote: `${(changes.availableReductionPercent || 0).toFixed(1)}% less monthly`,
                            tone: 'negative',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'FOIR Change',
                            value: `${foirDelta >= 0 ? '+' : ''}${foirDelta.toFixed(1)} pts`,
                            footnote: safeLimitBreached ? 'Crosses 50% safe limit' : 'Within previous band',
                            tone: safeLimitBreached ? 'negative' : undefined,
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Total Outstanding',
                            value: formatCurrency(analysis?.combinedOutstanding || 0),
                            footnote: `+ ${formatCurrency(analysis?.newLoanAmount || 0)} added`,
                        })}
                    </div>
                </div>
                {cf.impactLabel && (
                    <div className="mt-3 flex align-items-center gap-2">
                        <span className="text-sm text-color-secondary">Overall:</span>
                        <Tag
                            value={cf.impactLabel}
                            style={{ background: cf.impactColor || 'var(--surface-200)', color: '#fff' }}
                        />
                    </div>
                )}
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 3.2  Risk Assessment
    // ─────────────────────────────────────────────────────────────────────
    const renderRiskAssessment = () => {
        const risk = calculateRiskAssessment(analysis, score) || {};
        const factors = Array.isArray(risk.riskFactors) ? risk.riskFactors : [];

        const statusBody = (row) => {
            const sev = row.status === 'low' ? 'success' : row.status === 'medium' ? 'warning' : 'danger';
            const icon = row.status === 'low' ? 'pi-check' : row.status === 'medium' ? 'pi-exclamation-triangle' : 'pi-times-circle';
            return <Tag severity={sev} icon={`pi ${icon}`} value={row.statusLabel || row.status} />;
        };

        return renderComponentCard(
            'pi-shield',
            '#fef2f2',
            '#b91c1c',
            'Risk Assessment',
            'Factors that could affect repayment capacity over the loan tenure',
            <>
                {risk.overallRiskLabel && (
                    <div
                        className="flex align-items-center justify-content-between p-3 border-round-md mb-3"
                        style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                    >
                        <div>
                            <div className="text-xs uppercase font-semibold text-color-secondary mb-1" style={{ letterSpacing: '.07em' }}>
                                Overall risk
                            </div>
                            <div className="font-bold text-lg" style={{ color: risk.overallRiskColor }}>
                                {risk.overallRiskLabel}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs text-color-secondary mb-1">Risk score</div>
                            <div className="font-bold text-xl text-900">{risk.riskScore}/100</div>
                        </div>
                    </div>
                )}
                <DataTable value={factors} dataKey="factor" size="small" stripedRows responsiveLayout="scroll">
                    <Column field="factor" header="Risk Factor" body={(r) => <strong>{r.factor}</strong>} />
                    <Column field="value" header="Value" alignHeader="right" bodyClassName="text-right" />
                    <Column header="Status" body={statusBody} />
                    <Column field="description" header="Impact" />
                </DataTable>
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 3.3  Recommendations & Insights
    //
    // The memoised `recommendations` is the full object returned by
    // generateRecommendations(analysis, score):
    //   { recommendations: [...warnings], suggestions: [...info],
    //     overallRecommendation: {verdict, verdictLabel, message, ...} }
    // Display order: overall verdict first, then warnings, then suggestions.
    // ─────────────────────────────────────────────────────────────────────
    const renderRecommendationsAndInsights = () => {
        const rec = recommendations || {};
        const warnings = Array.isArray(rec.recommendations) ? rec.recommendations : [];
        const suggestions = Array.isArray(rec.suggestions) ? rec.suggestions : [];
        const overall = rec.overallRecommendation || null;
        // Compose a unified list. Overall first (always shown), then warnings, then suggestions.
        const items = [
            ...(overall
                ? [{
                    type: overall.verdictSeverity === 'warning' ? 'warning'
                        : overall.verdictSeverity === 'danger' ? 'danger'
                        : overall.verdictSeverity === 'success' ? 'success'
                        : 'info',
                    title: `Overall verdict — ${overall.verdictLabel || ''}`,
                    message: overall.message || '',
                    icon: overall.verdictSeverity === 'success' ? 'pi-check-circle' : 'pi-info-circle',
                }]
                : []),
            ...warnings,
            ...suggestions,
        ];

        if (items.length === 0) {
            return renderComponentCard(
                'pi-lightbulb',
                '#fffbeb',
                '#b45309',
                'Recommendations & Insights',
                'Specific actions tailored to this customer\'s profile',
                <div className="text-center text-color-secondary py-4">
                    <i className="pi pi-check-circle text-3xl mb-2 block text-400"></i>
                    <div>No urgent recommendations — profile is healthy.</div>
                </div>
            );
        }

        // Map recommendation type → Message severity + icon
        const toneFor = (type) => {
            switch ((type || '').toLowerCase()) {
                case 'warning': return { severity: 'warn', icon: 'pi-exclamation-triangle' };
                case 'danger':
                case 'critical': return { severity: 'error', icon: 'pi-times-circle' };
                case 'success':
                case 'positive': return { severity: 'success', icon: 'pi-check-circle' };
                case 'info':
                default: return { severity: 'info', icon: 'pi-info-circle' };
            }
        };

        return renderComponentCard(
            'pi-lightbulb',
            '#fffbeb',
            '#b45309',
            'Recommendations & Insights',
            'Specific actions tailored to this customer\'s profile',
            <div className="flex flex-column gap-2">
                {items.map((rec, idx) => {
                    const tone = toneFor(rec.type);
                    return (
                        <Message
                            key={`rec-${idx}`}
                            severity={tone.severity}
                            content={
                                <div className="flex align-items-start gap-2">
                                    <i className={`pi ${tone.icon} mt-1`}></i>
                                    <div>
                                        <div className="font-semibold mb-1">{rec.title || 'Insight'}</div>
                                        <div className="text-sm line-height-3">{rec.message || rec.body || ''}</div>
                                        {rec.action && (
                                            <div className="text-xs text-color-secondary mt-1">
                                                <strong>Action:</strong> {rec.action}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            style={{ width: '100%' }}
                        />
                    );
                })}
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 3.4  Debt Freedom Timeline
    // ─────────────────────────────────────────────────────────────────────
    const renderDebtFreedomTimeline = () => {
        const tl = calculateDebtFreedomTimeline(existingLoans, analysis) || {};
        const milestones = Array.isArray(tl.milestones) ? tl.milestones : [];

        // Colour ramp for milestones — gradient from amber → emerald
        const stepColors = ['#f59e0b', '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#22c55e'];

        return renderComponentCard(
            'pi-check-circle',
            '#f0fdf4',
            '#047857',
            'Debt Freedom Timeline',
            'When the customer becomes fully debt-free at current EMI schedule',
            <>
                {tl.debtFreeDateFormatted && (
                    <div
                        className="text-center p-3 border-round-md mb-3"
                        style={{
                            background: 'linear-gradient(135deg, #f0fdf4, #ecfeff)',
                            border: '1px solid var(--green-200, #bbf7d0)',
                        }}
                    >
                        <div className="text-xs uppercase font-semibold text-color-secondary mb-2" style={{ letterSpacing: '.07em' }}>
                            Estimated debt-free date
                        </div>
                        <div className="font-bold text-3xl" style={{ color: 'var(--green-700, #047857)', letterSpacing: '-.025em' }}>
                            {tl.debtFreeDateFormatted}
                        </div>
                        <div className="text-xs text-color-secondary mt-2">
                            ~{tl.debtFreeYears || 0} years {tl.debtFreeRemainingMonths ? `${tl.debtFreeRemainingMonths} months` : ''} from today, assuming current EMIs without prepayment
                        </div>
                    </div>
                )}
                {milestones.length === 0 ? (
                    <div className="text-center text-color-secondary py-3">
                        <i className="pi pi-inbox text-2xl mb-2 block text-400"></i>
                        <div>No tenure data available to chart milestones.</div>
                    </div>
                ) : (
                    <div className="grid">
                        {milestones.map((m, idx) => (
                            <div key={m.id || `ms-${idx}`} className="col-12 sm:col-6 md:col-3">
                                <div
                                    className="p-3 border-round-md h-full"
                                    style={{
                                        background: 'var(--surface-card)',
                                        border: '1px solid var(--surface-border)',
                                        borderTop: `3px solid ${stepColors[idx % stepColors.length]}`,
                                    }}
                                >
                                    <div className="text-xs uppercase font-semibold text-color-secondary mb-1" style={{ letterSpacing: '.05em' }}>
                                        {m.date instanceof Date
                                            ? m.date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                                            : ''}
                                    </div>
                                    <div className="font-bold text-900 text-sm mb-1">{m.label}</div>
                                    <div className="text-xs text-color-secondary line-height-3">
                                        Frees {formatCurrency(m.emiFreed || 0)}/mo · Remaining EMI {formatCurrency(Math.max(0, m.remainingEMI || 0))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 3.5  What-If Scenarios
    // ─────────────────────────────────────────────────────────────────────
    const renderWhatIfScenarios = () => {
        const wif = calculateWhatIfScenarios(analysis, selectedOffer) || {};
        const scenarios = Array.isArray(wif.scenarios) ? wif.scenarios : [];
        const current = wif.currentScenario || {};

        // Combine current + alternates into one table
        const rows = [
            {
                key: 'current',
                title: 'Current selection',
                amount: current.loanAmount,
                tenure: current.tenure,
                emi: current.emi,
                foir: current.foir,
                totalInterest: current.totalInterest,
                tag: { label: 'Selected', severity: 'info' },
                highlight: true,
            },
            ...scenarios.map((s) => {
                let tag;
                if (s.id === 'lower-amount') {
                    tag = s.newFOIR < 50 ? { label: 'Safer FOIR', severity: 'success', icon: 'pi-check' } : { label: 'Lower EMI', severity: 'info' };
                } else if (s.id === 'longer-tenure') {
                    tag = { label: 'Lower EMI', severity: 'info', icon: 'pi-arrow-down' };
                } else if (s.id === 'shorter-tenure') {
                    tag = s.newFOIR > 55 ? { label: 'High FOIR', severity: 'warning', icon: 'pi-exclamation-triangle' } : { label: 'Faster payoff', severity: 'success', icon: 'pi-check' };
                } else if (s.id === 'extra-emi') {
                    tag = { label: 'Saves interest', severity: 'success', icon: 'pi-check' };
                } else {
                    tag = { label: 'Alternate', severity: 'info' };
                }
                return {
                    key: s.id,
                    title: s.title,
                    amount: s.loanAmount,
                    tenure: s.tenure,
                    emi: s.emi,
                    foir: s.newFOIR,
                    // Total interest for non-current rows: derive from EMI × tenure − principal
                    totalInterest: (s.emi || 0) * (s.tenure || 0) - (s.loanAmount || 0),
                    tag,
                };
            }),
        ];

        const tagBody = (row) => (
            <Tag
                severity={row.tag?.severity || 'info'}
                icon={row.tag?.icon ? `pi ${row.tag.icon}` : null}
                value={row.tag?.label || ''}
            />
        );

        const titleBody = (row) => (
            <span className="font-bold" style={{ color: row.highlight ? 'var(--primary-color)' : 'var(--text-color)' }}>
                {row.title}
            </span>
        );

        return renderComponentCard(
            'pi-sliders-h',
            '#f5f9ff',
            '#1d4ed8',
            'What-If Scenarios',
            'Compare alternative loan structures side by side',
            <DataTable
                value={rows}
                dataKey="key"
                size="small"
                stripedRows
                responsiveLayout="scroll"
                rowClassName={(r) => (r.highlight ? 'surface-100' : '')}
            >
                <Column header="Scenario" body={titleBody} />
                <Column header="Amount" body={(r) => formatCurrency(r.amount || 0)} alignHeader="right" bodyClassName="text-right" />
                <Column header="Tenure" body={(r) => `${r.tenure || 0} mo`} alignHeader="right" bodyClassName="text-right" />
                <Column header="EMI" body={(r) => formatCurrency(r.emi || 0)} alignHeader="right" bodyClassName="text-right" />
                <Column header="FOIR" body={(r) => formatPercentage(r.foir || 0)} alignHeader="right" bodyClassName="text-right" />
                <Column header="Total Interest" body={(r) => formatCurrency(Math.max(0, r.totalInterest || 0))} alignHeader="right" bodyClassName="text-right" />
                <Column header="Recommendation" body={tagBody} />
            </DataTable>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 3.6  Smart Prepayment Planner
    //
    // Parameterised: user moves the slider → calculatePrepaymentImpact
    // re-runs with the new lump-sum + chosen strategy.
    // ─────────────────────────────────────────────────────────────────────
    const renderSmartPrepaymentPlanner = () => {
        // Bound the slider to the total outstanding so users can't model
        // prepaying more than they actually owe.
        const totalOutstanding = analysis?.combinedOutstanding || 0;
        const maxSlider = Math.max(100000, totalOutstanding);    // ₹1L minimum range

        const impact = calculatePrepaymentImpact(
            existingLoans,
            analysis,
            prepaymentAmount,
            prepaymentStrategy
        ) || {};
        const totals = impact.totalImpact || {};
        const allocations = Array.isArray(impact.allocations) ? impact.allocations : [];

        // Effective ROI on the prepayment = interest saved / amount prepaid
        const effectiveRoi = prepaymentAmount > 0 && totals.totalInterestSaved
            ? (totals.totalInterestSaved / prepaymentAmount) * 100
            : 0;

        const strategyOptions = [
            { label: 'Highest interest first (Avalanche)', value: 'avalanche' },
            { label: 'Smallest balance first (Snowball)', value: 'snowball' },
            { label: 'Proportional across loans', value: 'proportional' },
        ];

        return renderComponentCard(
            'pi-bolt',
            '#ecfeff',
            '#0e7490',
            'Smart Prepayment Planner',
            'Model the impact of a one-time lump sum on total interest and tenure',
            <>
                {/* Inputs */}
                <div
                    className="p-3 border-round-md mb-3"
                    style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                >
                    <div className="grid">
                        <div className="col-12 md:col-7">
                            <label htmlFor="prepaymentSlider" className="text-xs uppercase font-semibold text-color-secondary block mb-2" style={{ letterSpacing: '.07em' }}>
                                Lump-sum prepayment
                            </label>
                            <div className="flex flex-column md:flex-row align-items-stretch md:align-items-center gap-3">
                                <Slider
                                    id="prepaymentSlider"
                                    value={prepaymentAmount}
                                    onChange={(e) => setPrepaymentAmount(e.value)}
                                    min={0}
                                    max={maxSlider}
                                    step={5000}
                                    className="flex-1"
                                    ariaLabel="Lump-sum prepayment amount slider"
                                />
                                <InputNumber
                                    value={prepaymentAmount}
                                    onValueChange={(e) => setPrepaymentAmount(e.value || 0)}
                                    mode="currency"
                                    currency="INR"
                                    locale="en-IN"
                                    min={0}
                                    max={maxSlider}
                                    inputStyle={{ width: '100%' }}
                                    inputClassName="w-full md:w-auto"
                                    pt={{ root: { className: 'w-full md:w-auto', style: { minWidth: '10rem' } } }}
                                    aria-label="Lump-sum prepayment amount"
                                />
                            </div>
                            <div className="text-xs text-color-secondary mt-2">
                                Max ≈ total outstanding ({formatCurrency(totalOutstanding)})
                            </div>
                        </div>
                        <div className="col-12 md:col-5">
                            <label className="text-xs uppercase font-semibold text-color-secondary block mb-2" style={{ letterSpacing: '.07em' }}>
                                Allocation strategy
                            </label>
                            <SelectButton
                                value={prepaymentStrategy}
                                options={strategyOptions}
                                onChange={(e) => e.value != null && setPrepaymentStrategy(e.value)}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Impact metrics */}
                <div className="grid">
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Interest Saved',
                            value: formatCurrency(totals.totalInterestSaved || 0),
                            footnote: 'Over remaining tenure',
                            tone: 'positive',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Tenure Reduced',
                            value: `${totals.monthsSavedTotal || 0} months`,
                            footnote: `${totals.yearsSaved || 0} years earlier closure`,
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'New Debt-Free Date',
                            value: totals.newDebtFreeDate || '—',
                            footnote: totals.originalDebtFreeDate ? `From ${totals.originalDebtFreeDate}` : '',
                        })}
                    </div>
                    <div className="col-6 md:col-3">
                        {renderMetric({
                            label: 'Effective ROI',
                            value: `${effectiveRoi.toFixed(1)}%`,
                            footnote: 'On the prepaid amount',
                            tone: effectiveRoi > 0 ? 'positive' : undefined,
                        })}
                    </div>
                </div>

                {/* Allocation breakdown */}
                {allocations.length > 0 && prepaymentAmount > 0 && (
                    <DataTable value={allocations} dataKey="id" size="small" stripedRows responsiveLayout="scroll" className="mt-3">
                        <Column
                            header="Loan"
                            body={(r) => (
                                <span>
                                    <strong>{r.loanType || 'Loan'}</strong> · {r.lender || '—'}
                                    {' '}
                                    <span className="text-xs text-color-secondary">({formatPercentage(r.interestRate || 0)})</span>
                                </span>
                            )}
                        />
                        <Column
                            header="Allocation"
                            body={(r) => formatCurrency(r.extraAllocation || 0)}
                            alignHeader="right"
                            bodyClassName="text-right"
                        />
                        <Column
                            header="Months Saved"
                            body={(r) => (r.monthsSaved ? `${r.monthsSaved} mo` : '—')}
                            alignHeader="right"
                            bodyClassName="text-right"
                        />
                        <Column
                            header="Interest Saved"
                            body={(r) => formatCurrency(r.interestSaved || 0)}
                            alignHeader="right"
                            bodyClassName="text-right"
                        />
                    </DataTable>
                )}
                {prepaymentAmount === 0 && (
                    <div className="text-center text-color-secondary py-3 mt-2">
                        <i className="pi pi-arrow-up text-xl mb-2 block text-400"></i>
                        <div className="text-sm">Move the slider above to model a prepayment.</div>
                    </div>
                )}
            </>
        );
    };

    // ── Tab 3 body ────────────────────────────────────────────────────────
    const renderTab3 = () => (
        <div>
            {renderSectionIntro(
                3,
                'Repayment & Planner',
                'Future Repayment & Planner',
                'What the new loan does to monthly finances, and tools to model alternatives',
                6
            )}

            {/* Sub-group A — Repayment Health */}
            {renderSubgroup('Repayment Health')}
            {renderMonthlyCashFlowImpact()}
            {renderRiskAssessment()}
            {renderRecommendationsAndInsights()}

            {/* Sub-group B — Planning Tools */}
            {renderSubgroup('Planning Tools')}
            {renderDebtFreedomTimeline()}
            {renderWhatIfScenarios()}
            {renderSmartPrepaymentPlanner()}
        </div>
    );

    // ═════════════════════════════════════════════════════════════════════
    //  TAB 4 — Wealth & Cash Flow Management
    //
    // All numbers derive from analysis (netSalary, disposableAfter,
    // combinedEMI) + existingLoans (for home-loan ROI comparison).
    //
    // INDUSTRY-STANDARD CONSTANTS used (not mocked data, these are textbook
    // financial-planning conventions for Indian salaried customers):
    //   - Recommended savings rate          : 20% of net income
    //   - Emergency fund target             : 3× monthly EMI burden
    //   - Suggested allocation buckets      : SIP 42.7% / EmFund 20.1% /
    //                                         Prepay 12.5% / Lifestyle 24.7%
    //   - Asset-class portfolio weights     : Equity 59 / Debt 23 / PPF 12 / Gold 6
    //   - Blended CAGR for projection       : 10.2%  (matches the weight mix)
    //
    // The customer-specific NUMBERS come from analysis. The PERCENTAGES are
    // financial-planning standards.
    // ═════════════════════════════════════════════════════════════════════

    // Blended CAGR used for SIP / wealth projection — matches the asset
    // allocation table below (Equity 59 % × 12 + Debt 23 % × 7 + PPF 12 % × 7.1
    // + Gold 6 % × 8 ≈ 10.2). Exposed as a constant so changes here
    // automatically propagate to both 4.2 and 4.3.
    const WEALTH_BLENDED_CAGR = 10.2;

    // Future value of a monthly SIP — standard compound-interest formula.
    //   FV = P × [((1 + r)^n − 1) / r] × (1 + r)
    // where P = monthly contribution, r = monthly rate, n = months.
    const futureValueOfSip = (monthly, annualRate, years) => {
        if (monthly <= 0 || years <= 0) return 0;
        const r = annualRate / 12 / 100;
        const n = years * 12;
        if (r === 0) return monthly * n;
        return monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    };

    // Pull existing home-loan ROI if the customer has one — used in 4.3's
    // "wealth-build vs prepay" verdict.
    const findHomeLoanRate = () => {
        const homeLoan = (existingLoans || []).find((l) => {
            const t = (l.loan_type || l.type || '').toLowerCase();
            return t.includes('home') || t.includes('housing');
        });
        return homeLoan ? parseFloat(homeLoan.interest_rate || 0) : null;
    };

    // ─────────────────────────────────────────────────────────────────────
    // 4.1  Cash Flow Health
    //
    // Shows the customer's actual disposable income (from analysis), the
    // recommended-savings benchmark (20 % of net), and emergency-fund target
    // (3 × EMI burden). Then splits the disposable across 4 standard buckets.
    // ─────────────────────────────────────────────────────────────────────
    const renderCashFlowHealth = () => {
        const netSalary = analysis?.netSalary || 0;
        const disposable = analysis?.disposableAfter || 0;
        const combinedEmi = analysis?.combinedEMI || 0;
        const disposablePct = netSalary > 0 ? (disposable / netSalary) * 100 : 0;

        const recommendedSavings = Math.round(netSalary * 0.20);
        const emergencyFundTarget = Math.round(combinedEmi * 3);

        // Standard 4-bucket split of disposable income for an Indian salaried
        // customer with an active loan portfolio.
        const allocations = [
            {
                label: 'Investments (SIP / MF)',
                desc: 'Long-term wealth creation',
                pct: 42.7,
                dot: '#1d4ed8',
            },
            {
                label: 'Emergency Fund Build-up',
                desc: `Until ${formatCurrency(emergencyFundTarget)} target reached`,
                pct: 20.1,
                dot: '#f59e0b',
            },
            {
                label: 'Loan Prepayment Buffer',
                desc: 'Accelerate highest-interest loan closure',
                pct: 12.5,
                dot: '#10b981',
            },
            {
                label: 'Lifestyle & Discretionary',
                desc: 'Flexible monthly spending',
                pct: 24.7,
                dot: '#94a3b8',
            },
        ];

        return renderComponentCard(
            'pi-wallet',
            '#f0fdf4',
            '#047857',
            'Cash Flow Health',
            "Monthly money management — where the customer's salary goes after EMIs",
            <>
                <div className="grid">
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: 'Available After EMI',
                            value: formatCurrency(disposable),
                            footnote: `${disposablePct.toFixed(1)}% of monthly income`,
                            tone: disposablePct >= 30 ? 'positive' : undefined,
                        })}
                    </div>
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: 'Recommended Savings',
                            value: formatCurrency(recommendedSavings),
                            footnote: '20% of net income (rule of thumb)',
                        })}
                    </div>
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: 'Emergency Fund Target',
                            value: formatCurrency(emergencyFundTarget),
                            footnote: '3 months of EMI coverage',
                        })}
                    </div>
                </div>

                {/* Disposable allocation breakdown */}
                <div
                    className="p-3 border-round-md mt-3"
                    style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                >
                    <div className="text-sm font-bold text-900 mb-3">
                        Suggested allocation of disposable {formatCurrency(disposable)}
                    </div>
                    {allocations.map((a, idx) => {
                        const amount = Math.round((disposable * a.pct) / 100);
                        return (
                            <div
                                key={`alloc-${idx}`}
                                className="flex align-items-center gap-3 py-2"
                                style={{
                                    borderBottom: idx < allocations.length - 1 ? '1px dashed var(--surface-border)' : 'none',
                                }}
                            >
                                <span
                                    style={{
                                        width: '.7rem',
                                        height: '.7rem',
                                        borderRadius: '50%',
                                        background: a.dot,
                                        flexShrink: 0,
                                    }}
                                ></span>
                                <div className="flex-1">
                                    <div className="font-semibold text-900 text-sm">{a.label}</div>
                                    <div className="text-xs text-color-secondary mt-1">
                                        {formatCurrency(amount)} — {a.desc}
                                    </div>
                                </div>
                                <span className="font-bold text-900 text-sm">{a.pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 4.2  Asset Creation Plan
    //
    // Splits the customer's recommended SIP capacity (42.7 % of disposable
    // = the "Investments" bucket from 4.1) across 4 instruments in
    // industry-standard ratios for someone with a 15-20 yr horizon and a
    // running home loan.
    // ─────────────────────────────────────────────────────────────────────
    const renderAssetCreationPlan = () => {
        const disposable = analysis?.disposableAfter || 0;
        // SIP capacity = the "Investments" bucket from 4.1 (42.7% of disposable)
        const monthlySip = Math.round((disposable * 42.7) / 100);

        // Standard instrument weights for a balanced 15-20 yr portfolio
        const instruments = [
            { name: 'Equity SIP (Index / Large Cap)', pct: 59, returnRange: '11–13% CAGR', why: 'Long-term wealth creation; matches 15-20 yr horizon' },
            { name: 'Debt Mutual Fund (Short-Term)',   pct: 23, returnRange: '6.5–7.5%',     why: 'Stable returns; tax-efficient vs FD over 3 years' },
            { name: 'PPF / EPF Top-up',                pct: 12, returnRange: '7.1% (PPF)',    why: 'Section 80C tax benefit; safe sovereign return' },
            { name: 'Gold ETF / SGB',                  pct: 6,  returnRange: '~8% historical', why: 'Inflation hedge; diversification' },
        ];

        const rows = instruments.map((i, idx) => ({
            key: `inst-${idx}`,
            name: i.name,
            monthly: Math.round((monthlySip * i.pct) / 100),
            pct: i.pct,
            returnRange: i.returnRange,
            why: i.why,
        }));

        const totalMonthly = rows.reduce((s, r) => s + r.monthly, 0);

        return renderComponentCard(
            'pi-chart-bar',
            '#eef2ff',
            '#4338ca',
            'Asset Creation Plan',
            "Tailored investment recommendations based on the customer's surplus capacity",
            <>
                <DataTable value={rows} dataKey="key" size="small" stripedRows responsiveLayout="scroll">
                    <Column header="Instrument" body={(r) => <strong>{r.name}</strong>} />
                    <Column header="Monthly" body={(r) => formatCurrency(r.monthly)} alignHeader="right" bodyClassName="text-right" />
                    <Column header="Allocation" body={(r) => `${r.pct}%`} alignHeader="right" bodyClassName="text-right" />
                    <Column field="returnRange" header="Expected Return" alignHeader="right" bodyClassName="text-right" />
                    <Column field="why" header="Why" />
                </DataTable>
                <div
                    className="flex flex-wrap align-items-center justify-content-between gap-2 p-3 border-round-md mt-2"
                    style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                >
                    <div className="font-bold text-900">
                        Total monthly · {formatCurrency(totalMonthly)}
                    </div>
                    <div className="text-sm text-color-secondary">
                        ~{WEALTH_BLENDED_CAGR}% blended CAGR
                    </div>
                    <Tag value="Balanced portfolio" severity="info" icon="pi pi-check" />
                </div>
                <Message
                    className="mt-3 w-full"
                    severity="info"
                    content={
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-info-circle mt-1"></i>
                            <div>
                                <div className="font-semibold mb-1">Why this allocation?</div>
                                <div className="text-sm line-height-3">
                                    Equity-heavy SIP (59%) compounds over the long horizon. Adding the debt fund
                                    and PPF reduces volatility, so the overall portfolio grows at
                                    ~{WEALTH_BLENDED_CAGR}% blended. The point of comparison is the customer's home
                                    loan ROI — if blended return &gt; home loan rate, keep investing instead of
                                    pre-paying the home loan.
                                </div>
                            </div>
                        </div>
                    }
                />
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────
    // 4.3  Wealth Trajectory
    //
    // Future value of the customer's actual SIP capacity (from 4.2) at the
    // blended CAGR, projected over 3/5/10/15/20 years. Then compares to
    // the existing home-loan ROI (if any) to give the wealth-build vs
    // prepay verdict.
    // ─────────────────────────────────────────────────────────────────────
    const renderWealthTrajectory = () => {
        const disposable = analysis?.disposableAfter || 0;
        const monthlySip = Math.round((disposable * 42.7) / 100);

        const horizons = [3, 5, 10, 15, 20];
        const projections = horizons.map((years) => ({
            years,
            value: Math.round(futureValueOfSip(monthlySip, WEALTH_BLENDED_CAGR, years)),
        }));
        // Three headline numbers
        const fiveYr = projections.find((p) => p.years === 5)?.value || 0;
        const tenYr = projections.find((p) => p.years === 10)?.value || 0;
        const twentyYr = projections.find((p) => p.years === 20)?.value || 0;

        const maxValue = Math.max(...projections.map((p) => p.value), 1);

        const homeRate = findHomeLoanRate();
        const wealthBeatsLoan = homeRate != null && WEALTH_BLENDED_CAGR > homeRate;

        if (monthlySip === 0) {
            return renderComponentCard(
                'pi-arrow-up-right',
                '#f0fdf4',
                '#047857',
                'Wealth Trajectory',
                'Projected net worth if the recommended allocation is followed consistently',
                <Message
                    severity="warn"
                    className="w-full"
                    content={
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-exclamation-triangle mt-1"></i>
                            <div>
                                <div className="font-semibold mb-1">No surplus to invest</div>
                                <div className="text-sm line-height-3">
                                    Disposable income after EMIs is {formatCurrency(disposable)}. Reduce loan
                                    burden or increase income before building a wealth plan.
                                </div>
                            </div>
                        </div>
                    }
                />
            );
        }

        return renderComponentCard(
            'pi-arrow-up-right',
            '#f0fdf4',
            '#047857',
            'Wealth Trajectory',
            'Projected net worth if the recommended allocation is followed consistently',
            <>
                {/* 3 headline net-worth numbers */}
                <div className="grid">
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: '5-Year Net Worth',
                            value: formatCurrency(fiveYr),
                            footnote: 'From investments alone',
                            tone: 'positive',
                        })}
                    </div>
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: '10-Year Net Worth',
                            value: formatCurrency(tenYr),
                            footnote: 'Compounding kicks in',
                            tone: 'positive',
                        })}
                    </div>
                    <div className="col-12 md:col-4">
                        {renderMetric({
                            label: '20-Year Net Worth',
                            value: formatCurrency(twentyYr),
                            footnote: 'Plus debt-free home',
                            tone: 'positive',
                        })}
                    </div>
                </div>

                {/* Growth projection bars */}
                <div
                    className="p-3 border-round-md mt-3"
                    style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
                >
                    <div className="text-sm font-bold text-900 mb-3">
                        Investment growth projection ({formatCurrency(monthlySip)}/mo SIP @ {WEALTH_BLENDED_CAGR}% CAGR)
                    </div>
                    {projections.map((p) => {
                        const width = (p.value / maxValue) * 100;
                        const fillColor = p.years === 20 ? '#047857' : '#10b981';
                        return (
                            <div key={`yr-${p.years}`} className="flex align-items-center gap-3 py-2">
                                <span className="text-sm font-semibold text-color-secondary" style={{ width: '4rem', flexShrink: 0 }}>
                                    Year {p.years}
                                </span>
                                <div className="flex-1" style={{ height: '.6rem', background: 'var(--surface-200)', borderRadius: '999px', overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            width: `${width}%`,
                                            height: '100%',
                                            background: fillColor,
                                            transition: 'width .4s ease',
                                        }}
                                    ></div>
                                </div>
                                <span className="text-sm font-bold text-900" style={{ width: '6rem', flexShrink: 0, textAlign: 'right' }}>
                                    {formatCurrency(p.value)}
                                </span>
                            </div>
                        );
                    })}
                    <div className="text-xs text-color-secondary mt-2 line-height-3">
                        Projection assumes {WEALTH_BLENDED_CAGR}% blended CAGR and uninterrupted contributions.
                        Bonuses, salary hikes, and prepayments accelerate this further.
                    </div>
                </div>

                {/* Wealth-build vs prepay verdict — only meaningful if customer has a home loan */}
                {homeRate != null && (
                    <Message
                        className="mt-3 w-full"
                        severity={wealthBeatsLoan ? 'success' : 'warn'}
                        content={
                            <div className="flex align-items-start gap-2">
                                <i className={`pi ${wealthBeatsLoan ? 'pi-check-circle' : 'pi-exclamation-triangle'} mt-1`}></i>
                                <div>
                                    <div className="font-semibold mb-1">The full picture</div>
                                    <div className="text-sm line-height-3">
                                        {wealthBeatsLoan ? (
                                            <>
                                                Customer's home loan ROI is <strong>{formatPercentage(homeRate)}</strong>,
                                                below the blended return of <strong>{WEALTH_BLENDED_CAGR}%</strong>.
                                                <strong> Wealth-building beats home-loan prepayment</strong> — every rupee
                                                invested earns more than it saves on interest. Keep the home loan
                                                running, prioritise the SIP plan above.
                                            </>
                                        ) : (
                                            <>
                                                Customer's home loan ROI is <strong>{formatPercentage(homeRate)}</strong>,
                                                above the blended return of <strong>{WEALTH_BLENDED_CAGR}%</strong>.
                                                <strong> Prepay the home loan first</strong>, then build the
                                                investment corpus — the guaranteed interest saved is higher than
                                                the projected SIP return.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        }
                    />
                )}
                {homeRate == null && (
                    <Message
                        className="mt-3 w-full"
                        severity="info"
                        content={
                            <div className="flex align-items-start gap-2">
                                <i className="pi pi-info-circle mt-1"></i>
                                <div>
                                    <div className="font-semibold mb-1">No home loan to compare against</div>
                                    <div className="text-sm line-height-3">
                                        Customer has no active home loan in the portfolio. The blended SIP
                                        return of <strong>{WEALTH_BLENDED_CAGR}%</strong> is the baseline benchmark
                                        for any future borrowing decision.
                                    </div>
                                </div>
                            </div>
                        }
                    />
                )}
            </>
        );
    };

    // ── Tab 4 body ────────────────────────────────────────────────────────
    const renderTab4 = () => (
        <div>
            {renderSectionIntro(
                4,
                'Wealth',
                'Wealth & Cash Flow Management',
                'Beyond debt — building assets, managing cash flow, and growing net worth',
                3
            )}
            {renderCashFlowHealth()}
            {renderAssetCreationPlan()}
            {renderWealthTrajectory()}
        </div>
    );

    // ── 1.1B  BT Priority Ranking ─────────────────────────────────────────
    //
    // Takes the BT-chip data already cached in btOffersByLoan and ranks each
    // loan by "how much money BT-ing it would save you". Turns the Portfolio
    // from an inventory into a to-do list: "start with THIS loan for the
    // biggest saving".
    //
    // Three states:
    //   - no BT clicks yet   → prompt with CTA (call-out card)
    //   - some clicks, at least one saving available → the ranked table
    //   - all loans checked, none saveable → celebration card
    //
    // Effort classification (Easy / Medium) is based on loan type:
    //   Easy = unsecured (PL, CC, BL-unsecured, Auto, Consumer, Education)
    //     → just a rate arbitrage, no collateral transfer
    //   Medium = secured (HL, LAP, BL-secured, Gold)
    //     → title deed transfer or collateral repapering, slower + fees
    const renderBTPriorityRanking = () => {
        const loans = existingLoans || [];
        if (loans.length === 0) return null;

        // Effort lookup — matches the ENGINE_TYPES / MARKET_LENDERS split
        // in portfolioBtService.js and the getLoanIconForType category names.
        const effortForType = (typeLabel) => {
            const s = String(typeLabel || '').toLowerCase();
            if (s.includes('home') || s.includes('house') || s.includes('housing') || s.includes('property') || s.includes('lap')) {
                return { label: 'Medium', reason: 'Title deed transfer needed' };
            }
            if (s.includes('gold')) {
                return { label: 'Medium', reason: 'Gold collateral repapering' };
            }
            if (s.includes('business') && s.includes('secured')) {
                return { label: 'Medium', reason: 'Property collateral transfer' };
            }
            return { label: 'Easy', reason: 'Unsecured — no collateral transfer' };
        };

        // Humanize slug (same helper the row list uses)
        const humanize = (loan) => {
            if (loan?.loan_type_label) return loan.loan_type_label;
            const raw = loan?.loan_type || 'Loan';
            return String(raw).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
                .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        };

        // Build ranking rows: only loans whose BT data has been fetched AND
        // whose best offer beats the current rate meaningfully.
        // Loans not yet checked → counted separately for the prompt state.
        let fetchedCount = 0;
        const rankable = loans.map((loan) => {
            const key = loan.id || `${loan.loan_type}-${loan.lender_name}-${loan.outstanding_loan}`;
            const bt = btOffersByLoan[key];
            if (!bt || bt.loading) return null;
            fetchedCount += 1;
            if (!bt.best) return null;
            const currentRoi = Number(loan.interest_rate) || 0;
            const bestRoi = Number(bt.best.roi) || 0;
            const savings = Number(bt.best.savings) || 0;
            const roiDelta = currentRoi - bestRoi;
            // Only include if the BT actually saves money (positive savings
            // AND at least a 0.25% rate cut — smaller deltas aren't worth
            // the friction of a BT application)
            if (savings <= 0 || roiDelta < 0.25) return null;
            return {
                key,
                loanTypeLabel: humanize(loan),
                lenderName: loan.lender_name || 'Unknown',
                outstanding: Number(loan.outstanding_loan) || 0,
                currentRoi,
                bestRoi,
                bestLender: bt.best.lender_name || '—',
                isMarketRate: !!bt.best.isMarketRate,
                savings,
                roiDelta,
                effort: effortForType(loan.loan_type),
            };
        }).filter(Boolean).sort((a, b) => b.savings - a.savings);

        // ── Empty states ──
        const noneChecked = fetchedCount === 0;
        const allCheckedNoSaving = fetchedCount === loans.length && rankable.length === 0;

        let body;
        if (noneChecked) {
            body = (
                <div className="flex flex-column align-items-center text-center py-4">
                    <div
                        className="flex align-items-center justify-content-center border-round mb-3"
                        style={{ width: '3rem', height: '3rem', background: 'var(--primary-50)' }}
                    >
                        <i className="pi pi-bolt text-primary" style={{ fontSize: '1.2rem' }} />
                    </div>
                    <div className="text-sm font-semibold text-900 mb-2">
                        Ready to see which loans are worth refinancing?
                    </div>
                    <div className="text-xs text-color-secondary" style={{ maxWidth: '32rem' }}>
                        Click <b>"Check BT"</b> on any loan in the All Loans Overview above.
                        We'll fetch live lender quotes and rank your loans by potential savings —
                        so you know which one to refinance first.
                    </div>
                </div>
            );
        } else if (allCheckedNoSaving) {
            body = (
                <div className="flex align-items-start gap-3 p-3 border-round-md"
                    style={{ background: 'linear-gradient(135deg, var(--teal-50) 0%, var(--green-50) 100%)', border: '1px solid var(--teal-200)' }}
                >
                    <i className="pi pi-verified text-teal-700" style={{ fontSize: '1.5rem', flexShrink: 0 }} />
                    <div className="flex-1">
                        <div className="text-sm font-bold text-teal-800 mb-1">
                            All loans are already at competitive rates
                        </div>
                        <div className="text-xs text-color-secondary">
                            We checked all {loans.length} loans against live lender quotes and market rates.
                            None of them would save meaningful money if refinanced. Your borrower already
                            has the best deal available.
                        </div>
                    </div>
                </div>
            );
        } else {
            // Ranked table state
            const totalSaving = rankable.reduce((s, r) => s + r.savings, 0);
            const partialData = fetchedCount < loans.length;

            body = (
                <>
                    {/* Header strip: total saving + partial-data hint */}
                    <div className="flex align-items-center flex-wrap gap-2 mb-3 p-3 border-round-md"
                        style={{ background: 'linear-gradient(135deg, var(--green-50) 0%, var(--surface-0) 100%)', border: '1px solid var(--green-200)' }}
                    >
                        <i className="pi pi-bolt text-green-700" style={{ fontSize: '1.1rem' }} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-green-800">
                                Refinance these {rankable.length} loan{rankable.length === 1 ? '' : 's'} to save {formatCurrency(totalSaving)}
                            </div>
                            {partialData && (
                                <div className="text-xs text-color-secondary mt-1">
                                    Based on {fetchedCount} of {loans.length} loans checked.
                                    Click "Check BT" on the rest above to see the full ranking.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ranked rows */}
                    <div className="flex flex-column gap-2">
                        {rankable.map((r, idx) => {
                            const rank = idx + 1;
                            const medal = rank === 1 ? { icon: '🥇', bg: 'bg-yellow-100', ring: 'border-yellow-400' }
                                         : rank === 2 ? { icon: '🥈', bg: 'surface-200',   ring: 'surface-border' }
                                         : rank === 3 ? { icon: '🥉', bg: 'bg-orange-100', ring: 'border-orange-300' }
                                         : null;
                            const { icon: loanIcon, color: loanColor } = getLoanIconForType(r.loanTypeLabel);
                            return (
                                <div
                                    key={r.key}
                                    className={`border-round-md p-3 border-1 shadow-1 hover:shadow-3 transition-all transition-duration-150 ${rank === 1 ? 'border-green-300 bg-green-50' : 'surface-border surface-0'}`}
                                >
                                    <div className="flex align-items-center gap-3 flex-wrap">
                                        {/* Rank medal */}
                                        <div
                                            className={`flex align-items-center justify-content-center border-round-md font-bold flex-shrink-0 ${medal ? `${medal.bg}` : 'surface-100 text-color-secondary'}`}
                                            style={{ width: '2.25rem', height: '2.25rem', fontSize: medal ? '1.1rem' : '0.85rem' }}
                                            aria-label={`Priority ${rank}`}
                                        >
                                            {medal ? medal.icon : `#${rank}`}
                                        </div>

                                        {/* Loan-type icon chip */}
                                        <div
                                            className={`flex align-items-center justify-content-center border-round bg-${loanColor}-50 flex-shrink-0`}
                                            style={{ width: '2rem', height: '2rem' }}
                                            aria-hidden="true"
                                        >
                                            <i className={`pi ${loanIcon} text-${loanColor}-600`} style={{ fontSize: '0.9rem' }} />
                                        </div>

                                        {/* Loan identity */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {r.loanTypeLabel} · {r.lenderName}
                                            </div>
                                            <div className="text-xs text-color-secondary mt-1 flex align-items-center flex-wrap"
                                                style={{ columnGap: '0.75rem', rowGap: '0.15rem' }}>
                                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    Outstanding <b className="text-900">{formatCurrency(r.outstanding)}</b>
                                                </span>
                                                <span>·</span>
                                                <span title={r.effort.reason}>
                                                    Effort <b className={r.effort.label === 'Easy' ? 'text-green-700' : 'text-orange-700'}>{r.effort.label}</b>
                                                </span>
                                            </div>
                                        </div>

                                        {/* ROI Now → BT Best */}
                                        <div className="flex align-items-center gap-2 flex-shrink-0"
                                            style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            <div className="text-right">
                                                <div className="text-xs text-color-secondary uppercase font-semibold" style={{ letterSpacing: '.04em' }}>Now</div>
                                                <div className="text-sm font-bold text-900">{r.currentRoi.toFixed(2)}%</div>
                                            </div>
                                            <i className="pi pi-arrow-right text-color-secondary" style={{ fontSize: '0.75rem' }} />
                                            <div className="text-right">
                                                <div className="text-xs text-green-700 uppercase font-semibold" style={{ letterSpacing: '.04em' }}>BT</div>
                                                <div className={`text-sm font-bold ${r.isMarketRate ? 'text-blue-700' : 'text-green-700'}`}>{r.bestRoi.toFixed(2)}%</div>
                                            </div>
                                        </div>

                                        {/* Savings chip */}
                                        <div
                                            className="border-round px-3 py-2 flex-shrink-0"
                                            style={{
                                                background: 'linear-gradient(135deg, var(--green-100) 0%, var(--green-50) 100%)',
                                                border: '1px solid var(--green-300)',
                                                minWidth: '7rem',
                                            }}
                                        >
                                            <div className="text-xs text-green-700 uppercase font-semibold" style={{ letterSpacing: '.04em', fontSize: '0.6rem' }}>
                                                Save
                                            </div>
                                            <div className="text-sm font-bold text-green-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatCurrency(r.savings)}
                                            </div>
                                            <div className="text-xs text-color-secondary" style={{ fontSize: '0.6rem' }}>
                                                ↓ {r.roiDelta.toFixed(2)}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-xs text-color-secondary mt-3 pt-3 border-top-1 surface-border">
                        <i className="pi pi-info-circle mr-1" />
                        Savings are net of estimated foreclosure charges. "Easy" loans have no collateral;
                        "Medium" loans (Home / LAP / Gold) need collateral transfer.
                    </div>
                </>
            );
        }

        return renderComponentCard(
            'pi-sort-amount-down',
            '#ecfdf5',
            '#047857',
            'BT Priority Ranking',
            'Which loans to refinance first — ranked by potential saving',
            body
        );
    };

    // ── Tab 1 body ────────────────────────────────────────────────────────
    // Sub-group A (Today's Position) — built in Step 3
    // Sub-group B (Eligibility for New Loan) — built in Step 4
    const renderTab1 = () => (
        <div>
            {/* Sub-group A — Today's Position (blue tint) */}
            {renderSubgroupSection("Today's Position", 'info',
                <>{renderAllLoansOverview()}</>
            )}

            {/* Sub-group A2 — Debt Burden Analysis (amber tint) */}
            {renderSubgroupSection('Debt Burden Analysis', 'warn',
                <>{renderDebtBurdenAnalysis()}</>
            )}

            {/* Sub-group B — Existing Loan EMI Calculator (green tint) */}
            {renderSubgroupSection('Existing Loan EMI Calculator', 'success',
                <>
                    {renderExistingLoanCalculator()}
                    {renderRepaymentPriorityForThisLoan()}
                </>
            )}
        </div>
    );

    // ── BT all-lenders dialog ─────────────────────────────────────────────
    // Opens when the user clicks the BT chip on any loan row. Shows the full
    // sorted-by-ROI list of lenders (or the single market-rate fallback if
    // no engine was called). Rendered here at the component root so it's
    // outside all the tab panels — a dialog inside a hidden tab wouldn't
    // render correctly.
    // Loan-type icon lookup — mirrors getLoanIcon() from renderAllLoansOverview
    // but hoisted here so the BT dialog can reuse the same visual identity.
    // Kept as a small helper to avoid a shared module for one-line data.
    const getLoanIconForType = (loanTypeLabel) => {
        const s = String(loanTypeLabel || '').toLowerCase();
        if (s.includes('credit') || s.includes('card'))                 return { icon: 'pi-credit-card', color: 'pink' };
        if (s.includes('home') || s.includes('house') || s.includes('housing') || s.includes('property') || s.includes('lap')) return { icon: 'pi-home', color: 'cyan' };
        if (s.includes('auto') || s.includes('car') || s.includes('vehicle') || s.includes('two wheeler') || s.includes('two_wheeler')) return { icon: 'pi-car', color: 'orange' };
        if (s.includes('business') || s.includes('msme') || s.includes('working capital')) return { icon: 'pi-briefcase', color: 'purple' };
        if (s.includes('education') || s.includes('scholar')) return { icon: 'pi-book', color: 'teal' };
        if (s.includes('gold'))                              return { icon: 'pi-star', color: 'yellow' };
        if (s.includes('consumer') || s.includes('durable')) return { icon: 'pi-shopping-bag', color: 'indigo' };
        if (s.includes('personal'))                          return { icon: 'pi-user', color: 'blue' };
        return { icon: 'pi-money-bill', color: 'gray' };
    };

    const renderBTDialog = () => {
        if (!btDialogLoan) return null;
        const bt = btOffersByLoan[btDialogLoan.key];
        const currentRoi = Number(btDialogLoan.roi || 0);
        const currentEmi = Number(btDialogLoan.emi || 0);
        const remainingTenure = Number(btDialogLoan.remainingTenure || 0);
        const offers = bt?.all || [];
        const isMarket = bt?.source === 'market_rate';
        const { icon: loanIcon, color: loanIconColor } = getLoanIconForType(btDialogLoan.loanType);

        // For the animated comparison bars: scale each lender's ROI against
        // the range [min(offers.roi), currentRoi]. The bar fills from left
        // for LOW-roi (better) offers. Full bar = matches current rate;
        // shorter bar = deeper discount.
        const minOfferRoi = offers.length ? Math.min(...offers.map(o => o.roi)) : 0;
        const barMax = Math.max(currentRoi, minOfferRoi);
        const barMin = Math.min(currentRoi, minOfferRoi);
        const roiToWidth = (roi) => {
            if (barMax === barMin) return 100;
            return Math.max(6, Math.min(100, ((roi - barMin) / (barMax - barMin)) * 100));
        };

        // Rank medal per top-3 offer — the DSA's eye lands on gold/silver/bronze first
        const medalFor = (rank) => {
            if (rank === 1) return { bg: 'bg-yellow-100', text: 'text-yellow-700', ring: 'border-yellow-400', icon: '🥇' };
            if (rank === 2) return { bg: 'surface-200', text: 'text-color-secondary', ring: 'surface-border', icon: '🥈' };
            if (rank === 3) return { bg: 'bg-orange-100', text: 'text-orange-700', ring: 'border-orange-300', icon: '🥉' };
            return null;
        };

        return (
            <Dialog
                visible={!!btDialogLoan}
                onHide={() => setBtDialogLoan(null)}
                header={
                    <div className="flex align-items-center gap-3">
                        <div
                            className={`flex align-items-center justify-content-center border-round bg-${loanIconColor}-100 flex-shrink-0`}
                            style={{ width: '2.5rem', height: '2.5rem' }}
                            aria-hidden="true"
                        >
                            <i className={`pi ${loanIcon} text-${loanIconColor}-700`} style={{ fontSize: '1.1rem' }} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-base font-bold text-900">Balance Transfer options</div>
                            <div className="text-xs text-color-secondary mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {btDialogLoan.loanType} · {btDialogLoan.lenderName}
                            </div>
                        </div>
                    </div>
                }
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '780px' }}
            >
                {/* ─── Current-loan snapshot card ────────────────────
                    Green-accented card matches P26 (form section brand
                    pattern). Reads as a "your loan today" summary — the
                    baseline the DSA is comparing lender offers against. */}
                <div className="border-round-md border-1 border-left-3 border-green-500 surface-border p-3 mb-4 surface-0">
                    <div className="flex align-items-center gap-2 mb-3">
                        <i className="pi pi-flag-fill text-green-600" style={{ fontSize: '0.75rem' }} />
                        <span className="text-xs font-bold uppercase text-green-700"
                            style={{ letterSpacing: '.04em' }}>
                            Your current loan
                        </span>
                    </div>
                    <div className="grid text-sm">
                        <div className="col-6 md:col-3">
                            <div className="text-xs text-color-secondary font-semibold uppercase" style={{ letterSpacing: '.04em' }}>ROI</div>
                            <div className="text-lg font-bold text-900 mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {currentRoi.toFixed(2)}%
                            </div>
                        </div>
                        <div className="col-6 md:col-3">
                            <div className="text-xs text-color-secondary font-semibold uppercase" style={{ letterSpacing: '.04em' }}>EMI</div>
                            <div className="text-lg font-bold text-900 mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {formatCurrency(currentEmi)}
                            </div>
                        </div>
                        <div className="col-6 md:col-3">
                            <div className="text-xs text-color-secondary font-semibold uppercase" style={{ letterSpacing: '.04em' }}>Tenure Left</div>
                            <div className="text-lg font-bold text-900 mt-1">{remainingTenure} mo</div>
                        </div>
                        <div className="col-6 md:col-3">
                            <div className="text-xs text-color-secondary font-semibold uppercase" style={{ letterSpacing: '.04em' }}>Outstanding</div>
                            <div className="text-lg font-bold text-900 mt-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {formatCurrency(btDialogLoan.outstanding)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Source hint (market-rate fallback) — with freshness
                    warning based on marketMeta.asOf. Three states:
                      fresh (< 2 mo)   → green "Current"      banner
                      aging (2-4 mo)   → amber "Verify..."    banner
                      stale (> 4 mo)   → red   "Outdated..."  banner
                    This is the single most important trust signal in the
                    dialog: the DSA immediately sees whether the rates are
                    trustworthy without reading paragraphs. */}
                {isMarket && bt?.marketMeta && (() => {
                    // Parse "Jul 2026" style asOf strings into a Date. If
                    // parsing fails, fall back to "aging" so we're honest
                    // rather than falsely-green.
                    const MONTHS = {
                        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
                        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
                    };
                    const parseAsOf = (str) => {
                        if (!str || typeof str !== 'string') return null;
                        const parts = str.trim().split(/\s+/);
                        if (parts.length !== 2) return null;
                        const m = MONTHS[parts[0].slice(0, 3).toLowerCase()];
                        const y = parseInt(parts[1], 10);
                        if (m == null || Number.isNaN(y)) return null;
                        return new Date(y, m, 15);  // mid-month as a sensible anchor
                    };
                    const asOfDate = parseAsOf(bt.marketMeta.asOf);
                    // Age in months. If we couldn't parse, treat as 3 mo
                    // (aging) so the DSA sees a warning to double-check.
                    let ageMonths = 3;
                    if (asOfDate) {
                        const now = new Date();
                        ageMonths = (now.getFullYear() - asOfDate.getFullYear()) * 12
                                  + (now.getMonth() - asOfDate.getMonth());
                    }

                    let state, iconClass, badgeLabel, headline, bgColor, borderColor, textColor, badgeBg, badgeTextColor;
                    if (ageMonths < 2) {
                        state = 'fresh';
                        iconClass = 'pi pi-check-circle';
                        badgeLabel = 'Current';
                        headline = `Indicative rates · verified within the last ${Math.max(1, ageMonths)} month${ageMonths === 1 ? '' : 's'}.`;
                        bgColor = 'var(--green-50)';
                        borderColor = 'var(--green-200)';
                        textColor = 'var(--green-800)';
                        badgeBg = 'var(--green-500)';
                        badgeTextColor = '#fff';
                    } else if (ageMonths < 4) {
                        state = 'aging';
                        iconClass = 'pi pi-exclamation-triangle';
                        badgeLabel = 'Verify with lender';
                        headline = `Indicative rates · ${ageMonths} months old. Confirm with the lender before quoting.`;
                        bgColor = 'var(--yellow-50)';
                        borderColor = 'var(--yellow-300)';
                        textColor = '#7c4a03';
                        badgeBg = 'var(--yellow-500)';
                        badgeTextColor = '#7c4a03';
                    } else {
                        state = 'stale';
                        iconClass = 'pi pi-times-circle';
                        badgeLabel = 'Outdated';
                        headline = `Indicative rates · ${ageMonths} months old. These are stale — ask Ops to refresh, and always confirm with the lender.`;
                        bgColor = 'var(--red-50)';
                        borderColor = 'var(--red-200)';
                        textColor = 'var(--red-800)';
                        badgeBg = 'var(--red-500)';
                        badgeTextColor = '#fff';
                    }

                    return (
                        <div className="mb-3 p-3 border-round-md border-1 flex align-items-start gap-2"
                            style={{ background: bgColor, borderColor }}>
                            <i className={iconClass} style={{ color: textColor, fontSize: '1rem', marginTop: '0.15rem' }} />
                            <div className="flex-1 text-xs" style={{ color: textColor }}>
                                {/* Top row: loan-type + as-of + freshness pill */}
                                <div className="flex align-items-center flex-wrap gap-2 mb-1">
                                    <span className="font-bold" style={{ fontSize: '0.8rem' }}>
                                        {bt.marketMeta.loanTypeLabel}
                                    </span>
                                    <span style={{ opacity: 0.7 }}>· as of {bt.marketMeta.asOf}</span>
                                    <span
                                        className="inline-flex align-items-center border-round px-2 py-1 font-bold uppercase"
                                        style={{
                                            background: badgeBg,
                                            color: badgeTextColor,
                                            fontSize: '0.6rem',
                                            letterSpacing: '.06em',
                                        }}
                                    >
                                        {badgeLabel}
                                    </span>
                                </div>
                                {/* Headline: freshness message */}
                                <div className="mb-1" style={{ fontWeight: 500 }}>
                                    {headline}
                                </div>
                                {/* Domain note per loan type */}
                                <div style={{ opacity: 0.85 }}>{bt.marketMeta.note}</div>
                                {/* Source + honest caveat */}
                                <div className="mt-1" style={{ opacity: 0.75 }}>
                                    Source: {bt.marketMeta.sourceLabel}. Actual sanctioned rate depends on
                                    borrower profile (CIBIL, income, tenure). Contact the lender for a firm quote.
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Section header for the lender list */}
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-list text-primary" style={{ fontSize: '0.75rem' }} />
                    <span className="text-xs font-bold uppercase text-900" style={{ letterSpacing: '.04em' }}>
                        Lenders ranked by ROI
                    </span>
                    <span className="text-xs text-color-secondary">({offers.length})</span>
                </div>

                {/* Lender list — sorted ascending by ROI. Top 3 get medals. */}
                {offers.length === 0 ? (
                    <div className="text-center text-color-secondary py-4">
                        <i className="pi pi-inbox text-2xl mb-2 block text-400" aria-hidden="true" />
                        <div>No BT offers available for this loan.</div>
                    </div>
                ) : (
                    <div className="flex flex-column gap-2">
                        {offers.map((o, idx) => {
                            const delta = btDelta(currentRoi, o.roi);
                            const isBetter = delta.isBetter;
                            const rank = idx + 1;
                            const medal = medalFor(rank);
                            const barWidth = roiToWidth(o.roi);
                            const barColor = isBetter ? 'var(--green-500)' : 'var(--gray-300)';

                            return (
                                <div
                                    key={`${o.lender_name}-${idx}`}
                                    className={`border-round-md p-3 border-1 shadow-1 transition-all transition-duration-150 hover:shadow-3 ${isBetter && rank === 1 ? 'border-green-400 bg-green-50' : isBetter ? 'border-green-200 surface-0' : 'surface-border surface-0'}`}
                                >
                                    <div className="flex align-items-center gap-3">
                                        {/* Rank medal — visual scan cue for top 3 */}
                                        <div
                                            className={`flex align-items-center justify-content-center border-round-md font-bold flex-shrink-0 ${medal ? `${medal.bg} ${medal.text}` : 'surface-100 text-color-secondary'}`}
                                            style={{ width: '2.25rem', height: '2.25rem', fontSize: medal ? '1rem' : '0.85rem' }}
                                            aria-label={`Rank ${rank}`}
                                        >
                                            {medal ? medal.icon : `#${rank}`}
                                        </div>
                                        {/* Lender info + comparison bar */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex align-items-baseline justify-content-between gap-2">
                                                <span className="text-sm font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                    {o.lender_name}
                                                </span>
                                                <span className={`text-base font-bold ${isBetter ? 'text-green-700' : 'text-900'}`}
                                                    style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                    {o.roi.toFixed(2)}%
                                                </span>
                                            </div>
                                            {/* Animated ROI comparison bar */}
                                            <div className="mt-2 mb-1">
                                                <div
                                                    className="border-round overflow-hidden"
                                                    style={{ height: '0.4rem', background: 'var(--surface-100)' }}
                                                    role="progressbar"
                                                    aria-valuenow={o.roi.toFixed(2)}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                    aria-label={`${o.lender_name} ROI ${o.roi.toFixed(2)}%`}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${barWidth}%`,
                                                            height: '100%',
                                                            background: barColor,
                                                            transition: 'width 500ms ease',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {/* Sub-line: EMI + savings + ROI delta.
                                                For market-rate rows, we ALSO show the
                                                published rate range ("9.4% – 11.5%")
                                                so the DSA/borrower see the full band
                                                the lender advertises, not just the
                                                floor. Builds trust: they're seeing
                                                the same numbers they'd find on the
                                                bank's rate-card page. */}
                                            <div className="flex align-items-center justify-content-between gap-2 mt-1" style={{ fontSize: '0.7rem' }}>
                                                <span className="text-color-secondary">
                                                    EMI <b className="text-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(o.emi)}</b>
                                                    {o.roiMin != null && o.roiMax != null && o.roiMin !== o.roiMax && (
                                                        <>
                                                            {' · '}
                                                            <span className="text-color-secondary">
                                                                Range <b className="text-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                                    {o.roiMin.toFixed(2)}%–{o.roiMax.toFixed(2)}%
                                                                </b>
                                                            </span>
                                                        </>
                                                    )}
                                                    {o.savings > 0 && (
                                                        <>
                                                            {' · '}
                                                            <span className="text-green-700 font-semibold">
                                                                Save {formatCurrency(o.savings)}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                                <span className={`font-semibold flex-shrink-0 ${isBetter ? 'text-green-700' : 'text-color-secondary'}`}>
                                                    {delta.roiDelta > 0
                                                        ? `↓ ${delta.roiDelta.toFixed(2)}%`
                                                        : `↑ ${Math.abs(delta.roiDelta).toFixed(2)}%`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="text-xs text-color-secondary mt-3 pt-3 border-top-1 surface-border">
                    <i className="pi pi-info-circle mr-1" />
                    Savings shown are net of estimated foreclosure charges (~4% + GST on unsecured; 0% on floating-rate HL/EL). Actual quotes from the lender may vary.
                </div>
            </Dialog>
        );
    };

    return (
        <section className="p-2" aria-label="Loan portfolio dashboard">
            {/* Page-scoped keyframes — BT chip glow pulse.
                Only the "better offer" state (bt-chip-glow class) pulses;
                idle/loading/best-rate stay static. Respects
                prefers-reduced-motion (WCAG rule 32 in the ui-ux skill).
                Kept in a <style> block per skill rule 4 (no new CSS files);
                same pattern as P25 / P24. */}
            <style>{`
                @keyframes btChipGlow {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4),
                                    0 1px 3px rgba(0, 0, 0, 0.08);
                    }
                    50% {
                        box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.0),
                                    0 1px 3px rgba(0, 0, 0, 0.08);
                    }
                }
                .bt-chip-glow {
                    animation: btChipGlow 2s ease-in-out infinite;
                    border: 1px solid var(--green-300);
                }
                .bt-chip-glow:hover {
                    animation-play-state: paused;
                    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.25),
                                0 4px 12px rgba(0, 0, 0, 0.12) !important;
                    transform: translateY(-1px);
                }
                @media (prefers-reduced-motion: reduce) {
                    .bt-chip-glow { animation: none; }
                }
                /* Idle "Check BT" — subtle static ring only, no pulse */
                .bt-chip-idle {
                    border: 1px solid var(--primary-200);
                }
                .bt-chip-idle:hover {
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18),
                                0 2px 8px rgba(0, 0, 0, 0.08);
                    transform: translateY(-1px);
                }
                /* "Best rate" chip — softer green pulse (celebrates the fact
                    that the borrower already has the best rate). Slower cycle
                    than the "better BT" pulse (3s vs 2s) so it feels calm
                    rather than urgent. */
                @keyframes btChipBestPulse {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.35),
                                    0 1px 3px rgba(0, 0, 0, 0.06);
                    }
                    50% {
                        box-shadow: 0 0 0 5px rgba(20, 184, 166, 0),
                                    0 1px 3px rgba(0, 0, 0, 0.06);
                    }
                }
                .bt-chip-best {
                    animation: btChipBestPulse 3s ease-in-out infinite;
                    border: 1px solid var(--teal-300);
                    background: linear-gradient(135deg, var(--teal-50) 0%, var(--green-50) 100%) !important;
                }
                .bt-chip-best:hover {
                    animation-play-state: paused;
                    box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.2),
                                0 4px 10px rgba(0, 0, 0, 0.10) !important;
                    transform: translateY(-1px);
                }
                @media (prefers-reduced-motion: reduce) {
                    .bt-chip-best { animation: none; }
                }

                /* ── Portfolio tab strip ─────────────────────────────────────
                   Layout lives here rather than inline so the mobile media
                   query below can actually override it — inline styles win
                   over stylesheet rules. Only the active-state background /
                   colour / shadow stay inline, since they're state-driven. */
                .portfolio-tablist {
                    gap: 4px;
                    overflow-x: auto;
                }
                .portfolio-tab-pill {
                    flex: 1;
                    min-width: 0;
                    padding: 12px 16px;
                    border: none;
                    cursor: pointer;
                    border-radius: 10px;
                    font-family: inherit;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: -0.005em;
                    white-space: nowrap;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all .2s cubic-bezier(.4,0,.2,1);
                }
                /* Below 768px the four labels can't fit on one row — flex:1 +
                   nowrap left the last pills off-screen behind an invisible
                   scrollbar. A full-width dropdown replaces the strip there
                   (see the isMobileTabs branch); it takes its own row so
                   Export/Share wrap underneath it. */
                .portfolio-tab-select {
                    flex: 1 1 100%;
                    min-width: 0;
                }
                .portfolio-tab-select .p-dropdown {
                    width: 100%;
                    border-radius: 10px;
                }
                /* Match the active pill's dark-navy fill so the selected
                   section reads the same on both layouts. */
                .portfolio-tab-select .p-dropdown .p-dropdown-label {
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: -0.005em;
                    padding: 12px 14px;
                }
            `}</style>
            {!externalToast && <Toast ref={internalToast} />}
            {renderBTDialog()}

            {/* Dark-navy hero + secondary metric cards intentionally removed
                per redesign (2026-07-01). They duplicated numbers now shown
                inside individual tabs (KPI tiles in All Loans Overview,
                affordability panel in Debt Burden Analysis). The tab bar
                below now carries Export/Share to preserve those actions. */}

            {(() => {
                const tabs = [
                    { idx: 0, icon: 'pi-briefcase',  label: 'Existing & New Loan',    badge: 6, render: renderTab1 },
                    { idx: 1, icon: 'pi-key',        label: 'Other Loan Eligibility', badge: 3, render: renderTab2 },
                    { idx: 2, icon: 'pi-bolt',       label: 'Repayment & Planner',    badge: 6, render: renderTab3 },
                    { idx: 3, icon: 'pi-chart-pie',  label: 'Wealth',                 badge: 3, render: renderTab4 },
                ];
                const active = tabs.find(t => t.idx === activeTabIndex) || tabs[0];
                return (
                    <>
                        {/* Portfolio tab strip — 4 tabs (left) + Export/Share (right).
                            Sticky at the top of the viewport so the DSA always
                            sees which section they're on when they scroll deep
                            into a tab. z-index above modals? no — modals use
                            portal + higher z-index anyway. */}
                        <div
                            className="portfolio-tabs-wrap flex align-items-center flex-wrap gap-2"
                            style={{
                                background: '#ffffff',
                                border: '1px solid #e6e1d6',
                                borderRadius: 14,
                                padding: 6,
                                margin: '4px 0 16px',
                                boxShadow: '0 1px 2px rgba(10,19,32,.04), 0 1px 1px rgba(10,19,32,.03)',
                                position: 'sticky',
                                top: '0.5rem',
                                zIndex: 10,
                            }}
                        >
                            {/* Section picker — full-width dropdown on phones,
                                pill strip from 768px up. */}
                            {isMobileTabs ? (
                                <div className="portfolio-tab-select">
                                    <Dropdown
                                        value={activeTabIndex}
                                        options={tabs}
                                        optionLabel="label"
                                        optionValue="idx"
                                        onChange={(e) => setActiveTabIndex(e.value)}
                                        aria-label="Portfolio sections"
                                        valueTemplate={(option) => {
                                            const t = option || active;
                                            return (
                                                <span className="flex align-items-center gap-2">
                                                    <i className={`pi ${t.icon}`} aria-hidden="true" style={{ fontSize: 14 }} />
                                                    <span>{t.label}</span>
                                                </span>
                                            );
                                        }}
                                        itemTemplate={(option) => (
                                            <span className="flex align-items-center gap-2">
                                                <i className={`pi ${option.icon}`} aria-hidden="true" style={{ fontSize: 14 }} />
                                                <span>{option.label}</span>
                                            </span>
                                        )}
                                    />
                                </div>
                            ) : (
                            <div
                                role="tablist"
                                aria-label="Portfolio sections"
                                className="portfolio-tablist flex flex-1 min-w-0"
                            >
                                {tabs.map((t) => {
                                    const isActive = t.idx === activeTabIndex;
                                    return (
                                        <button
                                            key={t.idx}
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveTabIndex(t.idx)}
                                            className="portfolio-tab-pill"
                                            style={{
                                                background: isActive
                                                    ? 'linear-gradient(135deg, #0a1320 0%, #16243d 100%)'
                                                    : 'transparent',
                                                color: isActive ? '#ffffff' : '#475569',
                                                boxShadow: isActive
                                                    ? '0 4px 12px rgba(10,19,32,.22), inset 0 1px 0 rgba(255,255,255,.06)'
                                                    : 'none',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = '#f0ece2';
                                                    e.currentTarget.style.color = '#0a1320';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = '#475569';
                                                }
                                            }}
                                        >
                                            <i className={`pi ${t.icon}`} aria-hidden="true" style={{ fontSize: 14 }} />
                                            <span>{t.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            )}

                            {/* Action buttons — Export PDF + Share, right-aligned.
                                Small outlined buttons so they don't compete with
                                the active tab pill. On mobile they wrap under the
                                tabs (flex-wrap on the parent). */}
                            <div className="flex align-items-center gap-2 flex-shrink-0 pr-1 pl-1">
                                <Button
                                    icon="pi pi-download"
                                    label="Export PDF"
                                    size="small"
                                    outlined
                                    severity="secondary"
                                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.7rem' }}
                                    aria-label="Export portfolio as PDF"
                                />
                                <Button
                                    icon="pi pi-share-alt"
                                    label="Share"
                                    size="small"
                                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.7rem' }}
                                    aria-label="Share portfolio"
                                />
                            </div>
                        </div>

                        {/* Active tab content (fade-in mirrors the prototype animation) */}
                        <div
                            key={active.idx}
                            style={{ animation: 'tabFade .2s ease-out' }}
                        >
                            {active.render()}
                        </div>

                        {/* Inline keyframes for the fade — keeps the CSS scoped */}
                        <style>{`
                            @keyframes tabFade {
                                from { opacity: 0; transform: translateY(6px); }
                                to   { opacity: 1; transform: translateY(0); }
                            }
                        `}</style>
                    </>
                );
            })()}
        </section>
    );
};

export default LoanPortfolioStep;
